import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { formatMessagesPanelTabs } from './utils/messagesPanelUtils';
import { logActivity } from './utils/logActivity';
import { useMarketDataSync } from './utils/marketDataSync';
import { appendJournalEntry, loadJournal, saveJournal, resetJournal } from './utils/studyJournalUtils';
import { JOURNAL_TAB_INDEX, SUMMARY_TAB_INDEX } from './components/MessagesPanelDialog';
import ElliottWaveDialog from './components/ElliottWaveDialog';
import { buildElliottRequest, toVerificationBars, MIN_BARS_FOR_ANALYSIS } from './utils/elliottRequest';
import { allProjections, waveStructure } from './utils/elliottProjections';
import { verifyProjections, projectionSummary, projectionScoreline } from './utils/elliottVerification';
import {
  loadTrendPulseDataset, loadCtsDataset, hasPrecomputedOrb, loadCtsDetail,
} from './utils/precomputedDatasets';
import { buildBarIndex, valuesForBars, reversalBars, alignToBar } from './utils/precomputedAlignment';
import ConfigurationDialog from './components/ConfigurationDialog';
import MessagesDialog from './components/MessagesDialog';
import DynaManSimulatorDialog from './components/DynaManSimulatorDialog';
import DynaManProductionDialog from './components/DynaManProductionDialog';
import CustomDataUpload from './components/CustomDataUpload';
import { isCustomSymbol } from './constants/customSymbol';
import { readLastSymbol, writeLastSymbol } from './utils/lastSymbol';
import HeaderActiveSymbol from './components/HeaderActiveSymbol';
/*
 * ⚠ NOTHING IS IMPORTED FROM `toolbarProfiles` ANY MORE. `readProfile`/`writeProfile` persisted
 * the dev profile and `isResearchDialogAvailable` chose between the two DynaMan dialogs; the
 * first two went with the dropdown and the third is a manifest question now.
 */
import {
  ACTIVE_PRODUCT, readProductOverride, writeProductOverride,
} from './config/productTarget';
import { runFullSimulation } from './utils/dynamanSimulation';
import {
  strategyFor, REPLAY_TIMEFRAMES,
} from './components/DynaManLiveSimulator';
import { CYCLE_BUTTONS, cycleGroupId, cycleKeyForGroupId, cyclePillarIdPrefix,
  cycleBandIdPrefix } from './components/cycleToolbarButtons';
import {
  readWorkspaceColors, onWorkspaceColorsChange,
} from './utils/workspaceColors';
import {
  intervalIndices, gateIntervals, gateSummary, buildCycleBands, cycleRangeProjections,
  cycleGuideLines, pairDates,
} from './utils/cycleBandGating';
import { annotateBandCongestion, lightenStroke } from './utils/bandDensity';
import { CHANNEL_BORDER_WIDTH, PROJECTION_BORDER_WIDTH, boostAlpha } from './utils/bandWeights';
import { rsiLineDefault } from './utils/indicatorPalette';
import {
  cycleColors, cycleHandleColors, CYCLE_HANDLE_BORDER_WIDTH, CYCLE_HANDLE_SIZE,
} from './utils/cycleColors';
import { CTS_MARK_COLOR } from './utils/simulatorPalette';
import { ctsMarkStyle } from './utils/ctsMarkStyle';
import ChartEndDateDialog from './components/ChartEndDateDialog';
import { weekdaysBetween } from './utils/chartEndDates';
import {
  buildTradeSimVectorOverlay, TRADE_SIM_VECTOR_OVERLAY_ID,
} from './utils/tradeSimVectors';
import { GUIDE_LINE_WIDTH } from './utils/bandGuides';
import { runChartAnalytica, runLabel } from './utils/chartAnalytica';
import { sectionByBar, sectionsFrom } from './utils/analyticaGeometry';
import { ctsOddsSeries } from './utils/ctsOddsEngine';
import { oddsTooltipLines } from './utils/ctsOddsTooltip';
import { atrSeries, simulateTrade } from './utils/tradeSimEngine';
import {
  assessesAutomatically, providerFor,
} from './utils/tradeSimProviders';
import {
  EMPTY_SESSION, clearSession, recordClick, repriceTrade,
} from './utils/tradeSimSession';
import {
  IDLE_STATE, beginOpen, cancel as cancelManual, isAwaitingClick,
  registerClick as registerManualClick, withContext,
} from './utils/tradeSimManual';
import TradeSimulatorDialog from './components/TradeSimulatorDialog';
import {
  ANALYTICA_OVERLAY_IDS, analyticaOverlays, analyticaStatus,
} from './utils/analyticaOverlay';
import ChartAnalyticaDialog from './components/ChartAnalyticaDialog';
import { buildElliottOverlay, ELLIOTT_OVERLAY_ID } from './utils/elliottOverlay';
import { buildTrendPulseOverlay, trendPulseOverlaysToEvict } from './utils/trendPulseOverlays';

import { barsRemaining, gateFor, gateResolution } from './utils/replayGate';
import { HIT_ZONES, hitZoneFor } from './utils/chartHitZone';
import { extendBarData } from './utils/extendBarData';
import {
  maskBarsAfter, revealTargetFor, stepReveal, clampRevealIndex, revealedClose,
  MIN_BARS_TO_SIMULATE,
} from './utils/simulationMode';
import { addAreaHighlightOverlaysAndMessages } from './components/addAreaHighlightOverlaysUtil';
import {
  AREA_HIGHLIGHT_DEFAULT_COLOR, areaHighlightGroupId, areaHighlightLabel,
  areaHighlightOverlays, areaHighlightWindow, explainAreaHighlightRun,
  resolveAreaHighlightParams,
} from './utils/areaHighlightStudy';
import HorizontalLineDialog from './components/HorizontalLineDialog';
import PatternPredictionDialog from './components/PatternPredictionDialog';
import {
  ENTRY_OVERLAY_ID, buildEntryOverlay, summariseEntries, FORWARD_HORIZONS,
} from './utils/trendPulseEntries';
import {
  planSweep, openSweepTarget, runSweep, readZoomWindow, TARGET_DIRECTORY,
} from './utils/chartSweepCapture';
// ── OPERATOR FEATURES (revertible as one commit; see utils/operatorFeatures.js) ──────────────────
import { operatorFeaturesEnabled } from './utils/operatorFeatures';
import { buildPhaseRibbonOverlay, PHASE_RIBBON_OVERLAY_ID } from './utils/trendPulsePhases';
import {
  evaluateBoundaryShelves, buildValidationLineOverlays, summariseValidation,
  VALIDATION_LINE_PREFIX,
} from './utils/validationLines';
import { copyChartToClipboard, saveChartScreenshot } from './utils/operatorExport';
import { formatAnalysisReport, headline } from './utils/integralDeltaReport';
import { buildTradeOverlay, tradeOverlayStatus, INTEGRAL_DELTA_TRADES_OVERLAY_ID }
  from './utils/integralDeltaTrades';
import { buildDynaManTradeOverlay, dynaManOverlayStatus, DYNAMAN_TRADES_OVERLAY_ID }
  from './utils/dynaManTrades';
import DynaManReplayOverlay from './components/DynaManReplayOverlay';
import DynaManPortfolioDialog from './components/DynaManPortfolioDialog';
import { sessionDateOf as replaySessionDate } from './utils/replayStats';
import { buildPlayheadOverlay, REPLAY_PLAYHEAD_OVERLAY_ID } from './utils/replayPlayhead';
import { FAN_OVERLAY_ID } from './utils/fanLines';
import { REPLAY_ANNOTATION_OVERLAY_ID } from './utils/replayAnnotations';
import { buildTimeBandOverlay, TIME_BAND_OVERLAY_ID, indexAtOrAfter, indexAtOrBefore }
  from './utils/timeBandOverlay';
import {
  trialCutoffDates, cutoffForCycle, filterToCutoff, clampPairsToCutoff,
  narrowestBound, cutoffNote, cutoffEmptyNote,
} from './utils/trialSignalCutoff';
import { getChartFutureCeilingDate, fallbackTier } from './utils/getChartFutureCeilingDate';
import { CTS_PILLAR_OVERLAY_ID } from './utils/ctsPillars';
import { LEDGER_MARKER_OVERLAY_ID } from './utils/ledgerMarkers';
import SimulatorSetupDialog from './components/SimulatorSetupDialog';
import { buildSessionRequest, buildBandOnlyRequest } from './utils/replaySessionRequest';
import { buildLocalTimeline } from './utils/localTimeline';
import { fetchReplayTimeline } from './utils/dynamanSimulation';
// THE ACCOUNT THE REPLAY IS PRICED WITH, imported rather than restated. The discretionary book
// has to be charged the same commission and slippage as the algo one, and a second copy of those
// four numbers is a second thing to keep in step — the exact shape of defect the shared portfolio
// reducer was introduced to end.
import { DEBUG_ACCOUNT } from './components/DynaManLiveSimulator';
import { buildPendingSignalsOverlay, pendingOverlayStatus, padBarsNeededFor,
  PENDING_SIGNALS_OVERLAY_ID } from './utils/pendingSignals';
import DailyScanViewer from './components/DailyScanViewer';
import { alignSeriesToBars } from './utils/trendPulseEntries';
// ── end OPERATOR FEATURES imports ────────────────────────────────────────────────────────────────
import TabPanel from './components/TabPanel';
import EChartComponent, { computeRSI } from './components/EChartComponent';
import Login from './components/Login';
import RegistrationPending from './components/RegistrationPending';
import PaymentPending from './components/PaymentPending';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChartOverlayProvider } from './context/ChartOverlayContext';
import { CommandProvider, useCommandContext, REFUSAL } from './context/CommandContext';
import { resolveAspectParams, aspectQuery, aspectOverlays, explainAspectRun }
  from './utils/aspectStudy';
import { partitionPendingCommands, DISPOSITION } from './utils/pendingCommands';
import {
  buildStrategy2Payload, buildTimeAndEventPayload, runStrategyHeadless,
} from './utils/strategyPayloadBuilders';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import { useChartOverlays } from './context/ChartOverlayContext';
import { HelpProvider } from './context/HelpContext';
import HelpViewer from './components/HelpViewer';

import SymbolList from './components/SymbolList';
import { AppBar, Toolbar, CircularProgress, Snackbar } from '@mui/material';
import UserMenu from './components/UserMenu';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { watermarkForMarket } from './utils/branding';
import { DEFAULT_DRAWING_COLOR, withAlpha } from './utils/drawingColors';
import { MANUAL_DRAW_SOURCE, upsertTrendlineSummary } from './utils/trendlineMeasure';
import {
  buildMultiSpiralOverlays, countUndrawnZones, resolveZoneDates,
  SPIRAL_OVERLAY_IDS, SPIRAL_MAX_ZONES,
  SPIRAL_LOOKAHEAD_DAYS,
  lookaheadToLastPaintedBar,
} from './utils/spiralOverlays';
import {
  buildInspectOverlays, buildCombinedInspectOverlays, SPIRAL_INSPECT_OVERLAY_IDS,
} from './utils/spiralInspectOverlays';
import SpiralInspectMenu from './components/SpiralInspectMenu';
import SpiralModeDialog, { SPIRAL_MODES } from './components/SpiralModeDialog';
import {
  GANN_CONFLUENCE_OVERLAY_ID, buildGannArrowPoints,
  ctsScanParams, CTS_PULSE_MAX_VISIBLE_BARS, aspectLinesFor, arrowTooltipLines,
  composeMarkCard, arrowDetailLines,
  // Shared with the precomputed path so a mark drawn from a file and one drawn from a live scan
  // are the same mark: same direction rule, same two colours.
  pivotDirectionAt,
} from './utils/gannConfluenceOverlay';
import { indexDigit, badgeText, markerTooltipLines } from './utils/ctsPatterns';
import { CTS_COOLDOWN_BARS } from './utils/ctsDeduplication';
import { clusterGlyphPoints, clusterTooltipLine } from './utils/ctsClusterGlyph';
import { fetchClassification, dayForHit } from './utils/ctsClassify';

/**
 * How large a CTS triangle is drawn, and how large the index digit inside it is.
 *
 * <p>Eighteen rather than the eleven it used to be, because the marker now carries a character
 * inside it. A digit needs room the arrow alone did not: at eleven the number overflowed the shape
 * it was meant to be read on, and shrinking the digit instead would have made it unreadable at
 * every zoom rather than only at the crowded ones.
 */
const CTS_SYMBOL_SIZE = 18;
const CTS_INDEX_FONT_SIZE = 9;
/*
 * WHERE THE CLUSTER COLUMN STARTS, and why it is not just above the triangle.
 *
 * The column sits on the mark's own bar (x offset 0), so it has to climb past everything else
 * drawn on that bar: the triangle itself, which is `CTS_SYMBOL_SIZE` tall and CENTRED on the rail
 * so it reaches half that above it, and then the configuration badge, a bold chip the renderer
 * places `CTS_BADGE_OFFSET_PX` up.
 *
 * ⚠ ONE HEIGHT FOR EVERY MARK, including the ones with no badge. It could be computed per point --
 * App knows whether `badgeText` returned anything -- and then two marks side by side would carry
 * their columns at different heights, which makes a row of them unreadable for a gain nobody
 * asked for. A fixed clearance costs a visible gap under the columns that have no badge.
 *
 * The badge offset is stated on the overlay rather than left to the renderer's default, so this
 * derivation is a real dependency: move the badge and the column follows it.
 */
const CTS_BADGE_OFFSET_PX = 20;
const CTS_CLUSTER_BASE_PX = CTS_BADGE_OFFSET_PX + 12;
import { summariseGannRun, gannJournalLines } from './utils/gannConfluenceSummary';
import {
  ECLIPSE_TRANSIT_OVERLAY_ID, buildEclipseTransitPoints, eclipseTransitJournalLines,
} from './utils/eclipseTransitOverlay';
import GannScanDialog from './components/GannScanDialog';
import SpiralInspectDialog from './components/SpiralInspectDialog';
import SpiralScanBadge from './components/SpiralScanBadge';
import SpiralClickControl from './components/SpiralClickControl';
import { textSizeAt, activateTextTool, isSizedTool } from './utils/textAnnotationSizes';
import { newOverlayGroupId, lastOverlaySize, lastOverlayGroupId } from './utils/drawingStack';
import { buildAdditionSeriesStudy, buildFibPivotStudy } from './utils/pivotStudies';
import { nextChartView, normalizeChartView, withPriceHidden, CHART_VIEW, cycleFor } from './utils/chartViewMode';
import { tradeCardKeys } from './utils/tradeDecorator';
import { buildRecentPivotProjection, hasRecentFibOverlays, withoutRecentFibOverlays } from './utils/recentPivotProjection';
import { buildSk360, hasSk360Overlays, withoutSk360Overlays } from './utils/sk360';
import { buildTimeProjection, hasTimeProjectionOverlays, withoutTimeProjectionOverlays } from './utils/timeProjection';
import { buildSkGrid, hasSkGridOverlays, withoutSkGridOverlays } from './utils/skGrid';
import { buildFibTimeOverlays } from './utils/fibTimeProjection';
import { buildFixedCycleOverlays, withoutFixedCycles } from './utils/fixedCycles';
import FixedCycleDialog from './components/FixedCycleDialog';
import {
  TOOL_CLICKS, TECHNICAL_DRAW_SOURCE, fibonacciSegments,
  extendedTrendlineEnd, pitchfork, clampIndex, FUTURE_PROJECTION_BARS,
} from './utils/technicalDrawings';
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';

import { coolWhiteTheme, darkTheme } from './themes';
import { useThemeMode, DARK } from './utils/themeMode';
import ThemeModeToggle from './components/ThemeModeToggle';

import LongitudeStudyDlg, { PREFILL_FIELDS as ASPECT_STUDY_PREFILL_FIELDS }
  from './components/LongitudeStudyDlg';
import PatternConfigDialog from './components/PatternConfigDialog';
import PlanetFollowStudyDialog from './components/PlanetFollowStudyDialog';
import AspectsAreaHighlightDialog from './components/AspectsAreaHighlightDialog';

import DeclinationStudyDialog from './components/DeclinationStudyDialog';
import PlanetaryLinesDialog from './components/PlanetaryLinesDialog';
import AverageLongitudeDialog from './components/AverageLongitudeDialog';
import { unwrapDegreesWithGaps } from './utils/unwrapDegreesWithGaps';

import PlanetGroupDialog from './components/PlanetGroupDialog';
import VisualUniverseDialog from './components/VisualUniverseDialog';
import IntradayAnimationDialog from './components/IntradayAnimationDialog';
import ChartComparisonDialog from './components/ChartComparisonDialog';
import AreaHighlightDialog, { PREFILL_FIELDS as AREA_HIGHLIGHT_PREFILL_FIELDS }
  from './components/AreaHighlightDialog';
import ActiveOverlayPills from './components/ActiveOverlayPills';
import MessagesPanelButton from './components/MessagesPanelButton';
import AggregateAreaHighlightDialog from './components/AggregateAreaHighlightDialog';
import StrategyReportStudyDialog from './components/StrategyReportStudyDialog';
import StrategyReportStudyBDialog from './components/StrategyReportStudyBDialog';
import ChartCopilotBar from './components/ChartCopilotBar';
import StrategyDesignDialog, { PREFILL_FIELDS as STRATEGY1_PREFILL_FIELDS }
  from './components/StrategyDesignDialog';
import Strategy2DesignDialog, { PREFILL_FIELDS as STRATEGY2_PREFILL_FIELDS }
  from './components/Strategy2DesignDialog';
import TimeAndEventStrategyDialog, { PREFILL_FIELDS as TIME_EVENT_PREFILL_FIELDS }
  from './components/TimeAndEventStrategyDialog';
// MOA is mounted by TabPanel; only its field list is needed here, to refuse an unknown prefill.
import { PREFILL_FIELDS as MOA_PREFILL_FIELDS } from './components/MOAStrategyDialog';
import LocationSettingsDialog from './components/LocationSettingsDialog';
import CyclesDialog from './components/CyclesDialog';
import PlanetCharacteristicsPlotDialog from './components/PlanetCharacteristicsPlotDialog';
import AstroGraphDialog, {
  trendPulseCustomRunConfig, TRENDPULSE_LINE_DEFAULTS,
} from './components/AstroGraphDialog';
import AppBrandDialog from './components/AppBrandDialog';
import DeepPivotDialog from './components/DeepPivotDialog';
import PlanetaryAnalogDialog from './components/PlanetaryAnalogDialog';
import PivotCommonalitiesDialog from './components/PivotCommonalitiesDialog';
import {
  deepPivotOverlay, deepPivotStatus, DEEP_PIVOT_OVERLAY_ID,
} from './utils/deepPivotOverlay';
import CelestialAspectDialog from './components/CelestialAspectDialog';
import {
  celestialAspectOverlay, celestialStatus, CELESTIAL_OVERLAY_ID,
} from './utils/celestialAspectOverlay';
import {
  buildMidpointTrineOverlay, describeMidpointTrineScan, MIDPOINT_TRINE_OVERLAY_ID,
} from './utils/midpointTrineOverlay';
import {
  buildEclipseStudyOverlay, describeEclipseStudy, markerBreakdown, ECLIPSE_STUDY_OVERLAY_ID,
} from './utils/eclipseStudyOverlay';
import {
  buildPlanetaryAnalogOverlay, describePlanetaryAnalogPaint, PLANETARY_ANALOG_OVERLAY_ID,
} from './utils/planetaryAnalogOverlay';
import {
  buildPivotCommonalityOverlay, describePivotCommonalityPaint, PIVOT_COMMONALITY_OVERLAY_ID,
} from './utils/pivotCommonalitiesOverlay';
import {
  SCAN_STATE, SCAN_STATE_LABEL, describeScan,
} from './utils/marketScanner';
import ScannerCandidatesDialog from './components/ScannerCandidatesDialog';
import {
  randomBaselineOverlay, randomBaselineStatus, subsetForModifiers, baselineSizesFor,
  drawRandomIndices, splitIndices, RANDOM_OVERLAY_ID,
} from './utils/celestialRandomBaseline';
import {
  readBrandOverride, writeBrandOverride, clearBrandOverride, onBrandChange,
} from './utils/appBrand';
/*
 * ⚠ `nonAdminBrandFor` IS NO LONGER IMPORTED, and the helper is deliberately left in place.
 *
 * The manifest's `brandName` now answers the header for a customer AND for an admin, which that
 * helper never did. It still backs `nonAdminChrome`'s own tests and documents the defect it was
 * written for — a bare `NON_ADMIN_BRAND` constant that showed every customer the astro title —
 * so it is retained rather than deleted along with its reasoning.
 */
import { isCustomerFacingOnProduct } from './config/nonAdminChrome';
/*
 * THE MANIFEST — the product this session is, resolved once and read by three surfaces.
 *
 * ⚠ `resolveInitialManifest` is called HERE as well as inside `ManifestProvider`, and that is one
 * source rather than two: it is a pure function of (hostname, isAdmin, previewId) and both call
 * sites pass the same three. App needs the value directly for the header and the watermark, and
 * the provider needs to publish it to the toolbar and the canvas, which are rendered below.
 */
import { resolveInitialManifest } from './config/manifests';
import { ADMIN as ADMIN_PRODUCT } from './config/productTarget';
import { ManifestProvider, readStoredPreview } from './context/ManifestContext';
import { describeHostContext } from './utils/hostContext';
import {
  readBarMode, writeBarMode, onBarModeChange, otherMode, SHORT, LONG,
  SHORT_BAR_BRAND, SHORT_BAR_WATERMARK,
} from './utils/toolbarBarMode';
import TrialWelcomeDialog from './components/TrialWelcomeDialog';

import PropTypes from 'prop-types';

/**
 * The bar along the bottom that narrates what the application just did.
 *
 * <p>A `Box` with theme tokens rather than a `div` with three hexes. It was `#f3f4f6` on
 * `#e5e7eb` with `#334155` text, which is a light bar whatever theme is in force — and it is the
 * full width of the window, so on charcoal it was the brightest band on the screen and the one
 * carrying the least.
 */
function StatusBar({ statusText }) {
  return (
    <Box sx={{
      width: '100%',
      height: 32,
      bgcolor: 'background.paper',
      borderTop: '1px solid',
      borderColor: 'divider',
      color: 'text.secondary',
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      pl: 2,
      boxSizing: 'border-box',
    }}>
      Status: {statusText || 'Ready'}
    </Box>
  );
}

StatusBar.propTypes = {
  statusText: PropTypes.string
};

const unwrapDegrees = (degrees) => {
  if (!Array.isArray(degrees) || degrees.length === 0) return [];
  const out = new Array(degrees.length);
  let offset = 0;
  out[0] = degrees[0];
  for (let i = 1; i < degrees.length; i++) {
    const prevRaw = degrees[i - 1];
    const currRaw = degrees[i];
    const diff = currRaw - prevRaw;
    if (diff < -180) {
      offset += 360;
    } else if (diff > 180) {
      offset -= 360;
    }
    out[i] = currRaw + offset;
  }
  return out;
};


/**
 * A stable empty array for the toolbar's activeActions.
 *
 * A fresh `[]` in the JSX is a new reference on every render, which defeats any memoisation
 * the toolbar has and re-renders 62 buttons whenever anything in App changes.
 */
const EMPTY_ACTIONS = [];

function App() {
  return (
    <ConfigProvider>
      <Router>
        <AuthProvider>
          <HelpProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/registration-pending" element={<RegistrationPending />} />
              <Route path="/payment-pending" element={<PaymentPending />} />
              <Route path="/*" element={<ProtectedRoute />}>
                <Route path="*" element={<SessionScope />} />
              </Route>
            </Routes>
          </HelpProvider>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
}

/**
 * One clean slate per signed-in account.
 *
 * ProtectedRoute already unmounts MainApp on logout, so MainApp's own state resets. The overlay
 * context did not: it was mounted at the root, above AuthProvider, where it could neither see the
 * user nor be reset — so the studies one account drew were still on the chart when another account
 * signed in.
 *
 * Keying on uid remounts the provider (and everything under it) whenever the account changes,
 * which resets that state by construction rather than by remembering to clear each piece. The
 * 'anon' fallback keeps the key stable while the profile is still loading, so a session is not
 * torn down and rebuilt mid-login.
 */
/**
 * How long a symbol-dependent command waits for its bars before it gives up.
 *
 * <p>Generous, because a cold backend reading a thirty-year series from GCS is slow and a
 * command that gave up on a slow load would look exactly like one that failed. It is a backstop
 * against a fetch that never settles at all, not a latency budget: an ordinary failure is caught
 * by the error branch of the drain, within a second.
 */
const COMMAND_SYMBOL_TIMEOUT_MS = 30000;

function SessionScope() {
  const { user } = useAuth();
  return (
    <ChartOverlayProvider key={user?.uid || 'anon'}>
      {/*
        INSIDE the overlay provider, and that is deliberate rather than arbitrary.

        The key above remounts everything under it when the account changes, so the command
        registry gets a clean slate per session for free — the same reason the overlays do. A
        CommandProvider mounted above it would survive a sign-out holding closures over an
        unmounted MainApp's setters, which is a registry that answers and does nothing.

        It also needs to be ABOVE MainApp rather than inside it: MainApp is the registrar, and a
        provider cannot be consumed by the component that renders it.
      */}
      <CommandProvider>
        <MainApp />
      </CommandProvider>
    </ChartOverlayProvider>
  );
}

function MainApp() {
  const { user, isTrial, isAdmin, isExpired, isAuthenticated, token, api,
    profileIncomplete } = useAuth();

  // Always-current handle on `api`, for effects that need to CALL it but must not re-run when it
  // changes identity. `api` is a useMemo over the Firebase ID token, so it is a new object every
  // time the token rotates (~hourly) — an event that says nothing about the data. Assigned during
  // render rather than in an effect so it is guaranteed current before any effect below reads it.
  const apiRef = useRef(api);
  apiRef.current = api;

  const { config: appConfig } = useConfig();
  const [trialWelcomeOpen, setTrialWelcomeOpen] = useState(false);
  useEffect(() => {
    if (isTrial && user?.uid) {
      const key = `trialWelcomeSeen_${user.uid}`;
      if (!sessionStorage.getItem(key)) {
        setTrialWelcomeOpen(true);
        sessionStorage.setItem(key, '1');
      }
    }
  }, [isTrial, user?.uid]);
  /**
   * THE CHART END DATE THAT APPLIES TO THIS USER, or null for no restriction.
   *
   * <p>The server has already cut the bars it sends, so this is NOT how the restriction is
   * enforced — it is enforced in `SharedCacheService`, on the one read path every study and the
   * chart go through. What this is for is the FUTURE case, which the server cannot enforce by
   * omission: it has no bars after today to withhold, so "future bars are accessible for study"
   * means the axis has to reach that date. See `projectionBars`.
   *
   * <p>Declared HERE, far above `projectionBars`, because a hook dependency array is evaluated
   * during RENDER — naming a `const` declared further down throws
   * `Cannot access 'chartEndDate' before initialization` and takes the whole application out,
   * with lint clean and the build compiling. This file has paid for that four times.
   *
   * <p>Null on any failure. The endpoint is authenticated and a signed-out visitor has no role,
   * so a 401 here is the ordinary anonymous case rather than a fault — and the fail-open
   * direction is the same one the server takes.
   */
  const [chartEndDate, setChartEndDate] = useState(null);
  /**
   * HOW FAR FORWARD THIS ACCOUNT MAY SEE — the server's answer, not a role parsed here.
   *
   * <p>`/api/chart-window` carries `projectionTier`, one of ADMIN / REGULAR / CAPPED. Null until
   * the first response lands, which `projectionBars` reads as "no ceiling yet" and leaves the axis
   * at its ordinary length — a brief over-reach that the SERVER refuses to answer anyway, and the
   * alternative (assume CAPPED and draw nothing forward) would flicker the axis on every load.
   *
   * <p>⚠ NOT derived from `isAdmin`/`isTrial`. Those come from the profile, and a fallback user
   * whose profile never arrived is neither — so a derivation would resolve them to REGULAR and
   * grant a quarter of projection to a request the server may cap. `fallbackTier` exists for a
   * backend deployed before this field and is used only there.
   */
  const [projectionTier, setProjectionTier] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) { setChartEndDate(null); setProjectionTier(null); return undefined; }
    apiRef.current.get('/api/chart-window')
      .then((res) => {
        if (cancelled) return;
        setChartEndDate((res.data && res.data.endDate) || null);
        // An older backend answers without the field. `fallbackTier` then reads the flags the auth
        // context already derived, which is a guess — but a guess the server still overrules.
        setProjectionTier((res.data && res.data.projectionTier)
          || fallbackTier({ isAdmin, isTrial }));
      })
      .catch(() => { if (!cancelled) { setChartEndDate(null); setProjectionTier(null); } });
    return () => { cancelled = true; };
    // `apiRef`, not `api`: the token rotates hourly and that says nothing about the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, isTrial]);

    const [srDialogOpen, setSrDialogOpen] = useState(false);
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  const [isLocationSettingsOpen, setLocationSettingsOpen] = useState(false);
  const [locationPreferences, setLocationPreferences] = useState(null);
  useEffect(() => {
    const fetchLocationPreferences = async () => {
      if (isAuthenticated) {
        try {
          const response = await api.get('/api/user/preferences');
          setLocationPreferences(response.data);
        } catch (error) {
          console.error('Error fetching location preferences:', error);
        }
      }
    };

    fetchLocationPreferences();
  }, [isAuthenticated, api]);

  const handleSaveLocationSettings = async (newPreferences) => {
    if (isAuthenticated) {
      try {
        const response = await api.post('/api/user/preferences', newPreferences);
        setLocationPreferences(response.data);
        // You might want to show a success notification here
      } catch (error) {
        console.error('Error saving location preferences:', error);
      }
    }
  };

  function calculateMACD(bars) {
    if (!bars || bars.length < 26) return { macd: [], signal: [], histogram: [] };
    const closes = bars.map(b => Array.isArray(b) ? b[1] : b.close);
    function ema(period) {
      const k = 2 / (period + 1);
      let emaArr = [];
      let prev;
      closes.forEach((close, i) => {
        if (i === 0) {
          prev = close;
          emaArr.push(close);
        } else {
          prev = close * k + prev * (1 - k);
          emaArr.push(prev);
        }
      });
      return emaArr;
    }
    const ema12 = ema(12);
    const ema26 = ema(26);
    const macdLine = ema12.map((v, i) => v - ema26[i]);
    function emaOf(arr, period) {
      const k = 2 / (period + 1);
      let prev;
      let out = [];
      arr.forEach((val, i) => {
        if (i === 0) {
          prev = val;
          out.push(val);
        } else {
          prev = val * k + prev * (1 - k);
          out.push(prev);
        }
      });
      return out;
    }
    const signalLine = emaOf(macdLine, 9);
    const histogram = macdLine.map((v, i) => v - signalLine[i]);
    const macd = macdLine.map((v, i) => ({ time: i, value: v }));
    const signal = signalLine.map((v, i) => ({ time: i, value: v }));
    const hist = histogram.map((v, i) => ({ time: i, value: v }));
    return { macd, signal, histogram: hist };
  }

  const [messagesTabContents, setMessagesTabContents] = useState([
    <div key="summary">Initial Summary Content</div>,
    <div key="message">Initial Message Content</div>,
    <div key="reserved1">Reserved 1</div>,
    <div key="reserved2">Reserved 2</div>
  ]);
  const [messagesOpen, setMessagesOpen] = useState(false);
  // Which tab the Messages panel shows. Owned here so a study can open it on the tab it wrote to.
  const [messagesTabIndex, setMessagesTabIndex] = useState(SUMMARY_TAB_INDEX);
  // Elliott wave analysis is a request/response action rather than a chart overlay, so its result
  // lives here for the panel to render and is replaced by each run.
  const [elliottOpen, setElliottOpen] = useState(false);
  const [elliottLoading, setElliottLoading] = useState(false);
  const [elliottResult, setElliottResult] = useState(null);
  const [elliottError, setElliottError] = useState(null);
  const [elliottOverlayShown, setElliottOverlayShown] = useState(false);
  /**
   * Set when a run lands, cleared by the effect that draws its overlay.
   *
   * <p>The overlay is ON by default now, and drawing it cannot be done in the request handler:
   * the draw reads `elliottResult` and `elliottProjections` through a callback, and inside the
   * handler those still hold the PREVIOUS run's values - React has not re-rendered yet. So the
   * handler raises a flag and an effect below draws once the new state is actually readable.
   */
  const [elliottPendingDraw, setElliottPendingDraw] = useState(false);
  /**
   * The bar the displayed count was anchored at.
   *
   * <p>Held beside the result rather than read off `lastClickedBar` when rendering: the reader can
   * click another bar without re-running, and the panel would then name a bar the count on screen
   * knows nothing about.
   */
  const [elliottAnchor, setElliottAnchor] = useState(null);
  /**
   * The count's legs, and its projections judged against the bars that followed the anchor.
   *
   * <p>Computed once when the response lands rather than while rendering: the verification walks
   * the bar array, and re-running it on every re-render of a dialog would do that work for a
   * scroll. Held beside the result so the two can never describe different runs.
   */
  const [elliottStructure, setElliottStructure] = useState([]);
  const [elliottProjections, setElliottProjections] = useState([]);
  const [elliottScore, setElliottScore] = useState(null);
  const [studyTitle, setStudyTitle] = useState('');

  // Study Journal state — persisted to localStorage
  // Journal is per account. MainApp is remounted by SessionScope when the user changes, so the
  // initialiser re-runs with the new uid rather than carrying the previous account's entries.
  const [journalMarkdown, setJournalMarkdown] = useState(() => loadJournal(user?.uid));
  useEffect(() => { saveJournal(journalMarkdown, user?.uid); }, [journalMarkdown, user?.uid]);

  // Owned here rather than inside the dialog so the effect below can tell when the user is typing.
  const [journalEditing, setJournalEditing] = useState(false);


  const handleJournalAppend = useCallback((entryData) => {
    setJournalMarkdown(prev => appendJournalEntry(prev, entryData));
  }, []);

  /**
   * Log a study report AND put the reader in front of it.
   *
   * A study whose only output is the Journal has to open the panel on the Journal tab, or it
   * appears to have done nothing: the panel opens on Summary, which such a study never fills, so
   * a blank Summary is indistinguishable from a study that produced no result. Studies that fill
   * the Summary tab keep using handleJournalAppend and their own panel handling.
   */
  const handleStudyReportJournal = useCallback((entryData) => {
    setJournalMarkdown(prev => appendJournalEntry(prev, entryData));
    setStudyTitle(entryData?.studyLabel || 'Study');
    setMessagesTabIndex(JOURNAL_TAB_INDEX);
    setMessagesOpen(true);
  }, []);
  const handleJournalReset = useCallback(() => {
    if (window.confirm('Reset the Study Journal? This cannot be undone.')) {
      setJournalMarkdown(resetJournal());
    }
  }, []);

  const handleAspectsAreaHighlightRun = useCallback(({ title, stats, futureDates }) => {
    console.log('[App.js] handleAspectsAreaHighlightRun received:', { title, stats, futureDates });
    setStudyTitle(title || 'Aspects Study'); // Set title or a default
    const tabContents = formatMessagesPanelTabs(stats, futureDates);
    setMessagesTabContents(tabContents);
    setMessagesOpen(true);
  }, [setStudyTitle, setMessagesTabContents, setMessagesOpen]);

  
  const handleStrategyDesignRun = useCallback(({ title, tabContents }) => {
    setStudyTitle(title || 'Strategy Design');
    setMessagesTabContents(tabContents);
    setMessagesOpen(true);
  }, [setStudyTitle, setMessagesTabContents, setMessagesOpen]);

  const eChartRef = useRef(null);
  const animateIntervalRef = useRef(null);
  const animatePausedRef = useRef(false);
  const studyAdvanceRef = useRef(null);

  const { overlays, addOverlay, setAllOverlays, removeOverlay, updateOverlay, deleteLastOverlay, clearOverlays } = useChartOverlays();

  // Every figure a drawing tool produces goes through here so it is stamped with a groupId, which
  // is what makes it eligible for undo and what keeps a multi-line figure (Fibonacci, pitchfork)
  // deleting as one unit. Routing all the drawing adds through a single helper means a new tool
  // cannot silently arrive un-undoable by forgetting the field.
  // Always-current overlay list, for callbacks that need to REPLACE the set without taking
  // `overlays` as a dependency — handleBarSelection would otherwise rebuild on every draw.
  // Assigned during render so it is correct before any handler can fire.
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;

  const addDrawing = useCallback((overlay) => {
    addOverlay({ ...overlay, groupId: overlay.groupId || newOverlayGroupId(overlay.type) });
  }, [addOverlay]);

  // Placed AFTER `overlays` is declared, and that ordering is load-bearing: a hook's dependency
  // array is evaluated during render, so listing `overlays` above its own `const` threw
  // "Cannot access 'overlays' before initialization" and took the whole app down to a blank
  // screen. Neither the build nor the unit tests catch it — it is a temporal-dead-zone error at
  // runtime, in a file no test renders.
  //
  // Keeps the journal's trendline section in step with what is on the chart. The section is
  // delimited and rewritten in place, so drawing a fourth line replaces the block rather than
  // stacking a second copy underneath, and anything typed outside the markers is untouched.
  //
  // Skipped while the journal is open for editing: rewriting the text under an open editor would
  // overwrite what is being typed. It catches up the moment editing ends, which is why
  // journalEditing is a dependency and not just an early return.
  useEffect(() => {
    if (journalEditing) return;
    setJournalMarkdown(prev => {
      const next = upsertTrendlineSummary(prev, overlays);
      return next === prev ? prev : next;   // a no-op write would loop through the save effect
    });
  }, [overlays, journalEditing]);
  const [barData, setBarData] = useState({ ohlc: [], dates: [], volumes: [] });
  /**
   * Bar replay.
   *
   * <p>`simulateOriginalRef` holds the UNMASKED series. It is a ref rather than state because it is
   * never rendered and must not cause one: it exists so exiting restores real prices, and a state
   * update on every reveal would re-render the whole chart twice per step.
   */
  /**
   * The cycle studies currently drawn, by key.
   *
   * <p>Kept because a horizontal band is not a one-shot drawing any more: it appears when its
   * interval closes and disappears if the reader steps back before it, so the run has to survive
   * the click that created it in order to be re-rendered against a new boundary.
   */
  const [cycleRuns, setCycleRuns] = useState({});
  /*
   * ⚠ HOW MANY BARS A CTS MARK SILENCES AFTER IT. `CTS_COOLDOWN_BARS` (4) is the shipped value and
   * the default here; ZERO DISABLES THE FOLD and draws every flagged bar.
   *
   * It is adjustable because the number is arbitrary in a way the rest of CTS is not. The orb and
   * the combined ceiling are measured — 4.4% of days at orb 1.0, two thirds of the orb as the
   * ceiling — but "four bars" is a judgement about how close two marks may sit before they read as
   * one event, and nothing measured it. At intraday density it is also the single largest lever on
   * what reaches the chart: it discards 56% of CTS1's flagged bars and 73% of CTS3's, and WHICH
   * ones survive is decided by walk order rather than by which configuration was tightest.
   *
   * So it is exposed rather than tuned: the way to find a better rule is to look at the unfolded
   * data, and that was not previously possible from the UI.
   */
  const [ctsCooldownBars, setCtsCooldownBars] = useState(CTS_COOLDOWN_BARS);

  /**
   * THE MARKET SCANNER's state.
   *
   * <p>Declared HERE, far above the handlers that write it, because `scannerPendingDraw` is
   * named in a `useEffect` dependency array — and a dependency array is evaluated during
   * RENDER, so a declaration below it throws `Cannot access before initialization` and mounts
   * nothing. `App.tdz.test.js` exists for exactly this and this file has paid for it four times.
   */
  const [scannerOpen, setScannerOpen] = useState(false);
  /*
   * The date the NEXT scan will ask for. Empty means "let the backend default", which resolves to
   * today in the market's timezone -- and today is frequently a date the local EOD store has not
   * reached, which is the whole reason the dialog now offers this control.
   */
  const [scannerDate, setScannerDate] = useState('');
  const [scannerCandidates, setScannerCandidates] = useState([]);
  const [scannerState, setScannerState] = useState(SCAN_STATE.IDLE);
  const [scannerStatus, setScannerStatus] = useState('');
  const [scannerPendingDraw, setScannerPendingDraw] = useState(null);

  const [simulateMode, setSimulateMode] = useState(false);
  const [simulateReveal, setSimulateReveal] = useState(null);
  const simulateOriginalRef = useRef(null);
  /**
   * The boundary, mirrored into a ref.
   *
   * <p>handleBarSelection is handed to EChartComponent as onBarSelect, and that component is
   * explicit that a handler recreated each render makes echarts-for-react dispose and rebuild the
   * whole chart. `simulateReveal` changes on EVERY step, so naming it in that dependency array
   * would rebuild the chart once per bar - a flicker on the one interaction the mode is made of.
   * The handler reads the ref instead, which is current when the click fires.
   */
  const simulateRevealRef = useRef(null);

  /*
   * ⚠ DECLARED HERE, beside the two states it derives from, and NOT beside its readers.
   *
   * A `useMemo` naming it in a dependency array is evaluated during RENDER, so a declaration
   * below the first effect that names it threw `Cannot access 'assessmentBoundary' before
   * initialization` on mount — lint clean, build clean, and the whole application blank. The
   * fourth time this repository has paid for the TDZ class, and the first time `App.tdz.test.js`
   * failed to catch one: its scan did not reach a dependency spread over two lines.
   *
   * ── THE ASSESSMENT BOUNDARY ───────────────────────────────────────────────────────────
   *
   * The last bar the reader can SEE: the replay playhead while a simulation is running, and
   * the end of the series otherwise. Everything the simulator does is bounded by it — which
   * points are assessed at all, how far a theoretical trade may walk, and where a manual leg
   * may land.
   *
   * ONE derivation, named once. Three call sites computing `simulateMode ? simulateReveal :
   * null` would be three chances for one of them to keep reading the whole series, and the
   * symptom would be a card quoting an outcome from bars that are not drawn — invisible
   * unless somebody checked that specific card against the playhead.
   */
  const assessmentBoundary = useMemo(
    () => (simulateMode && Number.isFinite(simulateReveal) ? simulateReveal : null),
    [simulateMode, simulateReveal],
  );
  const assessmentBoundaryRef = useRef(null);
  useEffect(() => { assessmentBoundaryRef.current = assessmentBoundary; },
    [assessmentBoundary]);

  /*
   * ── THE PROGRESSION GATE ──────────────────────────────────────────────────────────────
   *
   * Armed by taking a trade at a paused CTS point; while it holds, the playhead advances one
   * bar per gesture so the reader watches the trade reach its own target, stop or horizon
   * rather than jumping thirty bars and reading the answer. Declared here, beside the boundary
   * it constrains, for the reason the boundary itself is: a dependency array is evaluated
   * during render, and this is named in two of them further down.
   */
  const [replayGate, setReplayGate] = useState(null);
  const replayGateRef = useRef(null);
  useEffect(() => { replayGateRef.current = replayGate; }, [replayGate]);

  /**
   * THE TIMING POINTS A SIMULATE CLICK STOPS AT, as bar indices.
   *
   * <p>A ref for the same reason the boundary is one: `handleBarSelection` is `onBarSelect`, and
   * naming a value that changes with the overlays in its dependency array would rebuild the whole
   * chart. Empty until a Critical Timing Scan has been run, which is the honest default — with no
   * scan on the chart there are no points to stop at and the click behaves exactly as it always
   * did.
   */
  const simulateCtsIndicesRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bumped whenever the market-data-updater reports a finished sync. It is a dependency of
  // the chart data effect below, so a completed sync re-fetches the current symbol instead
  // of leaving the customer looking at yesterday's bars until they refresh the page.
  const [marketDataRevision, setMarketDataRevision] = useState(0);
  /*
   * ⚠ RESTORED FROM STORAGE FIRST, because this state is what a REMOUNT used to silently reset.
   *
   * Nothing in this file resets the symbol — the three `setSelectedSymbol` call sites are all
   * explicit gestures. What reset it was the initialiser running again: Fast Refresh reloading a
   * module in development, or an ordinary page refresh in production, both put the reader back on
   * `defaultTicker` (unset locally, so `SPY`) with nothing on screen to say why.
   *
   * An initialiser FUNCTION rather than a bare value, so `localStorage` is read once on mount
   * instead of on every render. The fallback chain is unchanged and still lives here, which is why
   * `readLastSymbol` answers null rather than holding its own idea of the default.
   */
  const [selectedSymbol, setSelectedSymbol] = useState(
    () => readLastSymbol() || appConfig?.defaultTicker || 'SPY');

  /*
   * Remember it, so the next mount restores it.
   *
   * ⚠ DECLARED HERE, DIRECTLY BELOW THE STATE IT READS. A dependency array is evaluated during
   * RENDER, so an effect naming `selectedSymbol` from above its declaration throws
   * "Cannot access 'selectedSymbol' before initialization" and mounts nothing — with lint clean
   * and the build compiling. `App.tdz.test.js` exists for that failure and this file has paid for
   * it four times.
   *
   * ⚠ AND IT WRITES ON EVERY CHANGE rather than only on unmount. A `beforeunload` or cleanup
   * write would be lost in exactly the case this is for: Fast Refresh swaps the module without
   * running a normal teardown, so the value has to already be stored by the time it happens.
   *
   * `writeLastSymbol` silently declines the synthetic upload symbol, which is why there is no
   * condition here — putting one in would be a second place for "what is storable" to be decided.
   */
  useEffect(() => { writeLastSymbol(selectedSymbol); }, [selectedSymbol]);

  // Every overlay belongs to the symbol it was drawn on, so switching symbol clears the chart.
  //
  // Overlays are positioned by DATE. Carried across to another symbol they either land on
  // unrelated bars or, where the date does not exist in the new series, are silently dropped by
  // the renderer — so a study from the previous chart would appear to have been run on this one,
  // partially, with no indication that it had not been.
  //
  // Keyed on selectedSymbol alone, NOT on barData: a background refresh replaces barData for the
  // same symbol, and clearing there would wipe the customer's drawings every time a sync landed.
  const clearedForSymbolRef = useRef(null);
  useEffect(() => {
    // Skip the very first run, which would only clear an already-empty chart, and would also
    // discard anything restored before the first render.
    if (clearedForSymbolRef.current === null) {
      clearedForSymbolRef.current = selectedSymbol;
      return;
    }
    if (clearedForSymbolRef.current === selectedSymbol) return;
    clearedForSymbolRef.current = selectedSymbol;
    clearOverlays();
    setStatusText(`Loaded ${selectedSymbol}. Overlays from the previous chart were cleared.`);
  }, [selectedSymbol, clearOverlays]);


  const [isStudyDialogOpen, setStudyDialogOpen] = useState(false);
  const [isPlanetFollowStudyDialogOpen, setPlanetFollowStudyDialogOpen] = useState(false);
  const [isAspectsAreaHighlightDialogOpen, setIsAspectsAreaHighlightDialogOpen] = useState(false);
  const [isDeclinationStudyDialogOpen, setDeclinationStudyDialogOpen] = useState(false);
  const [isPlanetaryLinesDialogOpen, setPlanetaryLinesDialogOpen] = useState(false);
  const [isAverageLongitudeDialogOpen, setAverageLongitudeDialogOpen] = useState(false);
  const [isVisualUniverseDialogOpen, setVisualUniverseDialogOpen] = useState(false);
  const [visualUniverseInitialDate, setVisualUniverseInitialDate] = useState(null);
  const [isIntradayAnimationDialogOpen, setIntradayAnimationDialogOpen] = useState(false);
  const [isChartComparisonDialogOpen, setChartComparisonDialogOpen] = useState(false);

  const handleOpenVisualUniverse = (date) => {
    setVisualUniverseInitialDate(date);
    setVisualUniverseDialogOpen(true);
  };
  const [isPlanetGroupDialogOpen, setPlanetGroupDialogOpen] = useState(false);
  const [isStrategyReportStudyDialogOpen, setStrategyReportStudyDialogOpen] = useState(false);
  const [isStrategyReportStudyBDialogOpen, setStrategyReportStudyBDialogOpen] = useState(false);
  const [isAreaHighlightDialogOpen, setIsAreaHighlightDialogOpen] = useState(false);
  const [isAggregateAreaHighlightDialogOpen, setIsAggregateAreaHighlightDialogOpen] = useState(false);
  // Fixed-cycle tool: the dialog collects N, then the next chart click anchors it. The pending
  // length lives in state rather than a ref because the toolbar button reflects it when re-opened.
  const [fixedCycleDialogOpen, setFixedCycleDialogOpen] = useState(false);
  const [fixedCycleLength, setFixedCycleLength] = useState(null);
  const [fixedCycleUnit, setFixedCycleUnit] = useState('bars');
  const [isStrategyDesignDialogOpen, setIsStrategyDesignDialogOpen] = useState(false);
  const [isStrategy2DesignDialogOpen, setIsStrategy2DesignDialogOpen] = useState(false);
  const [isTimeAndEventStrategyDialogOpen, setTimeAndEventStrategyDialogOpen] = useState(false);
  const [isCyclesDialogOpen, setCyclesDialogOpen] = useState(false);
  const [isPlanetCharacteristicsDialogOpen, setIsPlanetCharacteristicsDialogOpen] = useState(false);
  const [isAstroGraphDialogOpen, setIsAstroGraphDialogOpen] = useState(false);
  const [astroGraphCommentary, setAstroGraphCommentary] = useState('');
  const [studyMode, setStudyMode] = useState(false);
  const [studyVisibleCount, setStudyVisibleCount] = useState(0);
  const [bar1Date, setBar1Date] = useState(null);
  const [bar2Date, setBar2Date] = useState(null);
  const [nextBarToSelect, setNextBarToSelect] = useState('bar1'); // 'bar1' or 'bar2'
  // Whether the NEXT hand-drawn trendline carries its points-and-percent label. Held as a ref
  // alongside the state for the same reason overlaysRef exists: handleBarSelection reads it when a
  // click lands, so the setting can change without rebuilding that callback — and rebuilding it
  // makes EChartComponent tear down and re-create the chart, which shows as flicker.
  // Spiral Calendar scan: the zone the badge reports, the parameters it ran with, and whether the
  // band had to be clipped at the chart edge.
  const [spiralZone, setSpiralZone] = useState(null);
  const [spiralParams, setSpiralParams] = useState(null);
  const [spiralTruncated, setSpiralTruncated] = useState(false);
  // Spiral inspection: the right-click menu's position, and the result it produced.
  const [spiralMenu, setSpiralMenu] = useState(null);
  const [spiralInspection, setSpiralInspection] = useState(null);
  /**
   * Whether the user has dismissed the scan chip.
   *
   * Keyed to nothing — it is reset by the effect below whenever a new zone arrives — because a
   * dismissal is about the answer currently on screen, not a standing preference. A user who hides
   * one result and then runs another scan is asking to see the new one.
   */
  const [spiralBadgeDismissed, setSpiralBadgeDismissed] = useState(false);
  /**
   * Whether a plain left-click on a bar runs a spiral inspection.
   *
   * Off by default, and that is deliberate rather than cautious: left-click already drives the
   * drawing tools and the two-bar selection, so turning this on silently would change what an
   * ordinary click does with nothing on screen to say why.
   */
  const [spiralClickMode, setSpiralClickMode] = useState(null);
  /** True once an inspection is on the chart, so the Clear control can appear only when it does. */
  const [spiralDrawn, setSpiralDrawn] = useState(false);
  // The scan result, kept so the mode dialog can offer its turns without scanning again.
  const [spiralScan, setSpiralScan] = useState(null);
  /**
   * The last bar the reader clicked, whatever the click also did.
   *
   * <p>Recorded at the very top of handleBarSelection, before any of the branches that consume a
   * click, so "the last bar clicked" means exactly that — a bar that placed a Fibonacci point or
   * ran an inspection was still clicked, and a reader who then asks about "the last bar clicked"
   * means that one. Reading it off a drawing or an inspection instead would answer a narrower
   * question than the one the option is named for.
   */
  const [lastClickedBar, setLastClickedBar] = useState(null);
  const [patternPredictionOpen, setPatternPredictionOpen] = useState(false);
  /**
   * The TrendPulse curve the pattern dialog detects shapes on.
   *
   * <p>Read from the overlay rather than held separately, so it is by construction the same curve
   * the reader is looking at. If several TrendPulse runs are on the chart the first is used — a
   * shape found across two different curves would not be a shape.
   */
  const trendPulseSeriesForPatterns = useMemo(() => {
    const ov = (overlays || []).find(o => o && o.type === 'astroGraphLine' && Array.isArray(o.data));
    return ov ? ov.data : null;
  }, [overlays]);
  /**
   * Open while an administrator is choosing the two orbs by hand.
   *
   * <p>The toolbar no longer opens this - CTS runs directly at its preset orb. The dialog and
   * this state are retained deliberately, so the parameters stay explorable.
   */
  const [gannDialogOpen, setGannDialogOpen] = useState(false);
  /**
   * How many bars are on screen, and whether that counts as zoomed in for CTS.
   *
   * <p>Held in a ref as well as in state: the scan handler reads it when it builds the overlay, and
   * a handler that closed over the state value would build with whatever the zoom was when the
   * handler was created rather than when the button was pressed.
   */
  const ctsZoomedRef = useRef(false);
  const [ctsZoomed, setCtsZoomed] = useState(false);
  /**
   * Whether the zones on the chart came from a window centred somewhere other than the last bar.
   *
   * <p>Read off the response rather than off which button was pressed, so a clamped anchor that
   * landed back on the last bar reports itself as unanchored — which is what it is.
   */
  const [spiralAnchored, setSpiralAnchored] = useState(false);
  const [spiralDialogOpen, setSpiralDialogOpen] = useState(false);
  // Every near-term zone drawn, and how many the palette could not take.
  const [spiralZones, setSpiralZones] = useState([]);
  const [spiralUndrawn, setSpiralUndrawn] = useState(0);

  // Declared AFTER spiralZone and spiralZones, not next to the flag it sets. A hook whose
  // dependency array names a const declared further down throws "Cannot access before
  // initialization" on first render and takes the whole app to a blank screen — this file has done
  // that twice, and neither the build nor the unit tests catch it.
  //
  // A new zone un-dismisses the chip: hiding one answer is not a request to never see another.
  // Depends on the state objects themselves, which only change identity when a scan sets them, so
  // this does not fire on unrelated renders.
  useEffect(() => {
    setSpiralBadgeDismissed(false);
  }, [spiralZone, spiralZones]);


  const [isManualDrawMode, setIsManualDrawMode] = useState(false);
  const [manualDrawStyle, setManualDrawStyle] = useState({ color: '#FF00FF', lineWidth: 3 });

  // Drawing tool state: null | 'draw_vertical_line' | 'draw_horizontal_line' | 'draw_trendline' | 'draw_rectangle'
  const [drawingMode, setDrawingMode] = useState(null);
  // Colour applied to newly drawn shapes and text. Existing drawings keep whatever they were
  // created with, so a chart can be marked up in several colours.
  const [activeDrawingColor, setActiveDrawingColor] = useState(DEFAULT_DRAWING_COLOR);
  const drawingFirstClick = useRef(null); // stores {date, price, dataIndex} for two-click tools
  // Separate accumulator for the multi-click technical tools. Kept apart from drawingFirstClick
  // rather than generalising it, so the vertical/horizontal/trendline/rectangle paths that already
  // work are not disturbed by a tool that needs a third click.
  const drawingPoints = useRef([]);
  // Text annotation size cycles Small -> Medium -> Large on each activation of the T tool.
  // Two pieces of state, deliberately: textSizeIndex is what the NEXT click will use, while
  // activeTextSize is what the current activation draws at. Deriving one from the other at
  // placement time would place text at the next size rather than the one the badge advertised.
  const [textSizeIndex, setTextSizeIndex] = useState(0);
  const [activeTextSize, setActiveTextSize] = useState(() => textSizeAt(0));
  
  const [chartType, setChartType] = useState('candlestick');
  const [barFloaterMode, setBarFloaterMode] = useState('ohlcv');
  // Watermark override for admin: null = default, { hidden: true } = no watermark, { text, fontSize } = custom
  const [watermarkOverride, setWatermarkOverride] = useState(null);
  // The watermark follows the customer's market, not the deployment: US shows integralDelta.ai,
  // India shows Dhanabhyas. Anonymous visitors and the moment before the profile loads fall back
  // to the deployment's own brand.
  // marketId is the stored state, but /api/user/me also returns `market` ("US"/"IN"), and the
  // deployment config carries one too. Taking the first that resolves means a profile missing one
  // of them — or a chart rendered before the profile arrives — still brands correctly.
  /**
   * The customer's own name for the application, if they have set one.
   *
   * <p>Seeded from storage at mount and kept in step with BOTH tabs: the `storage` event covers
   * other tabs and a custom event covers this one, because the tab that writes never hears its own
   * storage event. Without the second, renaming would need a reload — which is the one thing this
   * dialog exists to avoid.
   */
  const [brandOverride, setBrandOverride] = useState(() => readBrandOverride());
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [barMode, setBarMode] = useState(() => readBarMode());

  /**
   * The preset actually in force.
   *
   * <p>An admin gets their own stored choice; everyone else gets the one this DEPLOYMENT ships,
   * and has no control to change it. The preset decides what the product is called and which
   * studies are reachable from the toolbar, which makes it a deployment decision rather than a
   * per-browser preference — and a browser preference is exactly what localStorage is, reachable
   * from devtools by anyone who thinks to look.
   *
   * <p>Falls back to the stored value only when the config names nothing recognisable, so a
   * malformed REACT_APP_BAR_MODE cannot strand a region with a blank preset.
   */
  const effectiveBarMode = useMemo(() => {
    if (isAdmin) return barMode;
    const deployed = appConfig?.barMode;
    return deployed === SHORT || deployed === LONG ? deployed : barMode;
  }, [isAdmin, barMode, appConfig]);

  useEffect(() => onBrandChange(setBrandOverride), []);
  useEffect(() => onBarModeChange(setBarMode), []);

  /**
   * THE CUSTOMER'S OWN CHART COLOURS, and the dialog that edits them.
   *
   * <p>Subscribed the same way the bar mode is, and for the same reason: the change can arrive from
   * this tab (the dialog's Save) or from another one (a second window on the same chart). Held in
   * React state so a change re-renders, which is the whole of "applied immediately" - the colours
   * are resolved by ROLE inside chartDataProcessor, so nothing has to re-run a study or walk the
   * overlay list patching hex values into objects that are already drawn.
   */
  const [workspaceColors, setWorkspaceColors] = useState(() => readWorkspaceColors());
  useEffect(() => onWorkspaceColorsChange(setWorkspaceColors), []);
  const [configurationDialogOpen, setConfigurationDialogOpen] = useState(false);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [dynaManSimulatorOpen, setDynaManSimulatorOpen] = useState(false);
  /**
   * THE PRODUCTION DYNAMAN VIEW, for ASC and AI.
   *
   * <p>Its own state and its own dialog rather than a mode flag on the research one. A single
   * dialog that rendered differently per profile would make "which dialog did they see" a
   * question nobody could answer from the button that opened it -- and would couple a scaffold
   * that is about to be redesigned to the one surface that must not change under it.
   */
  const [dynaManProductionOpen, setDynaManProductionOpen] = useState(false);
  /**
   * WHICH TOOLBAR PROFILE IS RENDERED. A DEVELOPMENT control, not a permission.
   *
   * <p>Seeded from localStorage so a reload during a review does not silently return the reader
   * to Admin. When real role resolution arrives it replaces the default inside `readProfile` and
   * nothing here changes -- the switch is deleted and the state stays.
   *
   * <p>It changes what is RENDERED and nothing else. Every endpoint keeps its own authorisation,
   * so a reader who flipped to Admin would still be refused by the backend.
   */
  /*
   * ⚠ `toolbarProfile` IS GONE (2026-11-01), with the dropdown that set it.
   *
   * It previewed another product's toolbar by selecting a PROFILE, and the toolbar stopped
   * consulting profiles when it became manifest-driven — so the control changed nothing, which
   * is this codebase's own worst category. The Admin Panel's PRODUCT SWITCHER is the surviving
   * preview and it drives the manifest: one mechanism rather than two that can disagree.
   */
  const [portfolioOpen, setPortfolioOpen] = useState(false);


  const watermarkText = watermarkForMarket(
    user?.marketId ?? user?.market ?? appConfig?.market);
  const handleToggleWatermark = useCallback(() => {
    setWatermarkOverride(prev => {
      if (!prev) return { hidden: true };                             // default → hidden
      if (prev.hidden) return { text: watermarkText, fontSize: 120 }; // hidden → small
      return null;                                                    // small → back to default
    });
  }, [watermarkText]);
  /*
   * THE WORKSPACE THEME, from the persisted preference rather than hardcoded.
   *
   * <p>Two prebuilt theme objects selected between, never `createTheme` called in a render. A
   * theme built per render is a NEW OBJECT every time, and MUI's context carries it to every
   * styled component — so every `sx` on the page would recompute on every keystroke, and the
   * chart's own option memo names `theme.palette.mode` in its dependency list and would rebuild
   * an 8,000-bar option object with it.
   *
   * <p>`mode` is what the toggle reads back, and it is also what `cycleHandleColors` and the
   * chart palette key on — so the one store answers for all three and they cannot disagree about
   * which theme is on screen.
   */
  const mode = useThemeMode();
  const theme = mode === DARK ? darkTheme : coolWhiteTheme;
  /*
   * Through a REF for `renderCycleBands`, which is a `useCallback` the reveal watcher names in
   * its own dependency list. Naming `mode` there would rebuild that callback on a theme change
   * and re-run the watcher, which appends a band summary to the status line — a theme switch
   * would narrate itself as though the reader had stepped the replay. The effect below re-draws
   * the bands deliberately and quietly instead.
   */
  const themeModeRef = useRef(mode);
  themeModeRef.current = mode;

  /**
   * What each configurable element looks like when the customer has NOT chosen.
   *
   * <p>Read from the studies that own those colours rather than restated, so a swatch in the dialog
   * cannot disagree with the chart behind it.
   */
  const workspaceColorDefaults = useMemo(() => ({
    // FROM THE SAME SOURCE the chart draws from -- the lesson of the rsiLine swatch, which
    // showed grey for a line drawing blue because its default lived in only one of the two
    // places that needed it.
    ptaVertical: cycleColors('long').pillar,
    ptaHorizontal: cycleColors('long').fill,
    ptbVertical: cycleColors('short').pillar,
    ptbHorizontal: cycleColors('short').fill,
    // THE THREE CTS STATES, read off the one declaration that paints them rather than retyped --
    // the same lesson as rsiLine below, one step earlier.
    ctsUp: ctsMarkStyle(true).color,
    ctsDown: ctsMarkStyle(false).color,
    ctsMark: CTS_MARK_COLOR,
    // THE INDICATOR LINES. Missing here once, and the symptom is worth recording: the dialog
    // falls through to its own '#888888' placeholder for a role it has no default for, so the
    // RSI swatch opened on GREY for a line that was drawing blue -- the picker misreporting the
    // current colour before a reader touches it.
    //
    // From the theme's own mode, because the two themes differ: one stored hex cannot serve
    // both, which is the same reason the role falls back to the active theme rather than to a
    // single constant.
    rsiLine: rsiLineDefault(theme?.palette?.mode),
    // `theme` NAMED so the exhaustive-deps rule has nothing to say, and so a theme that one day
    // becomes switchable recomputes rather than serving the previous mode's swatch. It is a
    // module-level constant today, so this still computes exactly once.
  }), [theme]);
  const [isPatternConfigDialogOpen, setIsPatternConfigDialogOpen] = useState(false);
    const [maConfigs, setMaConfigs] = useState([
    { period: 20, color: '#2979FF', enabled: false },
    { period: 50, color: '#FF9800', enabled: false },
    { period: 200, color: '#4CAF50', enabled: false },
  ]);

  // Removes the most recent addition — a drawn figure or a whole study run. Reports what happened
  // either way: a shortcut that silently does nothing is indistinguishable from one that is broken.
  const handleDeleteLastDrawing = useCallback(() => {
    const removed = lastOverlaySize(overlays);
    if (removed === 0) {
      setStatusText('Nothing to undo — the chart is empty.');
      return;
    }
    // A CYCLE STUDY IS NOT ONLY ITS OVERLAYS, and deleting the overlays alone did not delete it.
    //
    // `cycleRuns` holds the intervals a drawn cycle returned, because a band has to be
    // re-rendered whenever the reveal boundary moves — and that effect redraws EVERY run it
    // finds, not just the one last clicked. So removing the overlays left the run in state and
    // the very next pass put the bands straight back: undo appeared to work and then the bands
    // reappeared the moment the other cycle was drawn. Purging the run is what makes the
    // deletion stick; without it the overlay array is a cache the effect refills.
    const cycleKey = cycleKeyForGroupId(lastOverlayGroupId(overlays));
    if (cycleKey) {
      setCycleRuns((prev) => {
        if (!(cycleKey in prev)) return prev;
        const next = { ...prev };
        delete next[cycleKey];
        return next;
      });
    }
    deleteLastOverlay();
    setStatusText(removed > 1
      ? `Removed last addition (${removed} overlays).`
      : 'Removed last addition.');
  }, [overlays, deleteLastOverlay]);

  // What the chart is busy computing, or null. Drives the overlay and the wait cursor.
  const [computingLabel, setComputingLabel] = useState(null);

  /**
   * Runs blocking work with a visible busy indicator.
   *
   * The two animation frames are the point. These scans hold the main thread for seconds on a long
   * chart, and JavaScript cannot paint while it is blocked — so setting the state and calling the
   * work in the same tick shows nothing at all, then freezes, then shows the finished result. The
   * user sees an unresponsive page and no explanation. Yielding twice lets React commit the state
   * and lets the browser paint it before the thread is taken.
   *
   * The indicator's spinner is a CSS animation, which keeps turning on the compositor while the
   * main thread is busy, so it stays honest for the whole wait rather than freezing mid-spin.
   */
  const runWithBusyIndicator = useCallback((label, work) => {
    setComputingLabel(label);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      try {
        work();
      } catch (err) {
        console.error('[App.js] study failed:', err);
        setStatusText(`${label} failed: ${err.message}`);
      } finally {
        // In a finally so a thrown error cannot strand the page behind a permanent overlay.
        setComputingLabel(null);
      }
    }));
  }, []);

  // Esc cancels drawing mode; Ctrl/Cmd+Z and Delete/Backspace undo the last drawing.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Never steal a keystroke from a field the user is typing in. Backspace in the annotation
      // edit box must delete a character, and Ctrl+Z there must undo typing — not silently
      // remove a drawing somewhere else on the chart.
      const el = e.target;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (el && el.isContentEditable)) return;

      if (e.key === 'Escape' && drawingMode) {
        setDrawingMode(null);
        drawingFirstClick.current = null;
        drawingPoints.current = [];
        setStatusText('Drawing cancelled.');
        return;
      }
      // A dialog or popover being open means the keystroke is meant for it, not for the chart
      // underneath. Without this, pressing Delete while a study dialog is up would quietly remove
      // a drawing the user cannot even see, and the status message would scroll past unread.
      // MUI unmounts modals when closed, so this selector only matches while one is actually open.
      if (document.querySelector('.MuiModal-root')) return;

      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z');
      const isDelete = !e.ctrlKey && !e.metaKey && !e.altKey && (e.key === 'Delete' || e.key === 'Backspace');
      if (isUndo || isDelete) {
        // Backspace outside a field navigates back in some browsers, and Ctrl+Z would reach the
        // browser's own undo stack.
        e.preventDefault();
        handleDeleteLastDrawing();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawingMode, handleDeleteLastDrawing]);

  const studyBarData = useMemo(() => {
    if (!studyMode || !barData || !barData.dates || studyVisibleCount <= 0) return barData;
    const count = Math.min(studyVisibleCount, barData.dates.length);
    return {
      ...barData,
      ohlc: barData.ohlc.map((bar, i) => i < count ? bar : [null, null, null, null]),
      volumes: barData.volumes.map((v, i) => i < count ? v : 0),
      realBarCount: count,
    };
  }, [studyMode, barData, studyVisibleCount]);

  const effectiveBarData = studyMode ? studyBarData : barData;
  /* The scan whose triggers are waiting to be painted. Declared HERE, above the projection memo
     that reads it, because a hook dependency array is evaluated during render - listing it below
     its own const throws "Cannot access before initialization" and takes the whole app down, which
     is the temporal dead zone App.tdz.test.js exists to catch. */
  const [pendingScan, setPendingScan] = useState(null);

  /**
   * How far past the last bar the axis must reach.
   *
   * <p>250 weekdays normally — about a year of projection room. A pending scan whose furthest
   * trigger lies beyond that widens it, because the alternative was anchoring that trigger back
   * onto the last real bar and putting a phantom mark on today's candle.
   *
   * <p>Study mode pads nothing, and that is unchanged: it is a view of history alone.
   */
  const projectionBars = useMemo(() => {
    if (studyMode) return 0;
    const base = extendBarData(effectiveBarData, 250);
    const needed = padBarsNeededFor(pendingScan?.signals, base.dates);
    /*
     * ...and far enough to REACH A CONFIGURED END DATE that lies in the future.
     *
     * This is the other half of the server-side cut. A date in the PAST is enforced entirely by
     * the server, which simply does not send those bars. A date in the FUTURE cannot be: there
     * are no future prices and the server must not invent any — so what "future bars are
     * accessible for study" can honestly mean is that the AXIS reaches that date, giving every
     * projection, pillar and scan room to draw into it.
     *
     * Counted in WEEKDAYS, because that is what `extendBarData` appends; a calendar count would
     * fall about 40% short. Zero whenever the date is absent, unparseable or already past, so
     * the ordinary chart is byte-identical to what it was.
     */
    const dates = effectiveBarData?.dates;
    const lastBar = dates?.[dates.length - 1];
    const toEndDate = weekdaysBetween(lastBar, chartEndDate);
    const wanted = Math.max(250, 250 + needed, toEndDate);

    /*
     * ⚠ ...AND THEN CAPPED AT THE ACCOUNT'S OWN PROJECTION CEILING.
     *
     * Every term above is a FLOOR — "at least 250", "at least far enough to reach the configured
     * end date", "at least far enough for the pending scan's furthest trigger". This is the only
     * CEILING, and it has to be applied last for that reason: a `max` taken after it would undo it.
     *
     * ⚠ THE NARROWER OF TWO RESTRICTIONS WINS. A configured chart end date in the FUTURE extends
     * the axis to reach it; the role ceiling says how far forward this account may see at all. Both
     * are restrictions, so an admin-set date of 2027-06-30 does not grant a REGULAR customer two
     * extra quarters — the same rule `narrowestBound` states for the band clip.
     *
     * ⚠ AND IT IS THE AXIS ONLY. The server clamps every forward request before a study reads its
     * range (see `security/FutureProjectionCeiling`), so this does not withhold anything — it stops
     * the chart drawing months of empty canvas the server will not fill, which reads as a study
     * that silently stopped working. Null tier — before the first `/api/chart-window` answer, or
     * when it failed — leaves the axis alone, because the enforcement is not here.
     */
    const ceiling = getChartFutureCeilingDate(lastBar, projectionTier);
    if (!ceiling) return wanted;
    // In WEEKDAYS, because that is what `extendBarData` appends; a calendar count would overshoot
    // by about 40% and put the axis past the ceiling it was computed from.
    const allowed = weekdaysBetween(lastBar, ceiling);
    return Math.max(0, Math.min(wanted, allowed));
  }, [effectiveBarData, studyMode, pendingScan, chartEndDate, projectionTier]);

  const extendedBarData = useMemo(() => extendBarData(effectiveBarData, projectionBars), [effectiveBarData, projectionBars]);

  /**
   * THE LAST COLUMN THAT HAS A PRICE, as a date. Null only when there is no series at all.
   *
   * <p>Two things make a column priceless and both resolve here: the 250 synthetic weekdays
   * `extendBarData` appends past the last real bar, and — in Alpha Replay — every bar after the
   * reveal, which `maskBarsAfter` has nulled.
   *
   * <p>Read by the chart's rail-anchored marker branch, which draws a CTS mark at its price
   * where there is one and at the foot of the grid where there is not. The whole point is that
   * it MOVES: as the replay reveals bars, marks snap off the rail onto the chart one at a time.
   */
  /**
   * THE LAST BAR THE MARKET ACTUALLY TRADED, and that is NOT `revealedUpToDate`.
   *
   * <p>Those two agree outside a replay and diverge inside one, which is the whole reason this
   * exists: `revealedUpToDate` is the playhead when a replay is running, so a renderer asking
   * "is this column forward of the real series" cannot use it. The padded columns past the last
   * traded bar are forward whoever is looking and wherever the playhead sits.
   *
   * <p>Read by the rail-vertical rule, which must draw a line under a CTS mark in the padded
   * future and must NOT draw one under every mark a replay is merely hiding — a replay opened
   * at bar 1 would otherwise hatch the chart, over the CTS pillars it already draws.
   */
  const lastTradedDate = useMemo(() => {
    const dates = effectiveBarData?.dates;
    return (Array.isArray(dates) && dates.length) ? dates[dates.length - 1] : null;
  }, [effectiveBarData]);

  /**
   * HOW MUCH OF CTS, PTA AND PTB THIS ACCOUNT MAY SEE — one value, read in four places.
   *
   * <p>Nulls for a paying or administrator account, which is what makes every consumer's
   * fast path "no cutoff, nothing to do" rather than a branch on the role. See
   * `utils/trialSignalCutoff` for the month arithmetic and for why this is presentational.
   *
   * <p>Keyed off `lastTradedDate` rather than the padded axis, deliberately: the 250 synthetic
   * weekdays reach about a year past real price, and measured from there a one-month cutoff
   * withholds nothing at all.
   *
   * <p>Declared HERE, immediately after the date it reads, because every consumer below is a
   * `useCallback` or `useMemo` that names it in a dependency array — and a dependency array is
   * evaluated during RENDER, so a `const` declared beneath its own readers takes the whole app
   * down on mount. That is the temporal dead zone `App.tdz.test.js` exists to catch.
   */
  const trialCutoffs = useMemo(
    () => trialCutoffDates(lastTradedDate, { isTrial, isAdmin }),
    [lastTradedDate, isTrial, isAdmin]);

  const revealedUpToDate = useMemo(() => {
    const dates = effectiveBarData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) return null;
    if (simulateMode && Number.isFinite(simulateReveal)) {
      return dates[Math.max(0, Math.min(simulateReveal, dates.length - 1))] || null;
    }
    // Outside a replay the real series IS the revealed part, and everything `extendBarData`
    // added beyond it is empty canvas.
    return dates[dates.length - 1] || null;
  }, [effectiveBarData, simulateMode, simulateReveal]);

  // Declared AFTER extendedBarData, and that ordering is load-bearing. A hook dependency array
  // is evaluated during render, so listing extendedBarData above its own `const` threw
  // "Cannot access 'extendedBarData' before initialization" and took the whole app down on
  // mount. The build does not catch it and no unit test renders this file, so nothing flagged
  // it — the same temporal dead zone trap this component has hit before.

  // The two admin pivot studies. The builders re-check isAdmin themselves — the hidden toolbar
  // button is a convenience, not the guard — and these handlers report the outcome either way,
  // since a study that legitimately finds nothing looks identical to one that silently failed.
  const runAdditionSeries = useCallback(() => {
    // Anchored on the clicked bar, exactly. Falling back to the last bar when nothing has been
    // clicked keeps the button useful on first press; it is still ONE bar, never a search.
    const picked = Number.isInteger(lastClickedBar?.index) ? lastClickedBar.index : undefined;
    const result = buildAdditionSeriesStudy(barData, {
      isAdmin, anchorIndex: picked, extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      const where = picked === undefined
        ? 'the last bar'
        : `${barData?.dates?.[picked] ?? `bar ${picked}`}`;
      // Names the bar it counted from. The study no longer tries neighbours, so "nothing found"
      // is a statement about ONE bar and the reader can go and pick a different one.
      setStatusText(`Addition series: no qualifying pivot sequence counting back from ${where}. `
        + 'Click a different bar and run it again.');
      return;
    }
    // One call, so the dots and the forward verticals are a single undo unit — undoing a study
    // halfway and leaving its projections behind without the series would be worse than not
    // undoing at all.
    addOverlay([result.overlay, ...(result.lines || [])]);
    const { seeds, terms, pivotCount, anchorDate,
      forwardOnChart, forwardProjected, forwardUnreachable } = result.summary;
    setStatusText(`Addition series from bar 0 (${anchorDate})`
      + `${picked === undefined ? ' — the last bar; click a bar to anchor it elsewhere' : ''}: `
      + `seeds ${seeds.join(' + ')}, ${terms.length} terms, ${pivotCount} marks`
      + `; forward ${forwardOnChart} on chart, ${forwardProjected} projected`
      + `${forwardUnreachable ? `, ${forwardUnreachable} beyond the chart` : ''}.`);
  }, [barData, extendedBarData, isAdmin, addOverlay, lastClickedBar]);

  const [sweepRunning, setSweepRunning] = useState(false);

  /**
   * The Integral Delta auto-analysis, into the Study Journal.
   *
   * <p>The Journal rather than a pop-up, and the choice is about what the output IS: a table over
   * hundreds of trades, whose whole purpose is comparison - against yesterday's run, against another
   * symbol, against a different set of ATR multiples. A modal is gone when dismissed and cannot be
   * compared with anything.
   *
   * <p>`handleStudyReportJournal` rather than `handleJournalAppend`, because this study fills no
   * Summary tab: the panel opens on Summary, so appending alone would leave the reader looking at
   * a blank tab, which is indistinguishable from a study that produced nothing. The status line
   * gets the one-line version, since it can hold a sentence but not a table.
   *
   * <h2>And it draws, which it did not before</h2>
   *
   * The numbers alone cannot say why a result is bad. Refining the stop multiples means seeing
   * where a trade entered and where it was taken out against the bars around it, so every trade
   * whose entry and exit both fall on the loaded chart is drawn: a connector coloured by outcome,
   * a triangle at the entry pointing the way the trade was taken, and a hollow diamond at the exit.
   *
   * <p>The previous drawing is removed first. The overlay carries ONE id for all three series, so
   * this cannot leave half of a superseded run behind — the failure mode that made the spiral
   * inspection overlays need a list of every id they can produce.
   */
  const handleAnalysisComplete = useCallback((report) => {
    handleStudyReportJournal({
      studyLabel: `Integral Delta — ${report.symbol}`,
      reportText: formatAnalysisReport(report),
    });

    removeOverlay(INTEGRAL_DELTA_TRADES_OVERLAY_ID);
    const overlay = buildTradeOverlay(report, barData);
    if (overlay.drawnCount > 0) addOverlay(overlay);
    // Both lines: the headline is the result, and the overlay line says how much of it is on screen.
    // A run whose trades all fall outside the loaded range would otherwise look like it drew nothing
    // for no reason.
    setStatusText(`${headline(report)} ${tradeOverlayStatus(overlay)}`);
  // setStatusText is deliberately absent, as it is from every sibling handler here: a stable setter
  // declared much later in this component, and naming it in a dependency array reaches it before
  // its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [handleStudyReportJournal, barData, addOverlay, removeOverlay]);

  /**
   * Draw a finished DynaMan run on the price chart.
   *
   * <h2>Why this is worth a chart and not just the ledger table</h2>
   *
   * The table says a premise exited on a 30-minute bar at 10:00. Only the chart says whether that
   * was the right call — what the bars around it were doing, and whether the level the rule named
   * was actually where price turned. That is the whole of manual validation, and it is the reason
   * the exit tooltip leads with the timeframe.
   *
   * <p>The previous drawing is removed first. One overlay id carries the connectors and both
   * marker sets, so this cannot leave half of a superseded run behind.
   *
   * <p>It uses its OWN id, separate from the Integral Delta trades overlay, so a reader can have
   * both studies on the chart at once and clear either one alone.
   */
  /* -- the DynaMan replay engine ------------------------------------------------------------
   *
   * The backend runs the simulation at FULL SPEED and hands over every frame; the clock lives
   * here. That is what makes the speed adjustable mid-run, pause actually pause, and stepping
   * backwards possible at all - none of which a server-paced stream can do.
   */
  const [replayTimeline, setReplayTimeline] = useState(null);

  // A getter, not the instance: the chart mounts after this render, and a stable identity keeps
  // the overlay's playhead effect from re-running on every App render.
  const getChartInstance = useCallback(
    () => eChartRef.current?.getEchartsInstance?.() || null, []);

  /** Load a run and arm the transport. The overlay is created here, once. */
  const handleStartReplay = useCallback((timeline) => {
    if (!timeline || !(timeline.frames || []).length) {
      setStatusText('DynaMan replay: the run produced no frames to replay.');
      return;
    }
    removeOverlay(REPLAY_PLAYHEAD_OVERLAY_ID);
    removeOverlay(REPLAY_ANNOTATION_OVERLAY_ID);
    removeOverlay(TIME_BAND_OVERLAY_ID);
    removeOverlay(CTS_PILLAR_OVERLAY_ID);
    removeOverlay(LEDGER_MARKER_OVERLAY_ID);
    removeOverlay(FAN_OVERLAY_ID);
    // THE PTA/PTB STRUCTURE, if the run had any. Created before the playhead so the bands and
    // their Top/Bottom rays sit UNDER everything else -- they are the background a reader audits
    // the trades against, not another mark competing with them.
    //
    // The levels come from the engine, not from this file: see timeBandOverlay for why a chart
    // that derived its own would be auditing itself.
    //
    // AGAINST THE EXTENDED AXIS, which is what the chart actually draws -- the real bars plus 250
    // synthetic weekdays. A generation band is pure calendar, so one standing in that empty space
    // ahead of the price reveals nothing; watching the candles animate into it is the point of the
    // forward canvas. The unpadded list would drop every forward band on the floor.
    const replayAxis = extendedBarData?.dates?.length ? extendedBarData.dates : barData?.dates;
    const bands = buildTimeBandOverlay(
      timeline.timeBands, replayAxis, replaySessionDate(timeline.frames[0].time));
    if (bands) addOverlay(bands);
    // THE CTS PILLARS and THE FILL MARKERS, created EMPTY and then merged by id on every bar.
    //
    // Empty on purpose: a silent series with no marks costs nothing, and it has to exist before
    // the clock's first tick can merge into it. Creating them only when there is something to
    // draw would mean a CTS scan run AFTER a replay started had nowhere to land -- the merge
    // would address a series that was never declared, and ECharts would ignore it in silence.
    addOverlay({
      id: CTS_PILLAR_OVERLAY_ID,
      type: 'ctspillars',
      label: 'CTS timing pillars',
      points: [],
      upToDate: replaySessionDate(timeline.frames[0].time),
    });
    addOverlay({
      // THE FAN, empty until the playhead reaches a pivot. Declared here for the same reason the
      // pillars and the markers are: the overlay's per-bar merge addresses this series by id, and
      // a merge onto a series that was never declared is ignored in silence.
      id: FAN_OVERLAY_ID,
      type: 'fanlines',
      label: 'Accelerating fan',
      trades: timeline.trades,
      upToDate: replaySessionDate(timeline.frames[0].time),
    });
    addOverlay({
      id: LEDGER_MARKER_OVERLAY_ID,
      type: 'ledgermarkers',
      label: 'Algo / human fills',
      timeline,
      human: null,
      upToIndex: 0,
      upToDate: replaySessionDate(timeline.frames[0].time),
    });
    // Seeded at the first frame so the overlay exists before the first tick updates it.
    const first = buildPlayheadOverlay(
      replaySessionDate(timeline.frames[0].time), barData?.dates, { label: 'CASH' });
    if (first) addOverlay(first);
    // The annotation series, created once and then MERGED by id on every bar. Seeded at frame 0
    // so nothing is drawn yet — the entry and exit lines appear as the playhead reaches them.
    addOverlay({
      id: REPLAY_ANNOTATION_OVERLAY_ID,
      type: 'replayannotations',
      label: 'DynaMan annotations',
      timeline,
      dates: barData?.dates || [],
      upToIndex: 0,
      trendline: null,
    });
    setReplayTimeline(timeline);
    setStatusText(`DynaMan replay: ${timeline.frames.length} bars loaded - press play. `
      + 'SPACE plays, the arrows step, B/S/X trade the discretionary book.');
  // setStatusText is deliberately absent, as it is from every sibling handler here: a stable
  // setter declared much later in this component, and naming it in a dependency array reaches it
  // before its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [barData, extendedBarData, addOverlay, removeOverlay]);

  const handleCloseReplay = useCallback(() => {
    setReplayTimeline(null);
    // THE SESSION GOES WITH IT. A session left behind would bound the next run started from the
    // research dialog to a window chosen for a different one -- and the dialog's path has no
    // window at all, which is the behaviour every earlier figure was produced under.
    setReplaySession(null);
    removeOverlay(REPLAY_PLAYHEAD_OVERLAY_ID);
    removeOverlay(REPLAY_ANNOTATION_OVERLAY_ID);
    removeOverlay(TIME_BAND_OVERLAY_ID);
    // EVERY id the replay can produce, or the decoration outlives the feature. The two added for
    // the copilot are the easiest to forget precisely because they are silent and empty most of
    // the time -- a leftover hollow triangle over a different symbol's chart would be a fill
    // nobody took.
    removeOverlay(CTS_PILLAR_OVERLAY_ID);
    removeOverlay(LEDGER_MARKER_OVERLAY_ID);
    removeOverlay(FAN_OVERLAY_ID);
  }, [removeOverlay]);

  /**
   * CTS TIMING POINTS FOR THE REPLAY'S PILLARS, taken from the scan already on the chart.
   *
   * <p>Not loaded again. The Critical Timing Scan has already read the published dataset, applied
   * the 4-bar fold and aligned every date onto the EXTENDED axis — so reusing its points is the
   * only way the pillars and the violet triangles beside them can agree about which days are
   * timing points. A second read would be a second alignment of one calendar.
   *
   * <p>`title` carries the day the scan fired, which for a mark snapped off a weekend is not the
   * bar it is drawn on. That is what lets `ctsPillars` apply the non-trading-day convention.
   */
  const replayCtsPoints = useMemo(() => {
    const scan = (overlays || []).find((o) => o && o.id === GANN_CONFLUENCE_OVERLAY_ID);
    const points = Array.isArray(scan?.points) ? scan.points : [];
    if (points.length === 0) return null;
    // `up` comes from `position`, the direction channel the whole product reads
    // (`'below'` means the mark sits under the bar, pointing up). Carried rather than re-derived
    // so the replay's pillar marks and the scan's own triangles cannot disagree about which way
    // a timing point reads -- they are the same points.
    return points.map((p) => ({
      date: p.date, price: p.price, firedOn: p.title, up: p.position === 'below',
    }));
  }, [overlays]);

  /**
   * THOSE SAME POINTS AS BAR INDICES, for the simulate-mode click.
   *
   * <p>Derived from the SCAN THAT IS ON THE CHART rather than fetched again, so the bar the
   * playhead stops at is the bar carrying the pillar the reader can see. A second read of the
   * dataset would be a second answer to "which days are timing points" — the chart folds
   * clustered points with `reversalBars`, and an unfolded list would stop the playhead on days
   * nothing is drawn on.
   *
   * <p>`p.date` is already the bar the mark is DRAWN on — `reversalBars` has snapped a weekend
   * point forward — so this is a plain lookup rather than a second alignment. A date that is not
   * on the axis at all yields -1 and is dropped: a point past the last bar is not somewhere the
   * playhead can go.
   *
   * <p>ASCENDING, because the chart's own ordering is not a guarantee this cares to inherit, and
   * `nextCtsIndex` takes the minimum anyway. Sorting here keeps the two honest about each other.
   */
  const simulateCtsIndices = useMemo(() => {
    const dates = barData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) return [];
    const points = replayCtsPoints;
    if (!points || points.length === 0) return [];
    const at = new Map(dates.map((d, i) => [d, i]));
    const out = [];
    for (const p of points) {
      const i = at.get(p.date);
      if (Number.isFinite(i)) out.push(i);
    }
    return [...new Set(out)].sort((a, b) => a - b);
  }, [replayCtsPoints, barData]);

  useEffect(() => { simulateCtsIndicesRef.current = simulateCtsIndices; }, [simulateCtsIndices]);


  /**
   * LEAVE REPLAY WHEN THE CHART STOPS BEING THE ONE THE REPLAY IS ABOUT.
   *
   * <h2>The trap this closes</h2>
   *
   * A replay is a timeline of ONE symbol's bars, indexed against the dates loaded when it
   * started. Nothing used to clear it, so loading a new symbol left the HUD mounted and the
   * playhead indexing a date list that no longer existed — QQQ's replay drawn over AAPL's
   * chart, with a running ledger of trades that have nothing to do with what is on screen.
   *
   * <p>That is worse than having no exit. An exit that is merely hard to find leaves the reader
   * stuck; this left them looking at a confident, wrong picture. Reported as "no way out of
   * replay mode", and both halves were true: the only control was an unlabelled × at the end of
   * a crowded transport bar, and the obvious escape — load something else — did not work.
   *
   * <h2>Two triggers, because there are two ways to mean "I am done with this"</h2>
   *
   * Changing symbol, and reopening the configuration dialog. The second matters because the
   * dialog is where the next run is set up: leaving the previous replay on the chart underneath
   * it would mean the next "Replay on chart" drew over a stale one.
   *
   * <p>Guarded on `replayTimeline` being set, so this is a no-op on every ordinary symbol change
   * and cannot churn overlays for a reader who has never opened a replay.
   */
  useEffect(() => {
    if (!replayTimeline) return;
    handleCloseReplay();
    setStatusText('Replay closed — the chart changed symbol.');
    // handleCloseReplay is stable and setStatusText is declared much later in this component;
    // naming either would reach it before its declaration, which App.tdz.test.js exists to catch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  useEffect(() => {
    if (!dynaManSimulatorOpen || !replayTimeline) return;
    handleCloseReplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynaManSimulatorOpen]);

  const handleShowDynaManTrades = useCallback((result) => {
    removeOverlay(DYNAMAN_TRADES_OVERLAY_ID);
    if (!result) return;
    const overlay = buildDynaManTradeOverlay(result, barData);
    if (overlay.drawnCount > 0) addOverlay(overlay);
    // Stated even when nothing was drawn. A run whose trades all fall outside the loaded date
    // range changes nothing on screen, which is indistinguishable from a broken button.
    setStatusText(dynaManOverlayStatus(overlay));
  // setStatusText is deliberately absent, as it is from every sibling handler here: a stable
  // setter declared much later in this component, and naming it in a dependency array reaches it
  // before its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [barData, addOverlay, removeOverlay]);

  /**
   * ONE-CLICK ENGINE EXECUTION: run the provider and paint it, with no dialog in between.
   *
   * <h2>It reuses the dialog's own parameters, and that is the point</h2>
   *
   * `strategyFor(provider)` is the same call every path in the simulator makes, so a chart drawn
   * by this button is the chart the research dialog would draw with nothing touched. If it built
   * its own parameter set, the two would diverge the first time either moved -- and a reader
   * comparing a painted chart against a published figure would be comparing two different runs.
   * This dialog has shipped that defect twice.
   *
   * <p>`timeframes` is sent for the same reason: omitting it replays every stored series rather
   * than the daily bars the chart is drawn from, which is exactly the discrepancy that made the
   * autorun report 160 trades against the audited 159.
   *
   * <p>The painting goes through {@link handleShowDynaManTrades}, unchanged -- so the icon and
   * the dialog's "show on chart" button produce the same overlay by construction.
   */
  const [engineRunning, setEngineRunning] = useState('');
  const handleRunEngine = useCallback(async (provider) => {
    if (!provider || engineRunning) return;
    if (!barData?.dates?.length) {
      setStatusText('Load a symbol before running an engine.');
      return;
    }
    setEngineRunning(provider);
    setStatusText(`Running ${provider} on ${selectedSymbol}…`);
    try {
      const result = await runFullSimulation(api, {
        ...strategyFor(provider),
        signalProvider: provider,
        disableStructuralEntry: true,
        timeframes: REPLAY_TIMEFRAMES,
        symbol: selectedSymbol,
        // The one-click engine icons run on an uploaded series too. Null for a stored symbol, and
        // the helper decides GET or POST from it -- so a reader who has loaded a CSV gets the same
        // one-click behaviour rather than a run on a ticker that does not exist.
        customSeries,
      });
      handleShowDynaManTrades(result);
    } catch (e) {
      // NAMED, not swallowed. A one-click button that silently does nothing is worse than one
      // that reports a refusal -- the reader cannot tell a failure from a run with no trades.
      setStatusText(`${provider} failed: ${e?.response?.data?.error || e.message || 'unknown'}`);
    } finally {
      setEngineRunning('');
    }
    // setStatusText is deliberately absent, as it is from every sibling handler here: it is a
    // stable setter declared much later, and naming it reaches it before its declaration - the
    // temporal dead zone App.tdz.test.js exists to catch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, barData, selectedSymbol, engineRunning, handleShowDynaManTrades]);

  const handleAnalysisError = useCallback((message) => {
    setStatusText(`Integral Delta: ${message}`);
  }, []);

  /* ── the Daily Scan Viewer ──────────────────────────────────────────────────────────────────
     Opening a stored scan and jumping to one of its symbols. The signals travel with the request
     rather than being re-fetched: the dialog has already read the file, and asking for it twice
     would make the chart's marks depend on a second round trip that can fail on its own. */
  const [scanViewerOpen, setScanViewerOpen] = useState(false);


  const handleScanLoadSymbol = useCallback((symbol, signals, scanDate) => {
    if (!symbol) return;
    setPendingScan({ symbol, signals: signals || [], scanDate });
    setSelectedSymbol(symbol);
    setStatusText(`Loading ${symbol} from the ${scanDate} scan…`);
  // setStatusText and setSelectedSymbol are stable setters declared elsewhere in this component;
  // naming them in a dependency array is the temporal dead zone App.tdz.test.js exists to catch.
  }, []);

  /**
   * Paint the scan's triggers once the symbol's bars have actually arrived.
   *
   * <p>Deferred to an effect rather than done in the click handler, and that ordering is
   * load-bearing: changing the symbol fires the effect that calls `clearOverlays()`, so an overlay
   * added synchronously alongside `setSelectedSymbol` would be wiped a moment later by the clear it
   * had just triggered. Waiting for the bars to be the new symbol's also means the circles are
   * placed against the right x axis rather than the previous chart's dates.
   *
   * <p>Built against `extendedBarData`, NOT `barData`. The chart renders the extended series — 250
   * padded weekday columns past the last bar — and a pending trigger is usually dated into exactly
   * that region. Building against the raw series made every future trigger fail the column lookup
   * and vanish, which is the one signal a desk opened the chart to see.
   *
   * <p>One-shot: `pendingScan` is cleared when it is drawn, so an ordinary redraw does not keep
   * re-adding a scan the user has since moved on from.
   */
  useEffect(() => {
    if (!pendingScan || selectedSymbol !== pendingScan.symbol) return;
    const rendered = extendedBarData?.dates?.length ? extendedBarData : barData;
    if (!rendered?.dates?.length) return;
    removeOverlay(PENDING_SIGNALS_OVERLAY_ID);
    // The last TRADED bar, so a projection column can be told from a real one. Taken from the
    // unpadded series: extendBarData appends placeholder columns, and without this the two are
    // indistinguishable by date alone.
    const lastRealDate = barData?.dates?.[barData.dates.length - 1];
    const overlay = buildPendingSignalsOverlay(pendingScan.signals, rendered, { lastRealDate });
    if (overlay.drawnCount > 0) addOverlay(overlay);
    setStatusText(pendingOverlayStatus(pendingScan.symbol, overlay));
    setPendingScan(null);
  }, [pendingScan, selectedSymbol, barData, extendedBarData, addOverlay, removeOverlay]);
  // ── OPERATOR FEATURES state ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState('');
  const operatorFeatures = operatorFeaturesEnabled();

  /*
   * ── THE PRODUCT TARGET AN ADMINISTRATOR IS INSPECTING ──────────────────────────────────────
   *
   * ⚠ DECLARED HERE, AFTER `setToast`, AND THAT IS NOT WHERE IT READS BEST. Its natural home is
   * beside `toolbarProfile`, which it mirrors — but the change handler raises a toast, and a
   * `useCallback` DEPENDENCY ARRAY IS EVALUATED DURING RENDER. Named up there, `setToast` throws
   * `Cannot access before initialization` on mount, with lint clean and the build compiling. This
   * file has paid for that class four times; the fifth is not going to be this one.
   *
   * ⚠ THE STORED VALUE IS STATE; THE EFFECTIVE VALUE IS DERIVED. `productOverride` holds what is
   * in storage and `productTarget` applies the role test on every render, so a demoted
   * administrator loses the override on the render their role changes — no effect to fire and
   * nothing to invalidate. Deriving it also means the role test cannot be skipped by a caller
   * reading the state directly.
   */
  // `true` reads STORAGE rather than asserting a role: this state is the stored value, and the
  // line below is the only place the role is tested. Never pass `productOverride` anywhere —
  // `productTarget` is the gated one, and it is what goes to the toolbar and the command gate.
  const [productOverride, setProductOverride] = useState(() => readProductOverride(true));
  const productTarget = isAdmin ? productOverride : ACTIVE_PRODUCT;
  const handleProductTargetChange = useCallback((next) => {
    setProductOverride(next);
    writeProductOverride(next);
    // The confirmation the brief asks for, and it NAMES BOTH VALUES when they differ: "switched to
    // astro" alone leaves an administrator unsure whether they changed the build or their view.
    setToast(next === ACTIVE_PRODUCT
      ? `Product target: ${next} — back to what this build ships.`
      : `Inspecting ${next}. This build ships ${ACTIVE_PRODUCT} — nobody else sees this.`);
  }, []);

  /**
   * THE SIMULATOR DOES NOT OPEN BY ITSELF.
   *
   * <p>It used to: an admin booting the app got the dialog on screen with a full PTA run already
   * under way. That was asked for while the audit WAS the task, and it is a poor default -- the
   * first thing anyone sees on loading a charting app should be the chart, and a simulation is an
   * unbounded read across a symbol's whole history that holds a thread on a single-instance
   * backend.
   *
   * <p>Nothing replaces it, deliberately. The toolbar button is the trigger, and a dialog opened
   * from the toolbar has never started a run by itself -- a reader who clicked it may want to
   * change the symbol or the provider first. So the boot path is gone along with the prop that
   * carried it, rather than left behind as a flag with one possible value.
   */

  /**
   * The lifecycle ribbon, redrawn whenever the TrendPulse curve or the bars change.
   *
   * <p>Derived rather than triggered by a button: the ribbon is a property of the curve, so a
   * control to "turn it on" would only be a control to forget. It disappears with the curve, and
   * with the feature flag.
   */
  useEffect(() => {
    if (!operatorFeatures || !barData?.dates?.length || !trendPulseSeriesForPatterns) {
      removeOverlay(PHASE_RIBBON_OVERLAY_ID);
      return;
    }
    const values = alignSeriesToBars(trendPulseSeriesForPatterns, barData.dates);
    if (!values.some(v => Number.isFinite(v))) {
      removeOverlay(PHASE_RIBBON_OVERLAY_ID);
      return;
    }
    removeOverlay(PHASE_RIBBON_OVERLAY_ID);
    addOverlay(buildPhaseRibbonOverlay(values, barData));
  }, [operatorFeatures, barData, trendPulseSeriesForPatterns, addOverlay, removeOverlay]);

  /**
   * Score every boundary shelf on the curve and draw a vertical line at each resolved one.
   *
   * <p>Previous lines are cleared first: this is a verdict on the whole chart, and two runs at
   * different thresholds overlaid would be two verdicts with no way to tell which line came from
   * which.
   */
  const handleEvaluateValidationLines = useCallback(() => {
    const dates = barData?.dates;
    const curve = trendPulseSeriesForPatterns;
    if (!Array.isArray(dates) || !dates.length || !curve) {
      setStatusText('Validation lines: run TrendPulse first — the shelves are on its curve.');
      return;
    }
    const values = alignSeriesToBars(curve, dates);
    if (!values.some(v => Number.isFinite(v))) {
      setStatusText('Validation lines: the TrendPulse curve does not cover these bars.');
      return;
    }
    (overlays || [])
      .filter(o => o && typeof o.id === 'string' && o.id.startsWith(VALIDATION_LINE_PREFIX))
      .forEach(o => removeOverlay(o.id));

    const evaluated = evaluateBoundaryShelves(values, barData.ohlc);
    const lines = buildValidationLineOverlays(evaluated, barData);
    lines.forEach(addOverlay);
    const s = summariseValidation(evaluated);
    if (!s.total) {
      setStatusText('Validation lines: no boundary shelves on this chart — the shelf must be flat '
        + 'within 0.75% of its 25-bar range, flanked by 4% moves, AND at the matching corridor '
        + 'edge.');
      return;
    }
    setStatusText(`Validation lines: ${s.drawn} drawn from ${s.total} shelves — `
      + `long ${s.longSuccess}✓/${s.longFailure}✗, short ${s.shortSuccess}✓/${s.shortFailure}✗`
      + `${s.successRate === null ? '' : `, ${s.successRate.toFixed(1)}% success`}`
      + `${s.unresolved ? `; ${s.unresolved} unresolved and undrawn` : ''}.`);
  // setStatusText is deliberately absent, as it is from every sibling handler here: it is a stable
  // setter declared much later in this component, and naming it in a dependency array reaches it
  // before its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [barData, trendPulseSeriesForPatterns, overlays, addOverlay, removeOverlay]);

  const handleCopyChart = useCallback(async () => {
    const chart = eChartRef.current?.getEchartsInstance?.();
    const result = await copyChartToClipboard(chart);
    setToast(result.ok ? 'Chart copied to clipboard!' : result.reason);
  }, []);

  const handleSaveScreenshot = useCallback(() => {
    const chart = eChartRef.current?.getEchartsInstance?.();
    const dates = barData?.dates;
    const view = readZoomWindow(chart, dates);
    if (!chart || !view) {
      setToast('Nothing to capture yet.');
      return;
    }
    const result = saveChartScreenshot(chart, {
      symbol: barData?.symbol || selectedSymbol,
      startDate: dates[view.startValue],
      endDate: dates[view.endValue],
    });
    setToast(result.ok ? `Saved ${result.name}` : result.reason);
  }, [barData, selectedSymbol]);
  // ── end OPERATOR FEATURES state ───────────────────────────────────────────────────────────────

  /**
   * Screenshot the chart a screenful at a time, walking forward to the end of the data.
   *
   * <p>The window width is whatever is on screen when the button is pressed, so the caller decides
   * how much history each image covers by zooming to it first. Each subsequent window starts on the
   * bar immediately after the previous one ended, which is what makes the set tile the history
   * rather than sample it.
   *
   * <p>Where the files land is the browser's decision, not this code's: a page cannot write to an
   * absolute path. `openSweepTarget` asks for a folder if the browser can (choose
   * C:\stockdata\ClaudeScreenShots there) and falls back to ordinary downloads if it cannot, and
   * the status line says which happened — "saved 14 images" means two different things and only one
   * of them ends with the files where they were asked for.
   */
  const handleSweepScreenshots = useCallback(async () => {
    const chart = eChartRef.current?.getEchartsInstance?.();
    const dates = barData?.dates;
    if (!chart || !Array.isArray(dates) || dates.length === 0) {
      setStatusText('Screenshot sweep: no chart to capture.');
      return;
    }
    const view = readZoomWindow(chart, dates);
    if (!view) {
      setStatusText('Screenshot sweep: could not read the current view.');
      return;
    }
    // Real bars only. The padded future bars carry no price and would be filed under dates that
    // have not happened.
    const lastReal = Math.max(0, (barData.realBarCount || dates.length) - 1);
    const width = view.endValue - view.startValue + 1;
    const windows = planSweep(dates, width, { to: lastReal });
    if (!windows.length) {
      setStatusText('Screenshot sweep: nothing to capture.');
      return;
    }

    setSweepRunning(true);
    setStatusText(`Screenshot sweep: ${windows.length} images of ${width} bars each — `
      + `choose ${TARGET_DIRECTORY} when asked, or they go to Downloads.`);
    try {
      const target = await openSweepTarget();
      const result = await runSweep({
        chart,
        dates,
        windows,
        target,
        onProgress: (i, n, w) => setStatusText(
          `Screenshot sweep: ${i + 1} of ${n} — ${w.fromDate} to ${w.toDate}`),
      });
      setStatusText(`Screenshot sweep: saved ${result.saved} of ${windows.length} to `
        + `${result.label}`
        + `${result.failed ? ` (${result.failed} failed)` : ''}`
        + `${result.mode === 'download'
          ? ` — move them to ${TARGET_DIRECTORY} if that is where you want them.` : '.'}`);
    } catch (err) {
      console.error('[App.js] screenshot sweep failed:', err);
      setStatusText(`Screenshot sweep failed: ${err.message}`);
    } finally {
      setSweepRunning(false);
    }
  // setStatusText is deliberately absent, as it is from every sibling handler here: it is a
  // stable setter declared much later in this component, and naming it in a dependency array
  // reaches it before its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [barData]);

  /**
   * Draws the entry arrows on the price bars and their stall segments on the TrendPulse pane.
   *
   * <p>Replaces rather than accumulates: the overlay is one answer about this chart, and two runs
   * drawn at once would be two answers in one picture with no way to tell which arrow came from
   * which pair of thresholds.
   */
  const handleApplyPatternPrediction = useCallback((entries, curve) => {
    removeOverlay(ENTRY_OVERLAY_ID);
    const overlay = buildEntryOverlay(entries, curve, barData);
    if (!overlay.points.length) {
      setStatusText('TrendPulse entries: nothing to draw on this chart.');
      return;
    }
    addOverlay(overlay);
    const n = FORWARD_HORIZONS[0];
    const stats = summariseEntries(entries);
    setStatusText(`TrendPulse entries: ${stats.total} drawn `
      + `(${stats.bullish} bullish, ${stats.bearish} bearish)`
      + `${stats.perHorizon[n].successRate === null ? ''
        : ` — ${stats.perHorizon[n].successRate.toFixed(1)}% over ${n} bars`}.`);
  // setStatusText is deliberately absent, as it is from every sibling handler here: it is a
  // stable setter declared much later in this component, and naming it in a dependency array
  // reaches it before its declaration - the temporal dead zone App.tdz.test.js exists to catch.
  }, [barData, addOverlay, removeOverlay]);

  const runFibPivotPairs = useCallback(() => {
    const result = buildFibPivotStudy(barData, { isAdmin, extendedDates: extendedBarData?.dates });
    if (!result) {
      setStatusText('Fibonacci pivot pairs: no qualifying pairs found on this chart.');
      return;
    }
    // One call, so the dots and the base-span verticals form a single undo unit — undoing a study
    // halfway, leaving its lines behind without the dots, would be worse than not undoing at all.
    addOverlay([result.overlay, ...(result.lines || [])]);
    const { pairsFound, pairsUsed, pivotCount, truncated, lineCount, baseSpan, projectionCount, nextTargetBars } = result.summary;
    setStatusText(`Fibonacci pivot pairs: ${pairsFound} pair(s) found`
      + `${truncated ? `, strongest ${pairsUsed} drawn` : ''}, ${pivotCount} pivots marked`
      + `${lineCount ? `, ${lineCount} base-span lines (span ${baseSpan} bars)` : ''}`
      + `${projectionCount ? `, ${projectionCount} projected target(s), next in ${nextTargetBars} bars` : ''}.`);
  }, [barData, extendedBarData, isAdmin, addOverlay]);

  // Toggle rather than add: the projections answer one question about the current state of the
  // chart, so a second set stacked on the first would be the same answer drawn twice. Removing by
  // id prefix is what makes the button idempotent no matter what else has been drawn since.
  const runRecentFibProjections = useCallback(() => {
    if (hasRecentFibOverlays(overlays)) {
      setAllOverlays(withoutRecentFibOverlays(overlays));
      setStatusText('Recent-pivot Fibonacci projections removed.');
      return;
    }
    const result = buildRecentPivotProjection(barData, {
      isAdmin,
      extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      setStatusText('Recent Fibs: need at least two 5-bar pivots on this chart.');
      return;
    }
    // One call, so the anchors, targets, hit dots and guide form a single undo unit.
    addOverlay(result.overlays);
    const { distance, targetCount, futureCount, hitCount, nextTargetBars, p1Date } = result.summary;
    setStatusText(`Recent Fibs from ${p1Date} (interval ${distance} bars): ${targetCount} target(s), `
      + `${futureCount} in the future${nextTargetBars !== null ? `, next in ${nextTargetBars} bars` : ''}`
      + `${hitCount ? `, ${hitCount} already hit` : ''}.`);
  }, [overlays, barData, extendedBarData, isAdmin, addOverlay, setAllOverlays]);

  // Wrapped so the busy indicator is on screen before the scan takes the thread.
  const handleRunPivotAdditionSeries = useCallback(
    () => runWithBusyIndicator('Scanning pivots for an addition series', runAdditionSeries),
    [runWithBusyIndicator, runAdditionSeries]);
  const handleRunFibPivotPairs = useCallback(
    () => runWithBusyIndicator('Scanning pivot pairs for Fibonacci ratios', runFibPivotPairs),
    [runWithBusyIndicator, runFibPivotPairs]);
  const handleToggleRecentFibProjections = useCallback(
    () => runWithBusyIndicator('Projecting from the two most recent pivots', runRecentFibProjections),
    [runWithBusyIndicator, runRecentFibProjections]);

  /*
   * ── SK360 AND TIME PROJECTION ───────────────────────────────────────────────────────────────
   *
   * Declared HERE, immediately after `runRecentFibProjections`, because that callback already
   * closes over every value these two need — `overlays`, `barData`, `extendedBarData`,
   * `lastClickedBar`, `addOverlay`, `setAllOverlays`, `setStatusText`. A hook dependency array is
   * evaluated during RENDER, so a position above any one of those declarations throws
   * "Cannot access '<name>' before initialization" on mount, with lint clean and the build
   * compiling. This file has paid for that twice; sitting beside a callback with the same closure
   * is how to not need the guard.
   *
   * Both TOGGLE: a second press removes the figure rather than stacking a second copy of it, and
   * both re-anchor to whatever bar is clicked when they are next switched on.
   */

  const runSk360 = useCallback(() => {
    if (hasSk360Overlays(overlays)) {
      setAllOverlays(withoutSk360Overlays(overlays));
      setStatusText('SK360 projection removed.');
      return;
    }
    const picked = Number.isInteger(lastClickedBar?.index) ? lastClickedBar.index : undefined;
    const result = buildSk360(barData, {
      isAdmin, anchorIndex: picked, extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      // NAMED, not silent. The ways this returns nothing are different — no series at all, or an
      // anchor whose close has no square root to turn — and a reader can act on the second by
      // clicking a different bar.
      setStatusText('SK360: no projection. Click a bar whose close is above zero — a price of '
        + 'zero has no root to turn, and this store holds all-zero rows.');
      return;
    }
    // ONE call, so both segments are a single undo unit. Added separately, X1 would take the
    // upper line and leave the lower one — half a projection, which reads as a rendering fault.
    addOverlay(result.overlays);
    const { anchorDate, anchorClose, root, upper, lower, lowerClamped, targetDate,
      truncated, shortBy, usedFallbackAnchor } = result.summary;
    const money = (v) => (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2));
    setStatusText(`SK360 from ${anchorDate} at ${money(anchorClose)} (root ${root.toFixed(3)})`
      + `${usedFallbackAnchor ? ' — the last bar; click a bar to anchor it elsewhere' : ''}: `
      + `+360° ${money(upper)}, −360° ${money(lower)}`
      + `${lowerClamped ? ' (clamped at zero — the root is under 2)' : ''}`
      + ` by ${targetDate}`
      + `${truncated
        ? `. The chart ends ${shortBy} column${shortBy === 1 ? '' : 's'} short of that date, so the `
          + 'lines are drawn at their true angle and cut off at the edge.'
        : '.'}`);
  }, [overlays, barData, extendedBarData, isAdmin, addOverlay, setAllOverlays, lastClickedBar]);

  const handleToggleSk360 = useCallback(
    () => runWithBusyIndicator('Projecting one turn of the wheel', runSk360),
    [runWithBusyIndicator, runSk360]);

  /**
   * Drops every reference to a bar the reader had picked.
   *
   * <h2>⚠ WHAT A "SELECTED BAR" ACTUALLY IS HERE — checked, not assumed</h2>
   *
   * Three things, and only three. {@code lastClickedBar} is the single anchor every geometry tool
   * reads; {@code drawingFirstClick} and {@code drawingPoints} are the in-progress clicks of the
   * two- and three-click drawing tools, which are the only place this application holds an ARRAY
   * of picked bars.
   *
   * <p>⚠ AND THERE IS NO CANDLE HIGHLIGHT TO CLEAR. The brief asks for pin markers and selection
   * highlighting to be removed; a sweep for one found nothing — `lastClickedBar` is pure state,
   * read by dialogs and tools, and no series, markPoint or `dispatchAction('highlight')` is keyed
   * to it. So that requirement is satisfied by there being nothing to satisfy it, which is worth
   * stating rather than quietly reporting as done.
   *
   * <p>The drawing MODE is deliberately left alone. Clearing the points means the next click
   * starts a fresh figure; cancelling the tool as well would take a pen out of the reader's hand
   * because they ran an unrelated study.
   */
  const clearBarSelection = useCallback(() => {
    setLastClickedBar(null);
    drawingFirstClick.current = null;
    drawingPoints.current = [];
  }, []);

  const runTimeProjection = useCallback(() => {
    if (hasTimeProjectionOverlays(overlays)) {
      setAllOverlays(withoutTimeProjectionOverlays(overlays));
      setStatusText('Time Projection markers removed.');
      return;
    }
    const picked = Number.isInteger(lastClickedBar?.index) ? lastClickedBar.index : undefined;
    const result = buildTimeProjection(barData, {
      isAdmin, anchorIndex: picked, extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      setStatusText('Time Projection: no offsets. The close has no readable digits — a price of '
        + 'zero, or one small enough that the browser prints it in exponent form.');
      return;
    }
    const { anchorDate, anchorClose, digits, offsets, markerCount,
      beyondCount, noOffsetCount, snappedCount } = result.summary;
    if (markerCount === 0) {
      // A summary with no overlays. Reported rather than treated as a failure, because "all three
      // land past the end of the chart" is a finding about the offsets and the chart's own forward
      // limit — not a tool that did not work.
      setStatusText(`Time Projection from ${anchorDate} at ${anchorClose}: `
        + `${offsets.map(o => `${o.name} +${o.days}d`).join(', ')} — none is on the chart`
        + `${beyondCount ? `, ${beyondCount} past its last column` : ''}`
        + `${noOffsetCount ? `, ${noOffsetCount} resolved to zero days` : ''}.`);
      return;
    }
    addOverlay(result.overlays);
    clearBarSelection();
    const drawn = offsets.filter(o => o.drawn);
    setStatusText(`Time Projection from ${anchorDate} at ${anchorClose} (digits "${digits}"): `
      + `${drawn.map(o => `${o.name} +${o.days}d → ${o.column}`).join(', ')}`
      + `${markerCount !== drawn.length
        ? ` on ${markerCount} column${markerCount === 1 ? '' : 's'}` : ''}`
      + `${snappedCount ? `, ${snappedCount} snapped forward off a non-trading day` : ''}`
      + `${beyondCount ? `, ${beyondCount} beyond the chart` : ''}`
      + `${noOffsetCount ? `, ${noOffsetCount} resolved to zero days` : ''}.`);
  }, [overlays, barData, extendedBarData, isAdmin, addOverlay, setAllOverlays, lastClickedBar,
    clearBarSelection]);

  const handleToggleTimeProjection = useCallback(
    () => runWithBusyIndicator('Projecting price as days', runTimeProjection),
    [runWithBusyIndicator, runTimeProjection]);

  /*
   * ── SK-GRID ─────────────────────────────────────────────────────────────────────────────────
   *
   * Beside its two siblings for the same TDZ reason stated above them: every value this closes
   * over is already declared by the time `runTimeProjection` is.
   *
   * ⚠ IT REFUSES AN ABSENT ANCHOR rather than falling back to the last bar, which is what the
   * other two do. The panel disables the button until a bar is clicked, and this is the other
   * half of that — a fallback behind a disabled button would be a second answer to "what is this
   * anchored to" that nobody could see.
   */
  const runSkGrid = useCallback(() => {
    if (hasSkGridOverlays(overlays)) {
      setAllOverlays(withoutSkGridOverlays(overlays));
      setStatusText('SK-Grid removed.');
      return;
    }
    const picked = Number.isInteger(lastClickedBar?.index) ? lastClickedBar.index : null;
    if (picked === null) {
      setStatusText('SK-Grid: click a candle first. The ladder is measured from one bar’s '
        + 'close, so there is nothing to anchor it to yet.');
      return;
    }
    const result = buildSkGrid(barData, {
      isAdmin, anchorIndex: picked, extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      // NAMED, not silent. The ways this returns nothing are distinguishable by the reader: a
      // close of zero has no root to step from, and this store holds all-zero rows.
      setStatusText('SK-Grid: no ladder. Click a bar whose close is above zero — a price of '
        + 'zero has no square root to step from, and this store holds all-zero rows.');
      return;
    }
    // ONE call, so the eight rungs are a single undo unit. Added separately, X1 would take one
    // rung and leave seven — which reads as a rendering fault rather than as a partial undo.
    addOverlay(result.overlays);
    const { anchorDate, anchorClose, root, levels, endDate, truncated, shortBy,
      spanLowPct, spanHighPct } = result.summary;
    const money = (v) => (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2));
    setStatusText(`SK-Grid from ${anchorDate} (close ${money(anchorClose)}, √ ${root.toFixed(3)}): `
      + `${levels.length} levels ${money(levels[0].price)}–${money(levels[levels.length - 1].price)} `
      + `(+${spanLowPct.toFixed(1)}% to +${spanHighPct.toFixed(1)}%), through ${endDate}`
      + `${truncated ? ` — the axis ends ${shortBy} column${shortBy === 1 ? '' : 's'} short of a `
        + 'full year, so the rungs stop there' : ''}.`);
  }, [overlays, barData, extendedBarData, isAdmin, addOverlay, setAllOverlays, lastClickedBar]);

  const handleToggleSkGrid = useCallback(
    () => runWithBusyIndicator('Building the SK-Grid price ladder', runSkGrid),
    [runWithBusyIndicator, runSkGrid]);

  // Use stable barData.ohlc for RSI/MACD (not study-truncated) to avoid recomputation on every advance
  const stableOhlc = barData?.ohlc;
  const rsiData = useMemo(() => computeRSI(stableOhlc), [stableOhlc]);
  const macdData = useMemo(() => calculateMACD(stableOhlc), [stableOhlc]);

  const handleTimeAndEventStrategyRun = useCallback(async (config) => {
    console.log('Time and Event Strategy RUN:', config);
    let intervalId;

    try {
      /*
       * THE DIALOG NOW EMITS ITS CONFIG, not a half-built payload.
       *
       * This payload used to be assembled in two places -- the study half in the dialog's own
       * switch, the symbol and date range here -- which is exactly why a headless run was
       * impossible without reimplementing one of them. The builder assembles the whole thing,
       * so the button and `strategy.run` cannot diverge.
       *
       * The emit contract could be changed outright because this handler is its ONLY consumer;
       * a dual shape with a sentinel was written first and thrown away, since two accepted
       * shapes is the drift this change exists to remove.
       */
      const fullPayload = buildTimeAndEventPayload(config, { barData, extendedBarData });
      if (!fullPayload) {
        setStatusText(`Time and Event Strategy: unknown study type "${config?.studyType}".`);
        return;
      }

      console.log('Full payload for Time and Event Strategy:', JSON.stringify(fullPayload, null, 2));

      const initialResponse = await api.post('/api/run-time-event-strategy', fullPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const { taskId } = initialResponse.data;

      const pollResult = () => {
        api.get(`/api/run-time-event-strategy/result/${taskId}`, { headers: { 'Authorization': `Bearer ${token}` } })
          .then((response) => {
            if (response.status === 200) {
              clearInterval(intervalId);
              const result = response.data;
              console.log('[App.js] Backend response from /api/run-time-event-strategy:', result);

              const datePairs = Array.isArray(result.datePairs) ? result.datePairs : [];
              const summaryLines = Array.isArray(result.summaryLines) ? result.summaryLines : [];
              const futureDates = Array.isArray(result.futureDates) ? result.futureDates : [];

              // Bodies arriving at an eclipse's own degree. Point events, so they are markers
              // rather than bands, and ADDITIVE - the eclipse study's own windows are still drawn
              // from datePairs below, unchanged, so turning the extension on adds to the chart
              // rather than replacing what was there.
              const eclipseTransits = Array.isArray(result.eclipseTransits)
                ? result.eclipseTransits : [];
              removeOverlay(ECLIPSE_TRANSIT_OVERLAY_ID);
              if (eclipseTransits.length > 0) {
                const source = extendedBarData?.dates?.length ? extendedBarData : barData;
                const built = buildEclipseTransitPoints(eclipseTransits, source);
                if (built.points.length > 0) {
                  addOverlay({
                    type: 'pivotMarkers',
                    id: ECLIPSE_TRANSIT_OVERLAY_ID,
                    label: 'Eclipse degree transits',
                    // The per-point symbol is what tells solar from lunar; this is only the
                    // fallback for a point that somehow carries none.
                    symbol: 'circle',
                    symbolSize: 10,
                    labelFontSize: 9,
                    points: built.points,
                  });
                }
                handleStudyReportJournal({
                  studyLabel: `Eclipse degree transits — ${selectedSymbol}`,
                  reportText: eclipseTransitJournalLines(eclipseTransits).join('\n'),
                });
                console.log(`[App] eclipse transits: ${built.points.length} bars marked`
                  + ` (${built.solar} solar, ${built.lunar} lunar)`
                  + `${built.offChart > 0 ? `, ${built.offChart} outside the chart` : ''}`);
              }

              addAreaHighlightOverlaysAndMessages({
        isAdmin,
                datePairs,
                summaryLines,
                futureDates,
                params: { color: result.color || 'rgba(33, 150, 243, 0.5)', label: 'Time and Event Strategy' },
                addOverlay,
                barData,
                extendedBarData,
                updateMessagesPanel: ({ summaryLines, futureDates }) => {
                  const tabContents = formatMessagesPanelTabs(summaryLines, futureDates);
                  setStudyTitle('Time and Event Strategy');
                  setMessagesTabContents(tabContents);
                  setMessagesOpen(true);
                },
                onJournalAppend: handleJournalAppend,
              });

              setTimeAndEventStrategyDialogOpen(false);
            }
          })
          .catch((err) => {
            // If the error is a 401, the auth interceptor will handle it.
            // We just need to stop polling.
            if (err.response && err.response.status === 401) {
              console.log('Polling stopped due to session expiration.');
            } else {
              // For other errors, show an alert.
              console.error('[App.js] Polling error:', err);
              alert(`Failed to get result for task ${taskId}: ${err.message}`);
            }
            clearInterval(intervalId);
          });
      };

      intervalId = setInterval(pollResult, 2000);
    } catch (err) {
      console.error('[App.js] RUN error from /api/run-time-event-strategy:', err);
      const errorMessage = err.response?.data?.error || err.message;
      alert(`Failed to run time and event strategy: ${errorMessage}`);
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  }, [api, token, isAdmin, addOverlay, removeOverlay, barData, extendedBarData, setStudyTitle, setMessagesTabContents, setMessagesOpen, setTimeAndEventStrategyDialogOpen, handleJournalAppend, handleStudyReportJournal, selectedSymbol]);

  const handleStrategy2DesignRun = useCallback(async ({ studies, color, threshold, token }) => {
    console.log('Strategy 2 RUN:', { studies, color, threshold });
    try {
      // Built by the SHARED builder since 2026-10-24. Note the rename it absorbs: the dialog
      // emits `color`/`threshold` and the endpoint wants `highlightColor`/`scoreThreshold`, and
      // that mapping now lives in exactly one place rather than here and in a headless path.
      const payload = buildStrategy2Payload(
        { studies, highlightColor: color, scoreThreshold: threshold },
        { barData, extendedBarData },
      );
      console.log('[App.js] Calling /api/run-strategy2 with payload:', payload);
      const response = await api.post('/api/run-strategy2', payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = response.data;
      console.log('[App.js] Backend response from /api/run-strategy2:', result);

      const datePairs = Array.isArray(result.datePairs) ? result.datePairs : [];
      const summaryLines = Array.isArray(result.summaryLines) ? result.summaryLines : [];
      let futureDates = Array.isArray(result.futureDates) ? result.futureDates : [];

      // Backend returns empty futureDates for Strategy 2; extract from datePairs where endDate is in the future
      if (futureDates.length === 0 && datePairs.length > 0) {
        const today = new Date().toISOString().slice(0, 10);
        futureDates = datePairs.filter(p => p.endDate && p.endDate >= today);
      }

      const formattedFutureDates = futureDates.map(pair => `${pair.startDate}-to-${pair.endDate}`);

      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs,
        summaryLines,
        futureDates: formattedFutureDates,
        params: { color, label: 'Strategy 2 Design' },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: ({ summaryLines, futureDates }) => {
          const tabContents = formatMessagesPanelTabs(summaryLines, futureDates);
          setStudyTitle('Strategy 2 Design');
          setMessagesTabContents(tabContents);
          setMessagesOpen(true);
        },
        onJournalAppend: handleJournalAppend,
      });
      setIsStrategy2DesignDialogOpen(false);
    } catch (err) {
      console.error('[App.js] RUN error from /api/run-strategy2:', err);
      const errorMessage = err.response?.data?.error || err.message;
      alert(`Failed to run strategy design study: ${errorMessage}`);
    }
  }, [api, isAdmin, barData, extendedBarData, addOverlay, setStudyTitle, setMessagesTabContents, setMessagesOpen, setIsStrategy2DesignDialogOpen, handleJournalAppend]);

  const handleAstroGraphExecute = useCallback(async (dialogState) => {
    if (!extendedBarData || !extendedBarData.dates || extendedBarData.dates.length === 0) {
      alert('Chart data not available. Please load a symbol first.');
      return false;
    }
    const startDate = extendedBarData.dates[0].substring(0, 10);
    const endDate   = extendedBarData.dates[extendedBarData.dates.length - 1].substring(0, 10);
    // Trading dates for AstroGraph computation (all chart dates including future extension)
    const priceDates  = (extendedBarData?.dates || []).map(d => d.substring(0, 10));
    // Price closes & dates for commentary engine (actual historical data only)
    const priceCloses = (barData?.ohlc || []).map(bar => bar[3]);
    const commentaryDates = (barData?.dates || []).map(d => d.substring(0, 10));

    const payload = {
      startDate,
      endDate,
      preset:         dialogState.preset,
      enabledPlanets: dialogState.planetEnabled,
      planetAspects:  dialogState.planetAspects,
      conjPolarity:   dialogState.conjPolarity,
      aspectPolarity: dialogState.aspectPolarity,
      planetWeights:  dialogState.planetWeights,
      aspectWeights:  dialogState.aspectWeights,
      planetDecl:     dialogState.planetDecl,
      legend:         dialogState.legend,
      longWeight:     dialogState.longWeight,
      mediumWeight:   dialogState.mediumWeight,
      priceCloses,
      priceDates,
      commentaryDates,
      toleranceDays:  dialogState.toleranceDays ?? 5,
    };
    if (Number.isFinite(dialogState.classicSmoothingDays)) {
      payload.classicSmoothingDays = dialogState.classicSmoothingDays;
    }
    try {
      console.log('[App.js] /api/astrograph payload:', payload);
      const response = await api.post('/api/astrograph', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { series, commentary, error: backendError } = response.data;
      if (backendError) {
        alert('TrendPulse backend error: ' + backendError);
        return false;
      }
      if (!Array.isArray(series) || series.length === 0) {
        alert('TrendPulse: no data returned from backend.');
        return false;
      }
      // Curves are KEPT, up to MAX_TRENDPULSE_OVERLAYS, so several smoothing presets can be
      // compared on one chart. This used to delete the previous curve before drawing, which meant
      // the study could answer "what does this preset look like" but never "which of these fits
      // this symbol better" — the question a smoothing parameter exists to be asked.
      trendPulseOverlaysToEvict(overlays).forEach(removeOverlay);
      addOverlay(buildTrendPulseOverlay({
        series,
        preset: dialogState.preset,
        overlays,
        color: dialogState.lineColor,
        thickness: dialogState.lineThickness,
        lineType: dialogState.lineType,
      }));
      // Update commentary in dialog (dialog stays open)
      setAstroGraphCommentary(commentary || '');
      return true;
    } catch (err) {
      console.error('[App.js] /api/astrograph error:', err);
      alert(`TrendPulse failed: ${err.response?.data?.error || err.message}`);
      return false;
    }
  }, [api, token, barData, extendedBarData, addOverlay, overlays, removeOverlay]);

  /**
   * Runs TrendPulse straight from the toolbar, on the Custom preset, with no dialog.
   *
   * <p>The configuration dialog is retained and still opens through onOpenAstroGraphDialog for
   * administrative use - this is a second entry point, not a replacement for its code.
   *
   * <p>The parameters come from trendPulseCustomRunConfig() rather than being written out here.
   * Two copies of a twelve-planet weight table and a seven-column aspect matrix would agree on the
   * day they were written and never again, and the disagreement would show as a curve that is
   * subtly wrong rather than as an error.
   */
  const handleRunTrendPulse = useCallback(async () => {
    const source = extendedBarData?.dates?.length ? extendedBarData : barData;
    const dates = (source?.dates || []).map(d => String(d).slice(0, 10));

    // The hosted dataset first. It covers 1801-2100 at the Custom preset, which is what this button
    // computes, so on the ordinary path there is nothing to compute at all.
    if (dates.length > 0) {
      setStatusText('TrendPulse: loading the precomputed series...');
      const rows = await loadTrendPulseDataset();
      if (rows) {
        const { values, matched, missing } = valuesForBars(rows, dates);
        // A handful of unmatched bars is a chart reaching outside 1801-2100, which is fine; almost
        // none matching means the dataset and the chart are not talking about the same thing, and
        // drawing a nearly empty curve would look like a quiet market rather than a broken join.
        if (matched >= Math.max(2, Math.floor(dates.length * 0.5))) {
          trendPulseOverlaysToEvict(overlays).forEach(removeOverlay);
          addOverlay(buildTrendPulseOverlay({
            series: dates.map((date, i) => ({ date, value: values[i] }))
              .filter(p => typeof p.value === 'number'),
            preset: 'custom',
            overlays,
            color: TRENDPULSE_LINE_DEFAULTS.lineColor,
            thickness: TRENDPULSE_LINE_DEFAULTS.lineThickness,
            lineType: TRENDPULSE_LINE_DEFAULTS.lineType,
          }));
          setStatusText(`TrendPulse: ${matched.toLocaleString()} bars from the precomputed `
            + `1801-2100 series${missing > 0 ? `, ${missing} outside it` : ''}.`);
          return;
        }
        console.warn(`[App] precomputed TrendPulse matched only ${matched}/${dates.length} bars`
          + ' — computing live instead');
      }
    }

    // The live study, unchanged. Reached when the dataset is unreachable, when the chart runs
    // outside 1801-2100, or when a symbol has no bars yet.
    setStatusText('TrendPulse: computing the Custom preset...');
    const ok = await handleAstroGraphExecute(trendPulseCustomRunConfig());
    if (ok) setStatusText('TrendPulse drawn from the Custom preset (computed live).');
    // handleAstroGraphExecute alerts on failure and returns false; nothing to add here.
  }, [handleAstroGraphExecute, barData, extendedBarData, overlays, addOverlay, removeOverlay]);

  const exitStudyMode = useCallback(() => {
    setStudyMode(false);
    setStudyVisibleCount(0);
    setStatusText('Study mode ended.');
    animatePausedRef.current = false;
    if (animateIntervalRef.current) {
      clearInterval(animateIntervalRef.current);
      animateIntervalRef.current = null;
    }
  }, []);

  const handleStudyAdvance = useCallback(() => {
    setStudyVisibleCount(prev => {
      if (!barData || !barData.dates) return prev;
      const next = prev + 1;
      if (next > barData.dates.length) return prev;
      return next;
    });
  }, [barData]);

  // Track study progress and detect completion (avoids setState-inside-setState)
  useEffect(() => {
    if (!studyMode || !barData || !barData.dates) return;
    if (studyVisibleCount >= barData.dates.length) {
      exitStudyMode();
      return;
    }
    if (studyVisibleCount > 0) {
      const d = barData.dates[studyVisibleCount - 1]?.substring(0, 10) || '';
      setStatusText(`Study mode: bar ${studyVisibleCount} of ${barData.dates.length} (${d})`);
    }
  }, [studyMode, studyVisibleCount, barData, exitStudyMode]);

  // Keep ref in sync so setInterval always calls the latest version
  studyAdvanceRef.current = handleStudyAdvance;

  const handleAstroGraphExecuteAndStudy = useCallback(async (dialogState) => {
    const success = await handleAstroGraphExecute(dialogState);
    if (!success) return;

    if (!barData || !barData.dates || barData.dates.length === 0) return;

    const targetDate = dialogState.studyStartDate;
    if (!targetDate) {
      alert('Please select a study start date.');
      return;
    }

    // Find the nearest trading date index at or before the target date
    let cutoffIdx = -1;
    for (let i = 0; i < barData.dates.length; i++) {
      const d = barData.dates[i].substring(0, 10);
      if (d <= targetDate) cutoffIdx = i;
      else break;
    }
    if (cutoffIdx < 0) {
      alert('No trading dates found at or before the selected date.');
      return;
    }
    const cutoffDate = barData.dates[cutoffIdx].substring(0, 10);

    setStudyVisibleCount(cutoffIdx + 1);
    setStudyMode(true);
    setStatusText(`Study mode: chart starts at ${cutoffDate}. Click to reveal bars.`);
    setIsAstroGraphDialogOpen(false);
    // Deactivate area zoom tool so the "+" cursor doesn't interfere with study clicks
    try {
      const chart = eChartRef.current?.getEchartsInstance?.();
      if (chart) {
        chart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
      }
    } catch (_) { /* ignore */ }
  }, [handleAstroGraphExecute, barData]);

  const handleAstroGraphAnimate = useCallback(async (dialogState) => {
    // Execute the AstroGraph overlay first
    const success = await handleAstroGraphExecute(dialogState);
    if (!success) return;

    if (!barData || !barData.dates || barData.dates.length === 0) return;

    const targetDate = dialogState.studyStartDate;
    if (!targetDate) {
      alert('Please select a study start date.');
      return;
    }

    // Find the nearest trading date index at or before the target date
    let cutoffIdx = -1;
    for (let i = 0; i < barData.dates.length; i++) {
      const d = barData.dates[i].substring(0, 10);
      if (d <= targetDate) cutoffIdx = i;
      else break;
    }
    if (cutoffIdx < 0) {
      alert('No trading dates found at or before the selected date.');
      return;
    }
    const cutoffDate = barData.dates[cutoffIdx].substring(0, 10);

    setStudyVisibleCount(cutoffIdx + 1);
    setStudyMode(true);
    setStatusText(`Animate mode: starting at ${cutoffDate}...`);
    setIsAstroGraphDialogOpen(false);

    // Deactivate area zoom
    try {
      const chart = eChartRef.current?.getEchartsInstance?.();
      if (chart) {
        chart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
      }
    } catch (_) { /* ignore */ }

    // Clear any existing animate interval
    if (animateIntervalRef.current) {
      clearInterval(animateIntervalRef.current);
      animateIntervalRef.current = null;
    }

    // --- Imperative animation: bypass React state to avoid full re-render cascade ---
    // We patch the chart directly each tick. NO setStudyVisibleCount during animation.
    // IMPORTANT: barData may have duplicate dates (e.g. 51277 entries) but the chart's
    // xAxis uses deduped dates (e.g. 8373 entries). We must use the deduped arrays
    // so the data dimensions match the xAxis categories.
    const dedupedData = extendBarData(barData, 0);
    const totalBars = dedupedData.dates.length;
    const ohlcFull = dedupedData.ohlc;
    const volumesFull = dedupedData.volumes;
    // Find cutoff in deduped data (original cutoffIdx may differ due to dedup)
    let dedupCutoffIdx = -1;
    for (let i = 0; i < dedupedData.dates.length; i++) {
      const d = dedupedData.dates[i].substring(0, 10);
      if (d <= targetDate) dedupCutoffIdx = i;
      else break;
    }
    if (dedupCutoffIdx < 0) dedupCutoffIdx = 0;
    let currentCount = dedupCutoffIdx + 1;
    const delaySec = dialogState.animateDelaySec || 1;

    animatePausedRef.current = false;
    animateIntervalRef.current = setInterval(() => {
      if (animatePausedRef.current) return; // skip tick while paused
      currentCount++;
      if (currentCount > totalBars) {
        // Animation complete — sync React state and exit
        clearInterval(animateIntervalRef.current);
        animateIntervalRef.current = null;
        setStudyMode(false);
        setStudyVisibleCount(0);
        setStatusText('Study mode ended.');
        return;
      }
      // Patch chart directly — no React re-render
      // Include type + axis indices to prevent ECharts merge from resetting axis bindings
      const instance = eChartRef.current?.getEchartsInstance?.();
      if (instance) {
        const visibleOhlc = ohlcFull.slice(0, currentCount);
        const visibleVols = volumesFull.slice(0, currentCount);
        instance.setOption({
          series: [
            { id: '__candle__', type: 'candlestick', xAxisIndex: 0, yAxisIndex: 0, data: visibleOhlc, animation: false },
            { id: '__volume__', type: 'bar', xAxisIndex: 1, yAxisIndex: 2, data: visibleVols, animation: false },
          ]
        }, false);
      }
    }, delaySec * 1000);
  }, [handleAstroGraphExecute, barData]);

  const handleRunAstroGraphPatterns = useCallback(async () => {
    // Find the AstroGraph overlay
    const agOverlay = overlays.find(o => o.type === 'astroGraphLine');
    if (!agOverlay || !agOverlay.data || !barData) {
      alert('No TrendPulse overlay found. Run TrendPulse first.');
      return;
    }
    const priceCloses = (barData?.ohlc || []).map(bar => bar[3]);
    const priceDates  = (barData?.dates || []).map(d => d.substring(0, 10));
    const payload = {
      series: agOverlay.data,
      priceCloses,
      priceDates,
    };
    try {
      console.log('[App.js] /api/astrograph-patterns payload size:', payload.series.length);
      const response = await api.post('/api/astrograph-patterns', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { patterns, summary, error: backendError } = response.data;
      if (backendError) {
        alert('Pattern detection error: ' + backendError);
        return;
      }
      console.log('[App.js] Pattern detection result:', { patternCount: patterns?.length, summary });
      // Remove any existing pattern overlay before adding new one
      const existingPatternOverlay = overlays.find(o => o.type === 'astroPatternLines');
      if (existingPatternOverlay) {
        removeOverlay(existingPatternOverlay.id);
      }
      addOverlay({
        id:       `astro-patterns-${Date.now()}`,
        type:     'astroPatternLines',
        label:    'TrendPulse Patterns',
        patterns: patterns || [],
        summary:  summary || {},
      });
    } catch (err) {
      console.error('[App.js] /api/astrograph-patterns error:', err);
      alert(`Pattern detection failed: ${err.response?.data?.error || err.message}`);
    }
  }, [api, token, barData, overlays, addOverlay, removeOverlay]);

  // Line -> Candlestick -> Line. The toolbar cycle is two states for everyone now: `hidden` moved
  // to the Admin Panel, where it is a switch. Pressing this button WHILE hidden still restores a
  // visible chart -- see nextChartView -- which is what somebody pressing it in that state wants.
  const handleToggleChartType = useCallback(() => {
    setChartType(prevType => nextChartView(prevType, isAdmin));
  }, [isAdmin]);

  /* -- the three Admin Panel actions that need the chart ----------------------------------- */

  /** The invisible-price view, as a switch rather than a stop on the toolbar cycle. */
  const handleTogglePriceHidden = useCallback((hidden) => {
    setChartType(prev => withPriceHidden(prev, hidden, isAdmin));
  }, [isAdmin]);

  /**
   * Zooms to the first calendar year of the loaded series.
   *
   * <p>Delegated to the chart rather than dispatched from here. The chart owns its ECharts
   * instance and its axis configuration, and a second place that knows how to dispatch a
   * `dataZoom` is a second place to get the target wrong -- a bare dispatch hits the y-axis zooms
   * too and collapses the price scale, which is a bug this codebase has already produced once.
   */
  const handleZoomFirstYear = useCallback(() => {
    const result = eChartRef.current?.zoomToFirstCalendarYear?.() || null;
    setStatusText(result
      ? `Zoomed to ${result.year}: ${result.from} to ${result.to} (${result.bars} bars).`
      : 'No series is loaded, so there is no first year to zoom to.');
    return result;
  }, []);

  /**
   * HOW MANY TRADE CARDS EXIST, derived from the overlays themselves.
   *
   * <p>Same helper the chart uses to decide what "all" means, so the Admin Panel's label and the
   * set the button opens cannot disagree -- a button reading "Show all (32)" that then shows
   * thirty-one is the kind of wrong nobody reports and everybody notices.
   */
  const tradeCardCount = useMemo(() => tradeCardKeys(overlays).length, [overlays]);

  /** How many are open right now. Pushed up by the chart, because the state lives in there. */
  const [openTradeCardCount, setOpenTradeCardCount] = useState(0);

  /**
   * Whether the yearly summary plaques are on, mirrored here so the Admin Panel's button can
   * label itself. The chart owns the real state; this follows it.
   */
  const [summariesShown, setSummariesShown] = useState(false);

  /** Shows or hides the yearly summary plaques. Returns how many are now drawn. */
  const handleSetYearlySummaries = useCallback((visible) => {
    const count = eChartRef.current?.setYearlySummaries?.(visible) ?? 0;
    setSummariesShown(!!visible);
    setStatusText(count
      ? `Showing ${count} yearly summary plaque${count === 1 ? '' : 's'}.`
      : 'Yearly summaries hidden.');
    return count;
  }, []);

  /** Opens or closes every trade card at once. Returns how many are now open. */
  const handleSetAllTradeCards = useCallback((visible) => {
    const count = eChartRef.current?.setAllTradeCards?.(visible) ?? 0;
    setStatusText(count
      ? `Showing ${count} trade card${count === 1 ? '' : 's'}.`
      : 'All trade cards hidden.');
    return count;
  }, []);

  // What the chart and the toolbar actually use. Rendering goes through the normaliser rather
  // than through raw state so that losing admin while the chart is hidden — a token refresh, or
  // signing back in as a standard user — restores a visible chart immediately, instead of leaving
  // a blank canvas and a two-state cycle that never gets back to it.
  const effectiveChartType = useMemo(
    () => normalizeChartView(chartType, isAdmin),
    [chartType, isAdmin]
  );

  // Watches the backend's data-freshness revision. The poll is cheap - a public endpoint
  // returning a counter - and the callback only fires when that counter actually moves, so
  // the steady state costs one small request every couple of minutes and nothing else.
  useMarketDataSync(api, {
    market: appConfig?.market === 'IN' ? 'IN' : 'US',
    onSyncComplete: useCallback((event) => {
      console.info(
        '[App.js] market data sync completed',
        event ? `(${event.market}: ${event.updated} ticker(s) updated, latest ${event.latestDataDate})` : ''
      );
      setMarketDataRevision(r => r + 1);
    }, []),
  });

  // Which symbol the chart currently holds data for. Comparing against it is what separates a
  // fetch the customer asked for from one the app decided to do on its own.
  const loadedSymbolRef = useRef(null);

  /**
   * THE CUSTOMER'S OWN SERIES, held in memory and nowhere else.
   *
   * <p>Plain React state: no localStorage, no sessionStorage, no IndexedDB. A reload loses it,
   * which is the correct behaviour for data the platform has promised not to keep and is the one
   * property that makes "zero knowledge" true on the client as well as the server. The chart's CTS
   * dataset reader caches to IndexedDB, so reusing any of that machinery here would quietly
   * persist a customer's price history on their own laptop.
   *
   * <p>It feeds two things: the chart (so an uploaded series is visible and a replay has candles
   * to run over) and every simulation request (so the engines run on it). One source for both,
   * because a chart drawn from one series while the engine ran another is the cross-implementation
   * divergence this repository has already paid for twice.
   */
  const [customSeries, setCustomSeries] = useState(null);
  const customSelected = isCustomSymbol(selectedSymbol);

  /*
   * DECLARED HERE, BELOW `customSeries`, and that placement is load-bearing.
   *
   * `loadSessionTimeline` names `customSeries` in its dependency array, and a dependency array is
   * evaluated during RENDER — so with this block in its natural reading position, above the replay
   * handlers, the whole app threw `Cannot access 'customSeries' before initialization` on mount.
   * Lint passed and the build compiled; `App.tdz.test.js` is the only thing that caught it, which
   * is the fourth time that guard has earned its place.
   */

  /* ── the Flight Simulator: the setup modal, the session, and the mandate ──────────────────
   *
   * A SECOND ENTRY POINT into the same replay the research dialog starts, with a different
   * question in front of it. The dialog asks "what does this configuration do"; this asks "over
   * this window, against this engine, would I have done better". The machinery below the two is
   * identical — one timeline, one overlay, one clock — and that is deliberate: a second replay
   * implementation is how two surfaces come to disagree about one run.
   */
  const [simulatorSetupOpen, setSimulatorSetupOpen] = useState(false);

  /**
   * THE SYMBOL LIST, fetched LAZILY the first time the modal opens.
   *
   * <p>`SymbolList` owns the sidebar's own copy and the two are deliberately not shared: that one
   * is driven by the customer's market and by the asset-class toggles, and lifting its state into
   * App to save one request would couple the modal to a component that reloads whenever the
   * market changes. One cached GET is cheaper than that coupling.
   *
   * <p>Lazy, because most sessions never open this modal, and a failure is non-fatal — the
   * selector then offers Z-Custom and the chart's current symbol, which is enough to launch.
   */
  const [simulatorSymbols, setSimulatorSymbols] = useState(null);
  const [replaySession, setReplaySession] = useState(null);
  const [sessionLaunching, setSessionLaunching] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  /**
   * Fetch a session's timeline, with the band tables for every overlay it draws.
   *
   * <h2>Why the extra requests, and why they are cheap</h2>
   *
   * `SimulationService` builds `timeBands` from the ATTACHED providers, so a CTS-mandate run
   * carries no PTA bands — and the levels cannot be computed here, because they are price extremes
   * of the engine's own bars and a chart that derived its own would be auditing itself.
   *
   * <p>But the band table is written before the replay window is chosen, so `maxBars: 1` returns
   * it complete in a few hundred bytes. One small request per extra category is what makes "draw
   * PTA while trading CTS" affordable rather than three half-megabyte replays.
   *
   * <p>A band fetch that FAILS costs that overlay and nothing else. The session still launches,
   * because the run it is about is the mandate's — losing a decorative overlay must not lose the
   * session.
   */
  const loadSessionTimeline = useCallback(async (session) => {
    /*
     * THE PRIVATE PATH: nothing is sent at all.
     *
     * With the engine switched off — only offered for an uploaded series — the session runs on a
     * timeline built here from the bars already in memory. The chart unveils, the operator trades,
     * and the algo column is empty because there is no algorithm; `buildLocalTimeline` carries the
     * warning that says so, rather than letting an empty column read as a strategy that found
     * nothing.
     *
     * This is the honest answer to "proprietary client data never needs to touch the backend".
     * The parse was always local; this is the half that makes the RUN local too.
     */
    if (session.engine === false) {
      const local = buildLocalTimeline(selectedSymbol, barData);
      if (!local) throw new Error('There are no bars to replay — upload a CSV first.');
      return local;
    }

    const timeline = await fetchReplayTimeline(api, buildSessionRequest({
      provider: session.mandate, symbol: selectedSymbol, customSeries,
      // THE OPERATOR'S TRADE-MANAGEMENT CHOICES, read off the session rather than held here.
      // A mandate change rebuilds the request from the session, so this is also what carries them
      // across one -- and `buildSession` has already normalised the block, so there is nothing to
      // coerce at the call site.
      management: session.management,
    }));

    const extras = ['PTA', 'PTB'].filter((c) => session.overlays[c] && c !== session.mandate);
    const bands = [...(timeline.timeBands || [])];
    for (const category of extras) {
      try {
        // Sequential rather than parallel: two of these at most, and the updater-style burst of
        // concurrent engine runs is not worth saving a few hundred milliseconds on a modal the
        // operator has just closed.
        // eslint-disable-next-line no-await-in-loop
        const extra = await fetchReplayTimeline(api, buildBandOnlyRequest({
          provider: category, symbol: selectedSymbol, customSeries,
          management: session.management,
        }));
        (extra.timeBands || []).forEach((b) => bands.push(b));
      } catch (e) {
        console.warn(`[App] ${category} bands unavailable for this session:`, e);
      }
    }
    return { ...timeline, timeBands: bands };
  }, [api, selectedSymbol, customSeries, barData]);

  /*
   * Fetch the symbol list the first time the modal opens, and only then.
   *
   * <p>A failure is deliberately non-fatal and silent beyond a console warning: the selector then
   * offers Z-Custom and whatever the chart is already on, which is enough to launch a session. An
   * error banner for a list that is a convenience would be a banner about the wrong thing.
   */
  useEffect(() => {
    if (!simulatorSetupOpen || simulatorSymbols !== null) return;
    let cancelled = false;
    api.get(`/api/symbols?market=${appConfig?.market || 'US'}`)
      .then((res) => {
        if (cancelled) return;
        setSimulatorSymbols(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn('[App] the simulator symbol list could not be loaded:', e);
        setSimulatorSymbols([]);
      });
    return () => { cancelled = true; };
  }, [simulatorSetupOpen, simulatorSymbols, api, appConfig]);

  /** Launch from the modal: fetch, arm the chart, close the modal. */
  const handleLaunchSession = useCallback(async (session) => {
    setSessionError(null);
    setSessionLaunching(true);
    try {
      const timeline = await loadSessionTimeline(session);
      // ORDER MATTERS. The session has to be set before the timeline, or the overlay mounts with
      // a run and no window and jumps the playhead to 1993 before the session's own effect can
      // move it -- a visible lurch at the start of every launch.
      setReplaySession(session);
      handleStartReplay(timeline);
      setSimulatorSetupOpen(false);
    } catch (e) {
      setSessionError(e?.response?.data?.error || e.message || 'The session could not be loaded');
    } finally {
      setSessionLaunching(false);
    }
  // handleStartReplay is declared above and is stable; setStatusText is deliberately absent, as
  // it is from every sibling handler here.
  }, [loadSessionTimeline, handleStartReplay]);

  /**
   * SWITCHING THE MANDATE RE-RUNS THE ENGINE, because the algo book is the backend's.
   *
   * <p>The window and the overlays are untouched, so the canvas does not move: the same bands are
   * drawn, the playhead returns to the same bar, and only the book being compared against changes.
   * The human book restarts with it — the target it was being measured against has been replaced,
   * and carrying a book across that would be comparing against something that moved after the
   * fact. `DynaManReplayOverlay` does that reset itself, keyed on the timeline.
   */
  const handleMandateChange = useCallback(async (mandate) => {
    if (!replaySession || mandate === replaySession.mandate) return;
    const next = { ...replaySession, mandate, overlays: { ...replaySession.overlays, [mandate]: true } };
    setSessionLaunching(true);
    try {
      const timeline = await loadSessionTimeline(next);
      setReplaySession(next);
      handleStartReplay(timeline);
    } catch (e) {
      setSessionError(e?.response?.data?.error || e.message || 'The mandate could not be changed');
    } finally {
      setSessionLaunching(false);
    }
  }, [replaySession, loadSessionTimeline, handleStartReplay]);

  useEffect(() => {
    // Only a first load or a symbol change blanks the canvas. A background refresh keeps the
    // existing chart on screen and swaps the data in underneath when it arrives.
    //
    // This is what caused "Loading chart data..." to appear unprovoked. The effect re-runs
    // whenever `api` changes identity, and `api` is a useMemo over the Firebase ID token — which
    // Firebase rotates roughly hourly. So a chart nobody had touched would blank itself and
    // reload, mid-session, for a token refresh that had nothing to do with the data. The same
    // happened when the sync poll reported a change. Neither is a reason to take the chart away
    // from someone who is reading it.
    const isBackgroundRefresh = loadedSymbolRef.current === selectedSymbol;

    // THE SYNTHETIC SYMBOL IS NEVER FETCHED. `Z-Custom` is an affordance, not a ticker: there is
    // no eod/Z-CUSTOM.txt and asking for one would answer 404 and render as "Error: ..." over a
    // feature that is working exactly as intended. The chart is built from the parsed upload
    // instead, or left empty so the upload panel shows.
    if (customSelected) {
      setLoading(false);
      setError(null);
      if (customSeries && customSeries.bars && customSeries.bars.length) {
        setBarData({
          symbol: selectedSymbol,
          dates: customSeries.bars.map((b) => b.date),
          // [open, close, low, high] -- ECharts' candlestick order, which is NOT OHLC. Taken from
          // the stored path three lines of this file away rather than re-derived, because the two
          // must agree: a transposed pair here would draw every candle inside out and still look
          // like a chart.
          ohlc: customSeries.bars.map((b) => [b.open, b.close, b.low, b.high]),
          volumes: customSeries.bars.map((b) => b.volume),
        });
        loadedSymbolRef.current = selectedSymbol;
      } else {
        setBarData(null);
        loadedSymbolRef.current = null;
      }
      return;
    }

    const fetchData = async () => {
      if (!isBackgroundRefresh) {
        setLoading(true);
        setError(null);
      }
      try {
        // Add aggressive cache-busting with timestamp and random parameter
        const timestamp = Date.now();
        const random = Math.random();
        // ⚠ ENCODED, because a symbol can contain a CARET and Tomcat refuses one in a raw path.
        //
        // `^DJI` and `^GSPC` — the long index series — are legal symbols under
        // `TickerListFormat.VALID_SYMBOL`, are in the ticker list, and are served correctly: the
        // backend answers 200 with 35,249 bars for `/api/getDataForSymbol/%5EDJI`. Interpolated
        // raw, the same request is `/api/getDataForSymbol/^DJI` and Tomcat rejects it with a
        // **400 before it ever reaches Spring** — `^` is in RFC 3986's "unwise" set and Tomcat's
        // request-target validation refuses it outright.
        //
        // So the symbol loaded, the picker offered it, the file was present and complete, and the
        // chart stayed empty with a 400 nothing in the app was reading. It reads as a data
        // problem and is a URL problem.
        //
        // A no-op for every other symbol here: `encodeURIComponent` leaves letters, digits, `.`,
        // `-` and `_` alone, which is the whole of the rest of the catalogue including the MIC
        // suffixes (`RELIANCE.XNSE`).
        const res = await apiRef.current.get(
          `/api/getDataForSymbol/${encodeURIComponent(selectedSymbol)}?_t=${timestamp}&_r=${random}`);
        const rawData = res.data;
        if (!Array.isArray(rawData)) throw new Error('Data is not an array');

        const sortedData = rawData.sort((a, b) => new Date(a.date) - new Date(b.date));

        const formattedData = {
          symbol: selectedSymbol,
          dates: sortedData.map(item => item.date),
          ohlc: sortedData.map(item => [item.open, item.close, item.low, item.high]),
          volumes: sortedData.map(item => item.volume),
        };

        setBarData(formattedData);
        loadedSymbolRef.current = selectedSymbol;
      } catch (err) {
        // A background refresh that fails leaves the chart exactly as it was. Replacing a chart
        // the customer is reading with an error banner, because a refresh they never asked for
        // failed, is strictly worse than showing slightly older data.
        if (isBackgroundRefresh) {
          console.warn('[App.js] background chart refresh failed; keeping existing data:', err.message);
        } else {
          setError(err.message);
        }
      } finally {
        if (!isBackgroundRefresh) setLoading(false);
      }
    };
    fetchData();
    // marketDataRevision is deliberately a dependency: incrementing it after a completed
    // sync is what re-fetches the chart without the customer refreshing the page.
    //
    // `api` deliberately is NOT one. It is read through a ref, so a token refresh no longer
    // re-runs this effect at all — the token changing says nothing about the data having changed.
  // `customSeries` is in here because the chart for the synthetic symbol is BUILT from it.
  // Without it an upload parses, the state updates, and nothing redraws -- the panel reports
  // success over an empty chart area, which reads as a parser that did not work.
  }, [selectedSymbol, marketDataRevision, customSelected, customSeries]);

  /**
   * Run an Elliott wave analysis anchored at the last bar the reader clicked.
   *
   * <p><b>The clicked bar is treated as the last bar of the series.</b> The engine returns only
   * CURRENT patterns, so where the series ends decides what it labels; cutting the bars at the
   * anchor is therefore the whole of anchoring the count. It also makes the count checkable — the
   * projection now runs into bars that already exist, so the reader can see whether price went
   * where the count said it would.
   *
   * <p>There is deliberately no fall-back to the true last bar. The point of the change is that a
   * count on screen names the bar it came from; silently defaulting would put back exactly the
   * ambiguity it removes, since a reader could not tell a deliberate anchor from a default. The
   * last bar is still one click away, and clicking it says so.
   *
   * <p>The panel opens immediately, before the request resolves, so the click has a visible effect
   * even on a slow connection. Opening it only on success would leave the toolbar spinner as the
   * sole feedback and make a failure look like nothing happened at all.
   */
  const handleRunElliottAnalysis = useCallback(async () => {
    const source = barData && Array.isArray(barData.dates) && barData.dates.length > 0
      ? barData
      : extendedBarData;

    setElliottError(null);
    setElliottResult(null);
    setElliottAnchor(null);
    setElliottStructure([]);
    setElliottProjections([]);
    setElliottScore(null);
    setElliottPendingDraw(false);
    setElliottOpen(true);

    if (!Number.isInteger(lastClickedBar?.index)) {
      setElliottError('Click a bar on the chart first. The count is anchored there — that bar is '
        + 'treated as the last bar of the series, so the projection runs forward from it. '
        + 'For a count on the newest data, click the last bar.');
      return;
    }

    const anchor = lastClickedBar;
    const body = buildElliottRequest(source, {
      symbol: selectedSymbol || source?.symbol,
      timeframe: 'Daily',
      upToBarIndex: anchor.index,
    });

    // Checked here as well as server-side: a round trip that can only come back "too few bars" is
    // one the customer waits for to be told something already known. Worded for the anchor, since
    // with one set the shortfall is usually where the reader clicked and not the range they loaded.
    if (body.bars.length < MIN_BARS_FOR_ANALYSIS) {
      setElliottError(`Only ${body.bars.length} usable bars sit at or before ${anchor.date} `
        + `(bar #${anchor.index + 1}). At least ${MIN_BARS_FOR_ANALYSIS} are needed to detect `
        + 'swings — click a later bar, or load a longer range.');
      return;
    }

    setElliottLoading(true);
    try {
      const res = await api.post('/api/charts/elliott-analysis', body);
      setElliottResult(res.data);
      setElliottAnchor(anchor);

      // Verified against `source` — the SAME array the request was built from, so a bar index means
      // the same bar on both sides. Verifying against the other of barData/extendedBarData would
      // shift every index by however many bars the two differ by.
      setElliottStructure(waveStructure(res.data, source?.dates, anchor.index));
      const verified = verifyProjections(
        allProjections(res.data, anchor.index), toVerificationBars(source),
        { anchorIndex: anchor.index });
      setElliottProjections(verified);
      setElliottScore(projectionSummary(verified));
      // A stale polyline from the previous symbol or range would sit on the new chart looking like
      // a count of it. Cleared on every run; the effect below immediately draws the new one.
      removeOverlay(ELLIOTT_OVERLAY_ID);
      setElliottOverlayShown(false);
      setElliottPendingDraw(true);
    } catch (err) {
      console.error('[App] Elliott analysis failed:', err);
      setElliottError(err.response?.data?.message || err.message || 'The analysis request failed.');
    } finally {
      setElliottLoading(false);
    }
  }, [api, barData, extendedBarData, selectedSymbol, removeOverlay, lastClickedBar]);

  /**
   * Draw or clear the wave polyline on the price panel.
   *
   * Goes through the overlay system rather than patching the chart instance directly. An
   * imperative patch would survive only until the next full option rebuild — which any other
   * overlay change triggers — and the wave lines would then vanish for no reason the customer
   * could see. The candlestick data itself is untouched either way; nothing is refetched and no
   * analysis is re-run.
   */
  const handleToggleElliottOverlay = useCallback((show) => {
    if (!show) {
      removeOverlay(ELLIOTT_OVERLAY_ID);
      setElliottOverlayShown(false);
      return;
    }
    const overlay = buildElliottOverlay(elliottResult, {
      color: activeDrawingColor,
      // The anchor drops counts that stopped describing the market long before it; the verdicts
      // colour each projection by what became of it. Both are what the click made knowable.
      anchorIndex: elliottAnchor?.index,
      verified: elliottProjections,
    });
    if (!overlay) {
      // The switch is disabled in this case, so reaching here means the result changed underneath
      // it. Leaving the switch off is the honest outcome; turning it on would claim a drawing that
      // is not there.
      setElliottOverlayShown(false);
      return;
    }
    // Replace rather than stack: re-running the analysis must not leave the previous count behind.
    removeOverlay(ELLIOTT_OVERLAY_ID);
    addOverlay(overlay);
    setElliottOverlayShown(true);

    // Put the count on screen.
    //
    // The engine returns only CURRENT patterns, and on a long series those are routinely a handful
    // of bars at the extreme right — a real AAPL run gave counts spanning 2, 2 and 4 bars out of
    // 11,502. Drawn at full-history zoom that is under a pixel wide, which is indistinguishable
    // from the overlay not working, and was reported as exactly that.
    //
    // Zooming is a change to the user's view, so it is done ONLY on the click that turns the study
    // on, and the status line says what happened and how wide the count is. A study that silently
    // moved the chart every time it redrew would be worse than one that could not be seen.
    // The extent to frame is everything DRAWN, not just the pivots: the targets and the dotted
    // projection run past the count into future space, and framing the pivots alone left them
    // correct, drawn, and outside the window.
    const rightEdge = Number.isFinite(overlay.drawnLastIndex)
      ? overlay.drawnLastIndex
      : overlay.lastBarIndex;
    const span = overlay.lastBarIndex - overlay.firstBarIndex;
    const chart = eChartRef.current?.getEchartsInstance?.();
    if (chart && Number.isFinite(overlay.firstBarIndex) && Number.isFinite(overlay.lastBarIndex)) {
      // Context either side, proportional to the count but never less than 30 bars — a four-bar
      // pattern framed by four bars is still unreadable, and the surrounding price action is what
      // makes a wave count mean anything.
      const pad = Math.max(30, Math.round(span * 1.5));
      try {
        chart.dispatchAction({
          type: 'dataZoom',
          startValue: Math.max(0, overlay.firstBarIndex - pad),
          endValue: rightEdge + Math.round(pad / 2),
        });
      } catch (e) {
        // A failed zoom must not take the overlay down with it: the study is drawn either way.
        console.warn('[App] Could not focus the chart on the Elliott count:', e);
      }
    }
    // The anchor is named, because the drawing is identical whether the count was read as of a
    // 2015 bar or as of today, and nothing else on the chart distinguishes the two. A count whose
    // projection runs across bars that already exist is a claim the reader can check; one that does
    // not say where it was read from is not.
    const asOf = Number.isInteger(elliottAnchor?.index)
      ? ` Read as of ${elliottAnchor.date} (bar #${elliottAnchor.index + 1}), treated as the last bar.`
      : '';
    // The scoreline goes in the status bar too, because it is the answer to "is any of this worth
    // anything" and a reader who closed the panel has otherwise thrown it away.
    const score = elliottScore ? ` ${projectionScoreline(elliottScore)}` : '';
    setStatusText(`Elliott: ${elliottResult.primaryConclusion.pattern} at `
      + `${elliottResult.primaryConclusion.degree} degree, spanning ${span + 1} bar(s).${asOf}${score} `
      + 'Chart focused on the count — zoom out to see it in context.');
    // setStatusText is deliberately absent, and its absence is load-bearing rather than an
    // oversight: it is a useState setter declared FURTHER DOWN this component, so naming it here
    // throws "Cannot access before initialization" during render and takes the app to a blank
    // screen. App.tdz.test.js caught exactly that. React guarantees a setter is stable across
    // renders, so it can never go stale — the body calls it long after the declaration is reached.
  }, [addOverlay, removeOverlay, elliottResult, elliottAnchor, elliottProjections, elliottScore,
    activeDrawingColor]);

  /**
   * Draws the overlay as soon as a run's results are readable — the study is ON by default.
   *
   * <p>Placed HERE, after handleToggleElliottOverlay, and that is load-bearing rather than tidiness:
   * the callback is a const in this component's body, so an effect declared above it that names it
   * in a dependency array throws "Cannot access before initialization" during render and takes the
   * app to a blank screen. App.tdz.test.js exists for exactly this.
   *
   * <p>An effect rather than a call inside the request handler, because the draw reads the result
   * and the verdicts out of state, and inside the handler those are still the previous run's.
   */
  React.useEffect(() => {
    if (!elliottPendingDraw || !elliottResult) return;
    setElliottPendingDraw(false);
    handleToggleElliottOverlay(true);
  }, [elliottPendingDraw, elliottResult, handleToggleElliottOverlay]);

  const handleRunSingleStudy = useCallback(async (studyOrOverlays) => {
    setLoading(true);
    setError(null);

    try {
      if (Array.isArray(studyOrOverlays)) {
        studyOrOverlays.forEach(addOverlay);
        setLoading(false);
        return;
      }

      const study = studyOrOverlays;
      if (!study.type) study.type = 'longitudinalDistance';

      const longitudeTypes = ['longitudinalDistance', 'longitudeFollow', 'sidereal', 'declination', 'longitude', 'longitudeDifference', 'planetFollow'];
      if (longitudeTypes.includes(study.type)) {
        if (!barData || !barData.dates || barData.dates.length === 0) {
          alert('Chart data is not available. Please load a symbol first.');
          return;
        }

        const fromDate = extendedBarData.dates[0];
        const toDate = extendedBarData.dates[extendedBarData.dates.length - 1];

        // frame decides whether the backend computes heliocentric or geocentric longitudes. Defaulted
        // here rather than left undefined, so a study object without one is explicit about asking
        // for the conventional frame instead of relying on the endpoint's own default.
        const params = new URLSearchParams({
          planet1: study.planet1, planet2: study.planet2, degrees: study.degrees, fromDate, toDate,
          frame: study.frameOfReference === 'Heliocentric' ? 'heliocentric' : 'geocentric',
        });

        const response = await api.get(`/api/followstudy/dates?${params.toString()}`);
        const dates = response.data;
        const overlaysToAdd = dates.map(date => {
          const formattedDate = date.split('T')[0];
          return {
            type: 'verticalLine',
            date: formattedDate,
            label: `${study.planet1.substring(0, 2)}-${study.planet2.substring(0, 2)} ${study.degrees}°\n${formattedDate}`,
            color: study.color || '#000000',
            thickness: study.thickness || 2,
            planet1: study.planet1,
            planet2: study.planet2,
            degrees: study.degrees,
          };
        });

        overlaysToAdd.forEach(addOverlay);
      } else {
        console.log(`Study type ${study.type} not handled yet.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [barData, extendedBarData, addOverlay, api]);

  /**
   * ALL - Average Longitude Lines.
   *
   * Draws through the SAME overlay type as the Planetary Lines study ('planetaryLine'), so it
   * inherits that pipeline whole: the longitude-to-price mapping, the 0-180 fold, octave stacking
   * and the gap at each turn. The only difference upstream is that the series is a circular mean
   * of several bodies rather than one body's longitude.
   *
   * A bar whose bodies cancel out - an exact opposition has no mean direction - arrives as null
   * and is carried through as null so the line breaks there. Feeding it to unwrapDegrees would
   * turn it into a bare offset and draw a line through a level nothing computed.
   */
  const handleRunAverageLongitude = useCallback(async ({
    planets, frame, dualSystem, label, color, lineType, thickness, pricePer360,
    autoFitOctaves, octaveStart, octaveEnd,
  }) => {
    const source = extendedBarData?.dates?.length ? extendedBarData : barData;
    if (!source?.dates?.length) {
      setError('Load a symbol before running the ALL study.');
      return;
    }
    const dates = source.dates.map(d => String(d).substring(0, 10));

    try {
      const res = await api.post('/api/followstudy/average-longitude', {
        planets, zodiac: frame, dualSystem, dates,
      });
      const data = res.data || {};
      const raw = Array.isArray(data.longitudes) ? data.longitudes : [];
      if (raw.length !== dates.length) {
        setError('The ALL study returned a series of the wrong length.');
        return;
      }

      const price360 = parseFloat(pricePer360);
      const price360Safe = Number.isFinite(price360) ? price360 : 360;
      const plotted = unwrapDegreesWithGaps(raw);

      addOverlay({
        id: `average-longitude-${Date.now()}`,
        type: 'planetaryLine',
        mode: 'longitude',
        planet: label,
        planet2: label,
        label,
        color,
        thickness,
        lineType,
        panel: 'main',
        pricePer360: Number.isFinite(price360) ? pricePer360 : '360',
        autoFitOctaves,
        octaveStart,
        octaveEnd,
        data: dates.map((date, i) => ({
          value: [date, plotted[i] == null ? null : (plotted[i] * price360Safe) / 360],
        })),
      });

      const undefinedBars = data.undefinedBars || 0;
      const partial = Array.isArray(data.partialBodies) ? data.partialBodies : [];
      setStatusText(
        `${label}: ${(data.bodies || []).length} bodies`
        + (partial.length ? ` (${partial.join(', ')} geocentric only)` : '')
        + (undefinedBars ? ` — ${undefinedBars} bar${undefinedBars === 1 ? '' : 's'} with no defined average` : '')
      );
    } catch (err) {
      console.error('[App] ALL study failed:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message
        || 'The ALL study request failed.');
    }
    // setStatusText and setError are deliberately absent. Both are useState setters, which React
    // guarantees stable for the life of the component, so listing them changes nothing — except
    // that setStatusText is DECLARED BELOW this callback, and naming it here is a temporal dead
    // zone reference that throws on mount and takes the whole app down. App.tdz.test.js caught it.
  }, [api, barData, extendedBarData, addOverlay]);

  const handleRunPlanetaryLines = useCallback(async ({ planet, color, lineType, thickness, mode, pricePer360, drawRawLongitude, autoFitOctaves, octaveStart, octaveEnd }) => {
    if (!extendedBarData || !extendedBarData.dates || !extendedBarData.dates.length) {
      alert('Chart data unavailable');
      return;
    }

    const panel = 'main'; // Force main panel as per Dialog logic
    const dates = extendedBarData.dates;
    const isLongitude = mode === 'longitude';

    // Log activity
    const eventDetails = {
      studyType: 'planetaryLine',
      parameters: { planet, pricePer360: panel === 'main' ? pricePer360 : undefined }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);

    const payload = {
      planet,
      dates,
      ...(panel === 'main' ? { pricePer360 } : {}),
      drawRawLongitude,
      isLongitude
    };

    try {
      const res = await api.post('/api/followstudy/getPlanetaryLines', payload);
      const data = res.data;
      
      if (Array.isArray(data.longitudes) && data.longitudes.length === dates.length) {
         const rawDegrees = data.longitudes;
         const degreesForPlot = (mode === 'declination' || drawRawLongitude) ? rawDegrees : unwrapDegrees(rawDegrees);

         let overlayData;
         if (panel === 'main') {
           if (mode === 'declination') {
             overlayData = dates.map((date, i) => ({ value: [date, degreesForPlot[i]] }));
           } else {
             const price360 = parseFloat(pricePer360);
             const price360Safe = Number.isFinite(price360) ? price360 : 360;
             overlayData = dates.map((date, i) => ({ value: [date, (degreesForPlot[i] * price360Safe) / 360] }));
           }
         } else {
             overlayData = dates.map((date, i) => ({ value: [date, degreesForPlot[i]] }));
         }

         addOverlay({
            id: `planetary-${planet}-${Date.now()}`,
            type: 'planetaryLine',
            mode,
            planet,
            planet2: planet,
            color,
            thickness,
            lineType,
            panel,
            pricePer360: (panel === 'main' ? (Number.isFinite(parseFloat(pricePer360)) ? pricePer360 : '360') : undefined),
            // Octave coverage, read by chartDataProcessor. Undefined on an overlay saved before
            // these existed, which octavesFor treats as auto-fit — the behaviour it always had.
            autoFitOctaves,
            octaveStart,
            octaveEnd,
            data: overlayData
         });

      } else {
         alert('Planet longitude data length mismatch.');
      }

    } catch (err) {
      console.error('[App.js] Error fetching planetary lines:', err);
      alert(`Error: ${err.message}`);
    }

  }, [extendedBarData, addOverlay, api, user]);

  const handleRunSeasonality = useCallback(async ({ tradingDays, matchMode, displayType, asOfDate }) => {
    if (!barData || !barData.ohlc || !barData.ohlc.length) {
      alert('Chart data unavailable');
      return;
    }
    const symbol = barData.symbol || selectedSymbol;
    const chartLastDate = barData.dates[barData.dates.length - 1]?.split('T')[0];
    const lastBarDate = asOfDate || chartLastDate;

    // Find the close price for the as-of date
    let currentPrice;
    if (asOfDate && asOfDate !== chartLastDate) {
      const targetDate = asOfDate.split('T')[0];
      const idx = barData.dates.findIndex(d => d.split('T')[0] === targetDate);
      if (idx !== -1) {
        currentPrice = barData.ohlc[idx][1]; // close price
      } else {
        // Find nearest prior date
        for (let i = barData.dates.length - 1; i >= 0; i--) {
          if (barData.dates[i].split('T')[0] <= targetDate) {
            currentPrice = barData.ohlc[i][1];
            break;
          }
        }
      }
    }
    if (currentPrice === undefined) {
      currentPrice = barData.ohlc[barData.ohlc.length - 1][1];
    }

    try {
      const res = await api.post('/api/followstudy/seasonality-projection', {
        symbol,
        tradingDays,
        matchMode,
        currentPrice,
        lastBarDate,
      });
      const data = res.data;
      if (data && Array.isArray(data.projections) && data.projections.length > 0) {
        // Remove any previous seasonality overlay before adding new one
        const existing = overlays.filter(o => o.type === 'seasonalityProjection');
        existing.forEach(o => removeOverlay(o.id));

        addOverlay({
          id: 'seasonality-projection',
          type: 'seasonalityProjection',
          displayType: displayType || 'candlestick',
          projections: data.projections,
        });
        setStatusText(`Seasonality projection: ${data.projections.length} days`);
      } else {
        alert('No seasonality data returned');
      }
    } catch (err) {
      console.error('[App.js] Seasonality projection error:', err);
      alert(`Seasonality error: ${err.message}`);
    }
  }, [barData, selectedSymbol, overlays, addOverlay, removeOverlay, api]);

  const handleRunCharacteristicsPlot = useCallback(async ({ characteristicType, planet1, planet2, color, lineThickness, lineType, panel }) => {
    if (!extendedBarData || !Array.isArray(extendedBarData.dates) || extendedBarData.dates.length === 0) {
      alert('Chart data unavailable');
      return;
    }
    try {
      const payload = {
        characteristicType,
        planet1,
        planet2,
        dates: extendedBarData.dates,
      };
      const res = await api.post('/api/followstudy/characteristics-series', payload);
      const data = res.data;
      if (data?.error) {
        alert('Characteristics error: ' + data.error);
        return;
      }
      const values = Array.isArray(data?.values) ? data.values : [];
      if (values.length !== extendedBarData.dates.length) {
        alert('Characteristics: data length mismatch.');
        return;
      }

      const labelBase =
        characteristicType === 'longitude_diff' ? `Lon diff: ${planet1}-${planet2}`
        : characteristicType === 'declination_diff' ? `Decl diff: ${planet1}-${planet2}`
        : characteristicType === 'distance_percent_diff' ? `Dist% diff: ${planet1}-${planet2}`
        : characteristicType === 'distance_percent' ? `Dist%: ${planet1}`
        : characteristicType === 'speed' ? `Speed: ${planet1}`
        : `${characteristicType}`;

      const overlayData = extendedBarData.dates.map((d, i) => ({ value: [d, values[i]] }));

      addOverlay({
        id: `characteristic-${characteristicType}-${planet1}-${planet2 || ''}-${Date.now()}`,
        type: 'characteristicLine',
        characteristicType,
        planet1,
        planet2,
        label: labelBase,
        color: color || '#E67C73',
        thickness: String(lineThickness || '2'),
        lineType: (lineType || 'Solid').toLowerCase(),
        panel: panel || 'main',
        data: overlayData,
      });
    } catch (err) {
      console.error('[App.js] Characteristics plot error:', err);
      alert(`Characteristics failed: ${err.response?.data?.error || err.message}`);
    }
  }, [extendedBarData, api, addOverlay]);

  const handleOpenAreaHighlightDialog = () => setIsAreaHighlightDialogOpen(true);
  const handleCloseAreaHighlightDialog = () => setIsAreaHighlightDialogOpen(false);

  const [statusText, setStatusText] = useState('Ready');

  useEffect(() => {
    if (statusText !== 'Ready') {
      const timer = setTimeout(() => {
        setStatusText('Ready');
      }, 3000);
      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [statusText]);

  /**
   * Run the Spiral Calendar convergence scan and draw the leading upcoming zone.
   *
   * <p>One click, no dialog: the scan takes the loaded symbol and the server's own defaults, which
   * were tuned against real data. Everything configurable is available on the endpoint for anyone
   * who wants it, but the button is meant to answer one question - is a reversal due? - without
   * first asking six.
   *
   * <p>Draws `upcomingZone`, NOT `confluenceZone`. The strongest convergence in the series is
   * historical and always will be: on AAPL it scores three times the best upcoming one, so leading
   * with it would answer "when was the biggest convergence ever" to a button asking about now.
   */
  /**
   * Draws the scan's upcoming zone. Split out so the mode dialog can run it without re-scanning.
   */
  const drawSpiralZone = useCallback((data) => {
    // Clear the previous run first, always. Overlays carry fixed ids so a re-run replaces
    // rather than stacks, but a scan that finds nothing must also take the old band away -
    // otherwise the last symbol's zone sits on the new chart looking like a result for it.
    // The full id list matters here: four zones followed by two would strand two orphan bands.
    SPIRAL_OVERLAY_IDS.forEach(removeOverlay);

    // The NEAR-TERM window, not the future-only list. A convergence that landed a few days ago is
    // the strongest evidence this study offers, because the bars are there to check it against -
    // and the future-only filter dropped it at exactly the moment it became verifiable. Measured
    // on SPY: a 5-pivot zone scoring 68 two days before the all-time high was invisible, while the
    // badge showed a future zone scoring 34.
    const zones = Array.isArray(data?.nearTermZones) ? data.nearTermZones : [];
    const source = extendedBarData?.dates?.length ? extendedBarData : barData;
    setSpiralParams(data?.params || null);
    setSpiralZones(zones);
    // Set here rather than at each call site, so every path that draws zones reports the window it
    // actually drew. `anchoredOnLastBar` is absent on an older response, which reads as unanchored.
    setSpiralAnchored(data?.nearTermWindow?.anchoredOnLastBar === false);

    if (zones.length === 0) {
      setSpiralZone(null);
      setSpiralTruncated(false);
      const w = data?.nearTermWindow;
      setSpiralUndrawn(0);
      setStatusText(`Φ scan: no convergence for ${selectedSymbol} between `
        + `${w?.fromDate || '?'} and ${w?.toDate || '?'} (${data?.pivotCount || 0} pivots examined).`);
      return;
    }

    buildMultiSpiralOverlays(zones, source).forEach(addOverlay);

    const lead = zones[0];
    const span = resolveZoneDates(source?.dates, lead);
    const undrawn = countUndrawnZones(zones);
    setSpiralZone(lead);
    setSpiralTruncated(Boolean(span?.truncated));
    setSpiralUndrawn(undrawn);

    const shown = Math.min(zones.length, SPIRAL_MAX_ZONES);
    setStatusText(`Φ scan: ${shown} convergence zone${shown === 1 ? '' : 's'} near now`
      + `, strongest ${lead.convergenceCount} pivots on ${lead.targetDate}`
      + `${undrawn > 0 ? ` (${undrawn} weaker not drawn)` : ''}.`);
  }, [selectedSymbol, barData, extendedBarData, addOverlay, removeOverlay, setStatusText]);

  /**
   * Re-runs the scan with the near-term window centred on a chosen bar, then draws it.
   *
   * <p>A second call rather than a filter over the scan already held, because the window is applied
   * server-side: `nearTermZones` arrives already cut to a span around the last bar, so a zone
   * sitting near the CLICKED bar and nowhere near today was never in the response to be re-read.
   * Filtering the cached result would silently return whichever of the anchored zones happened to
   * fall in both windows, which is a subset of the answer presented as the answer.
   *
   * <p>Declared above handleSpiralModeRun, which names it in a dependency array — a const named
   * before it is initialised throws on first render, and this file has broken that way twice.
   */
  const runSpiralScanAnchoredAt = useCallback(async (anchor) => {
    if (!selectedSymbol || !Number.isInteger(anchor?.index)) {
      setError('Click a bar on the chart before anchoring the Φ window to it.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/spiral/scan', {
        symbol: selectedSymbol,
        nearTermAnchorBarIndex: anchor.index,
        // Walk-forward: only turns at or before the clicked bar may cast a ladder. Without this the
        // zones just after the anchor would be assembled partly from turns that had not happened
        // yet at that bar, so "the projection landed on a later pivot" would be a restatement of
        // the input rather than a result.
        pivotsUpToAnchorOnly: true,
        // Same forward reach as the "near now" scan, so the two options differ in WHERE they look
        // and not in how far — otherwise a zone found by one and missed by the other would look
        // like a disagreement about the sky rather than about the window.
        nearTermLookaheadDays: lookaheadToLastPaintedBar(
          extendedBarData?.dates,
          barData?.dates,
          isAdmin ? SPIRAL_LOOKAHEAD_DAYS.admin : SPIRAL_LOOKAHEAD_DAYS.user,
        ),
      });
      const data = res.data || {};
      if (data.ok === false) {
        setError(data.error || 'The Φ scan failed.');
        return;
      }
      setSpiralScan(data);
      drawSpiralZone(data);

      // Said explicitly, because the drawing is identical to the "near now" one and nothing else on
      // the chart distinguishes a window centred on a 2015 turn from one centred on today. The
      // backend echoes the bar it actually used, so a clamped index reports where it landed rather
      // than where it was asked for.
      const w = data.nearTermWindow || {};
      const zones = data.nearTermZones || [];

      // Said plainly, because it is the difference between this option and the one above it: the
      // chart was cut at that bar, so a thinner result is a narrower question rather than a quieter
      // market, and pivotCount here is only the turns that were confirmable by then.
      const evidence = typeof data.asOfBarIndex === 'number'
        ? `, treating it as the last bar (${data.pivotCount} turns known by then)`
        : '';

      // The hit rate, which is the question the restriction exists to make askable: of the zones
      // projected PAST the anchor from turns that preceded it, how many landed on a turn that then
      // happened? Counted over forward zones only — a zone before the anchor is inside the data the
      // ladders were cast from, so crediting it would inflate the rate with what was already known.
      const forward = zones.filter(z => z.targetBarIndex > (w.anchorBarIndex ?? anchor.index));
      const landed = forward.filter(z => z.landedOnPivot).length;
      const rate = forward.length > 0
        ? ` ${landed} of ${forward.length} zones after that bar landed on a turn.`
        : '';

      setStatusText(`Φ scan anchored on ${w.anchorDate || anchor.date}`
        + ` (bar #${(w.anchorBarIndex ?? anchor.index) + 1})`
        + `: ${zones.length} zone(s) between ${w.fromDate} and ${w.toDate}`
        + `${evidence}.${rate}`);
    } catch (err) {
      console.error('[App] anchored Φ scan failed:', err);
      setError(err.response?.data?.error || err.message || 'The Φ scan request failed.');
    } finally {
      setLoading(false);
    }
  }, [api, selectedSymbol, extendedBarData, barData, isAdmin, drawSpiralZone, setStatusText]);

  /**
   * The toolbar button: scan, then ask which of the three questions to answer.
   *
   * The scan runs FIRST rather than after the choice, for two reasons. It supplies the turns the
   * picker offers, so the dialog can be populated rather than asking the user to type a date; and
   * it is what the "upcoming zone" option needs anyway, so the common case costs no extra call.
   * On AAPL - 11,500 bars - it returns in about a quarter of a second.
   */
  /**
   * Marks every day where a trine and a hard aspect meet on a shared body.
   *
   * <p>Scans from the chart's first bar to a year past its last, so the forward half is a forecast
   * rather than a description. The scan itself is server-side: a 45-year chart plus a year is
   * ~17,000 days across twelve bodies, which is 200,000 ephemeris evaluations and not something to
   * ask a browser for one day at a time.
   *
   * <p><b>The condition alone is not selective.</b> Measured over 2020-2024 at an orb of 7, 98.4%
   * of days qualify - with twelve bodies there are 66 pairs, and the Moon crosses every angle every
   * month. Requiring the two aspects to be TIGHT is what makes it a marker rather than a
   * highlighter: at a combined orb of 1 degree it is 6.6% of days. Both numbers are reported, so a
   * short list reads as narrowed rather than as a quiet sky.
   */
  const handleGannConfluenceScan = useCallback(async ({ orb, maxCombinedOrb }) => {
    const dates = barData?.dates || [];
    if (!selectedSymbol || dates.length === 0) {
      setError('Load a symbol before running the Critical Timing Scan.');
      return;
    }

    // ── the precomputed timeline ────────────────────────────────────────────────────────────
    //
    // Drawn against the EXTENDED dates when they exist, the same as the live path: the padded axis
    // reaches past the last real bar and the dataset runs to 2100, so the forward half of the scan
    // is the half that gains most from being precomputed.
    if (hasPrecomputedOrb(orb)) {
      setLoading(true);
      setError(null);
      try {
        const rows = await loadCtsDataset(orb);
        if (rows) {
          const source = extendedBarData?.dates?.length ? extendedBarData : barData;
          const index = buildBarIndex(source.dates);
          const { hits, onBar, flagged, snapped, offChart, folded } = reversalBars(
            rows, index, { cooldownBars: ctsCooldownBars });
          // Every flagged bar, BEFORE the fold -- what the column beside each mark reports on.
          const flaggedBars = new Set((onBar || []).map((h) => h.barIndex));

          // The description the dataset cannot carry. One request for the marked days only, so the
          // fast path stays fast; an empty map means it was unavailable and the marks draw bare.
          /*
           * WHEN each marked day was tightest, so classify can sample there.
           *
           * Fetched in PARALLEL with nothing else and awaited before the classification, because
           * the classification is what consumes it. A failure yields an empty map and the
           * classification falls back to midnight -- which describes only the days a daily sample
           * can see, and is exactly the behaviour this replaces.
           */
          const peaks = await loadCtsDetail(orb);
          const byDate = await fetchClassification(api, hits, orb, 'geo-sidereal', peaks);

          // The arrow's direction still comes from the price action at the bar, not from the
          // dataset: the dataset knows the sky and the chart knows the price, and which side of a
          // turn a bar sits on is a question only the chart can answer.
          // `let`, because the trial cutoff narrows it in one place below once the cluster
          // columns have been appended — see there for why that is the only honest point to cut.
          let points = hits.map((hit) => {
            const bar = source.ohlc?.[hit.barIndex] || [];
            const { direction } = pivotDirectionAt(source.ohlc || [], hit.barIndex);
            const up = direction === 'up';
            const price = up ? bar[2] : bar[3];
            if (typeof price !== 'number' || !Number.isFinite(price)) return null;
            const day = dayForHit(hit, byDate);
            return {
              date: source.dates[hit.barIndex],
              price,
              position: up ? 'below' : 'above',
              // The shape, the rotation and BOTH candidate fills, from the one declaration the
              // live path also spreads -- these two builders produce the same mark and drifting
              // apart is the only way they can be wrong. Per POINT, because the direction is a
              // property of the point and not of the overlay: one CTS run carries both.
              ...ctsMarkStyle(up),
              // The date, and nothing else - the same floater the live path shows. Where a weekend
              // snapped onto this bar it names the day the scan fired, not the bar it is drawn on.
              title: hit.dates.join(', '),
              // The same three fields the live path builds, from the same functions, so a bar reads
              // identically whether the run came from the timeline or from a scan.
              ...(indexDigit(day) !== null ? { label: indexDigit(day), labelInside: true } : {}),
              ...(badgeText(day) ? {
                badge: badgeText(day),
                badgeTooltipLines: markerTooltipLines(day, aspectLinesFor(day)),
              } : {}),
              /*
               * WHAT MADE THIS MARK, and how many are coming. The glyph column says that something
               * follows; this says what, without the reader counting pixels -- and for an
               * administrator it leads with the trine and the hard aspect that produced the
               * triangle, which used to be reachable only by hovering the 10px badge chip.
               *
               * One composition function, shared with the live path, so the two producers of a CTS
               * mark cannot disagree about what its card says.
               *
               * ⚠ APPENDED TO `tooltipLines`, which is the field the series actually renders. The
               * first version put it on a datum key of its own and nothing read it -- a correct
               * sentence computed and thrown away, which is the exact shape of defect this file
               * keeps recording. Checked by grepping the renderer rather than by assuming.
               */
              ...((() => {
                /*
                 * THE REASON on the card; EVERYTHING ELSE behind the disclosure.
                 *
                 * Reported as "very verbose and for many bars either the top or the bottom parts
                 * are unreadable" — twelve lines hanging off a mark at the foot of the grid.
                 * Nothing is deleted: `tooltipMoreLines` carries the full description and the
                 * renderer puts it behind a native `<details>`.
                 *
                 * `studyLines` is what the odds effect recomposes the EXPANDED half from, and it
                 * never writes it, which is what keeps that composition idempotent.
                 */
                const clusterLine = clusterTooltipLine(hit.barIndex, flaggedBars, ctsCooldownBars);
                const reason = arrowTooltipLines(day, { aspectBreakdown: isAdmin });
                const detail = arrowDetailLines(day, {
                  aspectBreakdown: isAdmin, clusterLine,
                });
                return {
                  ...(reason.length > 0 ? { tooltipLines: reason } : {}),
                  ...(detail.length > 0
                    ? { studyLines: detail, tooltipMoreLines: detail } : {}),
                };
              })()),
            };
          }).filter(Boolean);

          /*
           * ⚠ THE CLUSTER COLUMNS, APPENDED TO THE SAME OVERLAY rather than drawn as a second one.
           *
           * One overlay means one removal: `removeOverlay(GANN_CONFLUENCE_OVERLAY_ID)` already
           * clears the marks, and a sibling overlay would be a second thing to remember to clear --
           * which is how a stale annotation comes to float beside a chart that no longer has the
           * study on it. The series supports per-point symbol and size, so dots and dashes can
           * travel with the triangles.
           */
          /*
           * ⚠ EACH COLUMN INHERITS ITS OWN TRIANGLE'S PLACEMENT, which is read back off the point
           * that was just built rather than recomputed.
           *
           * Pinned to the rail while the mark followed its price, the column was up to a THOUSAND
           * pixels away from the triangle it describes -- measured at 979px on 1993-02-02 at a
           * 21-bar zoom -- and looked attached only because a zoomed-out price axis puts an
           * early-1990s price within a few pixels of the rail anyway. Reported as "display of the
           * dash-dot stack is not consistent, sometimes it disappears on zooming".
           *
           * Reading the built point is what guarantees they agree: recomputing the price here
           * would be a second answer to "where is this mark", and the two would differ the first
           * time `pivotDirectionAt` or the high/low choice above was touched.
           */
          const markByDate = new Map(points.map((p) => [p.date, p]));
          const clusterPoints = hits.flatMap((hit) => {
            // The date is passed EXPLICITLY: a hit carries `barIndex` and a list of CTS `dates`,
            // never a single `date`, and the renderer drops any point whose date is undefined.
            const date = source.dates[hit.barIndex];
            const mark = markByDate.get(date);
            return clusterGlyphPoints(hit, flaggedBars, ctsCooldownBars, {
              date,
              basePx: CTS_CLUSTER_BASE_PX,
              // Absent on a mark the renderer will put on the rail anyway, which is exactly when
              // the column should stay there too.
              ...(mark ? { price: mark.price, position: mark.position } : {}),
            });
          });
          points.push(...clusterPoints);

          /*
           * ⚠ THE TRIAL CUTOFF, applied to the FINISHED array and nowhere earlier.
           *
           * One filter covers the triangles AND their cluster columns, because a column carries
           * the same `date` as the triangle it hangs off — so filtering here cannot leave a
           * dash-dot stack under a mark that is no longer drawn, which filtering `hits` or
           * `points` separately could.
           *
           * It runs AFTER `markByDate` is built, so a kept mark's columns still inherit their own
           * triangle's placement. And the FOLD counts are untouched on purpose: a glyph says how
           * many signals were folded into the mark beside it, and those neighbours are within the
           * cooldown window of a bar the account may see — they describe the same event.
           */
          const ctsCut = filterToCutoff(points, trialCutoffs.cts);
          points = ctsCut.kept;

          removeOverlay(GANN_CONFLUENCE_OVERLAY_ID);
          if (points.length > 0) {
            addOverlay({
              type: 'pivotMarkers',
              id: GANN_CONFLUENCE_OVERLAY_ID,
              // AT THE FOOT OF THE GRID, not at a price. A CTS point says WHEN a turn is
              // due and nothing about the level -- and these were drawn at the bar's own
              // high or low, which on the replay's forward canvas is a mark at a price
              // nobody has seen. The rail is a fixed 0..1 scale, so "the bottom" survives
              // every zoom. `position` stays on each point: it is the direction channel
              // the Trade Simulator and the odds engine read, and only the DRAWING changes.
              anchor: 'bottom',
              // ⚠ A FAINT VERTICAL UNDER EVERY RAIL MARK, and nothing else changes. Reported as
              // "I do not see future dots on the chart": they are drawn and they are twelve
              // 18px ticks across 250 padded columns at the foot of a grid whose candles are
              // hundreds of pixels above, so the forward half of the study reads as missing.
              // The vertical carries the eye to them. It adds NO label -- the triangles keep
              // exactly the marks they have always had.
              railVerticals: true,
              label: 'CTS',
              symbol: 'triangle',
              symbolSize: CTS_SYMBOL_SIZE,
              labelFontSize: 10,
              insideLabelFontSize: CTS_INDEX_FONT_SIZE,
              // The renderer's own default, stated so `CTS_CLUSTER_BASE_PX` derives from
              // something real rather than from a number repeated in two files.
              badgeOffsetPx: CTS_BADGE_OFFSET_PX,
              // Marks the hover card as ASTROLOGICAL, so the Bar Floater's OHLCV mode can suppress
              // it. A flag on the overlay rather than a check on the overlay's id: the id says
              // which study drew it, and what the suppression is about is what the card SAYS.
              astroTooltip: true,
              // The card is the REASON; everything else is behind a disclosure, which needs the
              // pointer to be able to reach it.
              expandableTooltip: true,
              pulse: ctsZoomedRef.current,
              points,
            });
          }
          const described = points.filter(p => p.label || p.badge).length;
          setStatusText(`CTS orb ${orb}° from the precomputed 1801-2100 timeline: `
            + `${points.length.toLocaleString()} bar${points.length === 1 ? '' : 's'} marked`
            + ` from ${flagged.toLocaleString()} reversal days`
            + `${snapped > 0 ? `; ${snapped} snapped to a neighbouring trading day` : ''}`
            + `${offChart > 0 ? `; ${offChart.toLocaleString()} outside the chart` : ''}`
            // ⚠ NAMED, because a reader who sees 1,147 marks from 3,020 reversal days has no
            // way to tell whether the rest were off-chart, on the same bar, or suppressed.
            + `${folded > 0 ? `; ${folded.toLocaleString()} folded by the `
              + `${ctsCooldownBars}-bar cooldown` : ''}`
            + `${ctsCooldownBars === 0 ? '; cooldown OFF, every flagged bar drawn' : ''}`
            // Said out loud rather than left to be noticed. Bare marks look exactly like marks on
            // days that carried nothing, and the difference is whether the description was
            // unavailable or absent.
            + `${described === 0 && points.length > 0
              ? '; classification unavailable, marks drawn unlabelled' : ''}.`
            // ⚠ THE WITHHELD COUNT, or a trial reader cannot tell a cut month from a quiet sky.
            // Two sentences rather than one: a thin scan and an EMPTY one are different findings,
            // and an empty chart beside a count of withheld marks reads as a failed scan.
            + (points.length === 0 && ctsCut.withheld > 0
              ? cutoffEmptyNote('CTS', trialCutoffs.cts, source.dates[0])
              : cutoffNote('CTS mark', ctsCut.withheld, trialCutoffs.cts)));
          setLoading(false);
          return;
        }
        console.warn(`[App] precomputed CTS orb ${orb} unavailable — scanning live`);
      } catch (e) {
        console.warn('[App] precomputed CTS failed, scanning live:', e);
      }
      setLoading(false);
    }

    setLoading(true);
    setError(null);
    try {
      const lastBar = dates[dates.length - 1];
      const end = new Date(lastBar);
      end.setFullYear(end.getFullYear() + 1);

      const res = await api.post('/api/gann-confluence/scan', {
        startDate: dates[0],
        endDate: end.toISOString().slice(0, 10),
        orb,
        maxCombinedOrb,
        zodiac: 'geo-sidereal',
      });
      const data = res.data || {};
      if (data.ok === false) {
        setError(data.error || 'The Critical Timing Scan failed.');
        return;
      }

      // Drawn against the EXTENDED dates when they exist: the scan reaches a year past the last
      // real bar, and those dates only exist on the padded axis. Snapping to barData alone would
      // silently drop every forward hit, which is the half the reader came for.
      const source = extendedBarData?.dates?.length ? extendedBarData : barData;
      // The last TRADED bar, so a forward confluence can be told from a historical one. Without it
      // every forward mark read its direction off a flat placeholder and drew a green or red
      // triangle at the last close - one horizontal line of them in the empty space to the right.
      const built = buildGannArrowPoints(data.days, source,
        { lastRealDate: barData?.dates?.[barData.dates.length - 1],
          // Admin only -- see `arrowTooltipLines`. Passed in rather than read inside the builder,
          // which is a pure function of its arguments and has no way to ask who is looking.
          aspectBreakdown: isAdmin });

      // ⚠ THE SAME CUTOFF AS THE PRECOMPUTED PATH ABOVE, through the same function. These are the
      // two producers of a CTS mark and a reader cannot tell which one drew their chart, so a
      // trial account that saw nine extra months because the precomputed orb happened to be
      // unavailable would be the worst outcome available — and it would look like nothing at all.
      const liveCut = filterToCutoff(built.points, trialCutoffs.cts);

      removeOverlay(GANN_CONFLUENCE_OVERLAY_ID);
      if (liveCut.kept.length > 0) {
        addOverlay({
          type: 'pivotMarkers',
          id: GANN_CONFLUENCE_OVERLAY_ID,
          anchor: 'bottom',   // see the precomputed path above
          railVerticals: true,   // and the same forward findability -- see there
          label: 'CTS',
          symbol: 'triangle',
          symbolSize: CTS_SYMBOL_SIZE,
          labelFontSize: 10,
          insideLabelFontSize: CTS_INDEX_FONT_SIZE,
          // Whether the arrows pulse is a property of the OVERLAY, not something patched onto the
          // chart afterwards. An imperative patch survives only until the next full option rebuild,
          // which any other overlay change triggers - the animation would then stop for no reason
          // the reader could see.
          astroTooltip: true,
          expandableTooltip: true,   // see the precomputed path above
          pulse: ctsZoomedRef.current,
          points: liveCut.kept,
        });
      }

      const qualifying = data.daysQualifying ?? data.days?.length ?? 0;
      // ⚠ RECOUNTED from what is DRAWN, not taken from `built`. `built.up`/`built.down` describe
      // the unfiltered set, so on a trial run the line would have read "12 bars marked (31 up,
      // 24 down)" — three numbers about one scan, two of them about marks nobody can see.
      // `position: 'below'` is the up direction, the channel the whole product reads.
      const drawnUp = liveCut.kept.filter((p) => p && p.position === 'below').length;
      setStatusText(`CTS ${data.startDate}..${data.endDate}: `
        + `${liveCut.kept.length} bar${liveCut.kept.length === 1 ? '' : 's'} marked`
        + ` (${drawnUp} up, ${liveCut.kept.length - drawnUp} down)`
        + ` from ${data.days.length} of ${qualifying} qualifying days`
        + ` (orb ${data.orb}°, combined ≤ ${maxCombinedOrb}°)`
        + `${built.suppressed > 0 ? `; ${built.suppressed} adjacent kept the tighter` : ''}`
        + `${built.offChart > 0 ? `; ${built.offChart} fell outside the chart` : ''}`
        + `${built.truncated > 0 ? `; ${built.truncated} weaker not drawn` : ''}.`
        + (liveCut.kept.length === 0 && liveCut.withheld > 0
          ? cutoffEmptyNote('CTS', trialCutoffs.cts, source.dates[0])
          : cutoffNote('CTS mark', liveCut.withheld, trialCutoffs.cts)));

      // Every run is logged, so two orbs can be compared by scrolling back rather than by
      // remembering. The outcome statistics are measured against the REAL bars, which is why they
      // are computed here and not server-side: the scan knows the sky, the chart knows the price.
      //
      // ⚠ MEASURED OVER WHAT WAS DRAWN. The journal's own line reads "N arrows drawn — X up, Y
      // down", and scoring the unfiltered set would both make that sentence false and put the
      // withheld months back on screen as TEXT — a restriction that holds on the canvas and
      // leaks into the panel beside it is not a restriction. `suppressed`, `truncated` and
      // `offChart` stay as `built` reported them: those are facts about the SCAN, not about
      // which of its marks this account may see.
      const stats = summariseGannRun(liveCut.kept, barData);
      handleStudyReportJournal({
        studyLabel: `CTS — ${selectedSymbol}`,
        reportText: gannJournalLines({
          symbol: selectedSymbol,
          scan: data,
          built: { ...built, points: liveCut.kept, up: drawnUp, down: liveCut.kept.length - drawnUp },
          orb: data.orb,
          maxCombinedOrb,
          stats,
        }).join('\n')
          + (cutoffNote('CTS mark', liveCut.withheld, trialCutoffs.cts)
            ? `\n ${cutoffNote('CTS mark', liveCut.withheld, trialCutoffs.cts).trim()}` : ''),
      });
    } catch (err) {
      console.error('[App] CTS failed:', err);
      setError(err.response?.data?.error || err.message || 'The Critical Timing Scan failed.');
    } finally {
      setLoading(false);
    }
   }, [api, selectedSymbol, barData, extendedBarData, addOverlay, removeOverlay,
     // `isAdmin` gates the aspect breakdown on each triangle's card. Named here because a stale
     // closure would keep whichever role was in force when this callback was last built -- which
     // for a feature that exists only for an administrator is the half that fails silently.
     setStatusText, ctsCooldownBars, isAdmin,
     // The trial cutoff, for the same reason `isAdmin` is named: a stale closure would keep
     // whichever account was in force when this callback was last built, and for a restriction
     // that is the half which fails silently — the scan would simply draw too much.
     trialCutoffs,
      handleStudyReportJournal]);

  const handleSpiralReversalScan = useCallback(async () => {
    if (!selectedSymbol) {
      setError('Load a symbol before running the Φ scan.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // How far past the last bar "near now" reaches, in calendar days.
      //
      // 90 for an admin, 30 for everyone else. The wider window is genuinely more useful and
      // genuinely noisier — the SPY example in the notes has a 6-pivot zone sitting 34 days out,
      // just outside the 30-day default and therefore invisible — but a longer horizon admits more
      // weak convergences, and the six-colour palette starts dropping zones. An admin is the reader
      // equipped to tell those apart.
      //
      // Sent explicitly rather than left to the backend's own default, so the window a user sees is
      // decided by who they are rather than by which end of the wire last changed.
      // ...and it now reaches to the LAST PAINTED BAR rather than stopping at that constant. The
      // canvas draws 250 future bars, about 362 calendar days, so a zone could land on a bar that
      // is visibly on the chart and still fall outside the window deciding what gets a band. The
      // role default remains the floor, and is what study mode uses since it paints nothing ahead.
      const res = await api.post('/api/spiral/scan', {
        symbol: selectedSymbol,
        nearTermLookaheadDays: lookaheadToLastPaintedBar(
          extendedBarData?.dates,
          barData?.dates,
          isAdmin ? SPIRAL_LOOKAHEAD_DAYS.admin : SPIRAL_LOOKAHEAD_DAYS.user,
        ),
      });
      const data = res.data || {};
      if (data.ok === false) {
        setError(data.error || 'The Φ scan failed.');
        return;
      }
      setSpiralScan(data);
      setSpiralParams(data.params || null);
      setSpiralDialogOpen(true);
    } catch (err) {
      console.error('[App] Φ scan failed:', err);
      setError(err.response?.data?.error || err.message || 'The Φ scan request failed.');
    } finally {
      setLoading(false);
    }
    // barData and extendedBarData are dependencies because the lookahead is now READ FROM THEM.
    // Omitted, this callback would keep the arrays it closed over when it was created and compute
    // the horizon from a previous symbol's chart. Both are declared well above this point, so
    // naming them here cannot repeat the temporal-dead-zone crash App.tdz.test.js guards against.
  }, [api, selectedSymbol, isAdmin, barData, extendedBarData]);

  /**
   * Right-click or shift-click on a candle opens the inspection menu for that bar.
   *
   * Position is kept in CLIENT coordinates because the menu is anchored to the pointer - there is
   * no DOM element behind a candle to attach it to.
   */
  const handleBarContextMenu = useCallback((bar) => {
    if (!selectedSymbol) return;
    setSpiralMenu({
      barIndex: bar.dataIndex,
      date: bar.date,
      position: { top: bar.clientY, left: bar.clientX },
    });
  }, [selectedSymbol]);

  const closeSpiralMenu = useCallback(() => setSpiralMenu(null), []);

  /**
   * Runs one inspection and draws it.
   *
   * The scan's own parameters are echoed back, so a trace names the pivots the chart is actually
   * showing. Without that the inspection would silently use defaults and could attribute the band
   * on screen to turns the scan never considered - with nothing visible to say the two disagreed.
   */
  const runSpiralInspection = useCallback(async (mode, barIndex) => {
    if (!selectedSymbol || barIndex == null) return;
    setSpiralMenu(null);
    setLoading(true);
    setError(null);
    try {
      const bodyFor = (m) => {
        const body = { symbol: selectedSymbol, mode: m, barIndex };
        if (spiralParams) {
          body.minWindow = spiralParams.minWindow;
          body.maxWindow = spiralParams.maxWindow;
          body.period = spiralParams.periodName;
          body.scoring = spiralParams.scoring;
          body.tolerance = spiralParams.windowBars;
          body.forecastBars = spiralParams.forecastBars;
        }
        return body;
      };

      // BOTH is two requests, not a third backend mode. The endpoint answers one direction at a
      // time by design — FORWARD may not start from an extrapolated bar while BACKWARD may, and
      // collapsing them server-side would have to pick one of those rules for a combined call.
      //
      // Sent together rather than in sequence: they are independent reads of the same bar, so
      // serialising them would double the wait for no benefit. allSettled, not all — one direction
      // failing must not discard the other's answer, which is the whole reason for asking for both.
      if (mode === 'BOTH') {
        const [fwd, back] = await Promise.allSettled([
          api.post('/api/spiral/inspect', bodyFor('FORWARD')),
          api.post('/api/spiral/inspect', bodyFor('BACKWARD')),
        ]);
        const ok = (r) => {
          const d = r.status === 'fulfilled' ? (r.value?.data || {}) : null;
          return d && d.ok !== false ? d : null;
        };
        const forward = ok(fwd);
        const backward = ok(back);
        if (!forward && !backward) {
          setError('The Φ inspection failed in both directions.');
          return;
        }

        SPIRAL_INSPECT_OVERLAY_IDS.forEach(removeOverlay);
        const source2 = extendedBarData?.dates?.length ? extendedBarData : barData;
        buildCombinedInspectOverlays(forward, backward, source2).forEach(addOverlay);

        // The dialog opens on the LINEAGE when there is one: "which turns relate to this bar" is
        // the question a click on a bar is usually asking, and the forward ladder is drawn on the
        // chart either way. Falls back to whichever half survived.
        setSpiralInspection(backward || forward);

        const parts = [];
        if (forward) {
          const s2 = forward.summary || {};
          parts.push(`${s2.verified} of ${s2.testable} forward rungs landed on a turn`);
        }
        if (backward) {
          parts.push(`${backward.convergenceCount} earlier turn(s) point at it`);
        }
        if (!forward || !backward) {
          parts.push(`the ${forward ? 'backward' : 'forward'} half failed`);
        }
        setStatusText(`Φ both ways from ${(forward || backward).anchor?.date
          || (backward || forward).target?.date || `bar ${barIndex}`}: ${parts.join('; ')}.`);
        return;
      }

      const res = await api.post('/api/spiral/inspect', bodyFor(mode));
      const data = res.data || {};
      if (data.ok === false) {
        setError(data.error || 'The Φ inspection failed.');
        return;
      }

      // Clear the previous inspection first, always - including its projection lines, whose count
      // varies per run. Leaving a longer previous ladder behind would put two answers on the chart.
      SPIRAL_INSPECT_OVERLAY_IDS.forEach(removeOverlay);

      const source = extendedBarData?.dates?.length ? extendedBarData : barData;
      buildInspectOverlays(data, source).forEach(addOverlay);
      setSpiralInspection(data);

      if (mode === 'FORWARD') {
        const s = data.summary || {};
        setStatusText(`Φ forward from ${data.anchor?.date}: `
          + `${s.verified} of ${s.testable} testable rungs landed on a turn`
          + (s.future ? `, ${s.future} still ahead.` : '.'));
      } else {
        setStatusText(`Φ lineage for ${data.target?.date}: `
          + `${data.convergenceCount} earlier turn(s) project onto it.`);
      }
    } catch (err) {
      console.error('[App] Φ inspection failed:', err);
      setError(err.response?.data?.error || err.message || 'The Φ inspection request failed.');
    } finally {
      setLoading(false);
    }
  }, [api, selectedSymbol, spiralParams, barData, extendedBarData, addOverlay, removeOverlay, setStatusText]);

  /**
   * A left-click on a bar, turned into the inspection that bar is asking for.
   *
   * <h2>Which direction</h2>
   * A HISTORICAL bar asks "what led here" — the turns behind it are all knowable, and its own
   * future is already on the chart to be checked against. A CURRENT bar asks the opposite: there is
   * nothing after it yet, so the only useful question is where its ladder points.
   *
   * "Current" is the last real bar give or take the scan's own tolerance, not the last bar exactly.
   * The tolerance is the window within which this study treats two bars as the same event, so
   * inside it "now" and "the last bar" are the same claim — and requiring a pixel-perfect click on
   * one final candle would make the forward case practically unreachable.
   */
  const runSpiralForBar = useCallback((dataIndex, mode) => {
    if (dataIndex == null || !mode) return;
    // FROM asks where this bar points; TO asks what points here. Both are legitimate about any bar,
    // which is why the mode is chosen by the user rather than inferred from where the bar sits.
    runSpiralInspection(mode === 'FROM' ? 'FORWARD' : 'BACKWARD', dataIndex);
    setSpiralDrawn(true);
  }, [runSpiralInspection]);

  /**
   * Read by the bar-click handler, which is deliberately never rebuilt.
   *
   * That handler is pinned across renders — rebuilding it makes echarts-for-react dispose and
   * recreate the whole chart — so anything it closes over is frozen at first render. Reaching the
   * current dispatcher through a ref is how the rest of this file solves the same problem
   * (overlaysRef, onBarSelectRef); calling runSpiralForBar directly would call the version that
   * existed before a symbol was ever loaded.
   */
  const spiralClickRef = useRef(null);
  useEffect(() => {
    spiralClickRef.current = spiralClickMode
      ? (dataIndex) => runSpiralForBar(dataIndex, spiralClickMode)
      : null;
  }, [spiralClickMode, runSpiralForBar]);

  /** Takes every inspection drawing off the chart, without opening anything. */
  const clearSpiralInspection = useCallback(() => {
    SPIRAL_INSPECT_OVERLAY_IDS.forEach(removeOverlay);
    setSpiralInspection(null);
    setSpiralDrawn(false);
    setStatusText('Φ relationships cleared.');
  }, [removeOverlay, setStatusText]);

  /**
   * Dispatches the mode chosen in the dialog.
   *
   * Declared after runSpiralInspection because it calls it — a dependency array naming a const
   * declared further down throws on first render, which is exactly how this file broke once before.
   */
  const handleSpiralModeRun = useCallback(async (mode, pivotBarIndex) => {
    setSpiralDialogOpen(false);
    if (mode === SPIRAL_MODES.UPCOMING) {
      drawSpiralZone(spiralScan);
      return;
    }
    if (mode === SPIRAL_MODES.UPCOMING_AT_BAR) {
      await runSpiralScanAnchoredAt(lastClickedBar);
      return;
    }
    // FORWARD and BACKWARD are the inspect endpoint's own mode names, so they pass straight through.
    runSpiralInspection(mode, pivotBarIndex);
  }, [spiralScan, drawSpiralZone, runSpiralInspection, runSpiralScanAnchoredAt, lastClickedBar]);

  useEffect(() => {
    const chart = eChartRef.current?.getEchartsInstance?.();
    if (!chart) return undefined;

    const read = () => {
      try {
        const total = (barData?.dates?.length || 0) + (extendedBarData?.dates?.length || 0);
        if (total === 0) return;
        const zoom = (chart.getOption()?.dataZoom || []).find(z => z && z.type === 'inside')
          || (chart.getOption()?.dataZoom || [])[0];
        if (!zoom) return;
        // start/end are percentages; startValue/endValue are indices. Either may be the one
        // present depending on how the zoom was last set, so both are handled rather than assumed.
        const span = Number.isFinite(zoom.startValue) && Number.isFinite(zoom.endValue)
          ? zoom.endValue - zoom.startValue + 1
          : ((Number(zoom.end) - Number(zoom.start)) / 100)
            * ((extendedBarData?.dates?.length || barData?.dates?.length || 0));
        if (!Number.isFinite(span) || span <= 0) return;
        const zoomedIn = span <= CTS_PULSE_MAX_VISIBLE_BARS;
        if (zoomedIn !== ctsZoomedRef.current) {
          ctsZoomedRef.current = zoomedIn;
          setCtsZoomed(zoomedIn);
        }
      } catch (_) { /* a zoom we cannot read is not a reason to break the chart */ }
    };

    read();
    chart.on('dataZoom', read);
    chart.on('restore', read);
    return () => {
      try { chart.off('dataZoom', read); } catch (_) { /* cleanup */ }
      try { chart.off('restore', read); } catch (_) { /* cleanup */ }
    };
  }, [barData, extendedBarData]);

  /**
   * Turns the pulse on and off on an ALREADY DRAWN scan as the reader zooms.
   *
   * <p>updateOverlay rather than remove-and-add: the arrows are the same marks either way, and
   * removing the overlay to re-add it would make them blink out and back on every zoom that
   * crossed the threshold.
   */
  useEffect(() => {
    if (typeof updateOverlay !== 'function') return;
    const drawn = (overlays || []).some(o => o && o.id === GANN_CONFLUENCE_OVERLAY_ID);
    if (drawn) updateOverlay(GANN_CONFLUENCE_OVERLAY_ID, { pulse: ctsZoomed });
    // `overlays` is deliberately absent: this must run when the ZOOM crosses the threshold, not on
    // every overlay change, and it reads the list only to check the scan is on the chart at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctsZoomed, updateOverlay]);

  /**
   * Draws one of the two quick-run cycle studies: shaded intervals, and nothing else.
   *
   * <p>Deliberately NOT routed through addAreaHighlightOverlaysAndMessages, which is what the
   * configurable study uses. That helper also draws boundary markers and horizontal price bands,
   * writes a journal entry and opens the message panel - all correct for a study someone opened a
   * dialog to configure, and all of it noise for a one-click button whose entire output is "these
   * intervals, on the chart". Adding a suppression flag to the shared helper would leave every one
   * of its other callers carrying a branch they never take.
   *
   * <p>Each run replaces its OWN previous bands and leaves the other button's alone, so the two can
   * be read together or separately without one clearing the other.
   */
  /**
   * Draws one cycle study's horizontal bands, for the intervals that have closed.
   *
   * <p>The bars are measured from the UNMASKED series while simulating, and that is safe precisely
   * because only closed intervals are measured: up to the boundary the masked and unmasked series
   * are identical, so reading the original there reads exactly what the chart is showing. Measuring
   * the masked series instead would work too, right up until an interval was half revealed — and
   * then it would quietly average in the flat bars rather than refusing.
   *
   * <p>Removes this run's bands before adding any, so a step in either direction lands on the
   * correct set rather than accumulating.
   */
  /**
   * THE BANDS A CYCLE RUN PRODUCES — the one computation, shared by the renderer and the audit.
   *
   * <p>Extracted so Chart Analytica measures the bands the chart DREW rather than bands of its
   * own. A second derivation would agree with itself whatever the renderer did, which makes
   * every number in the dashboard unfalsifiable by the picture beside it — the failure this
   * repository has paid for twice, in the projected range bands and in PTB's colour.
   *
   * <p>It computes and returns; it draws nothing. `renderCycleBands` below is the only caller
   * that adds overlays.
   *
   * @returns {{bands:Array<object>, visible:Array<object>, pending:Array<object>}}
   */
  const cycleBandsFor = useCallback((run, revealIndex) => {
    const none = { bands: [], visible: [], pending: [] };
    if (!run) return none;
    const source = simulateOriginalRef.current || barData;
    const dates = source?.dates || [];
    if (dates.length === 0) return none;

    /*
     * Resolved FORWARD — the first bar at or after the date — which is what the vertical pillar
     * does, and the whole of the alignment fix.
     *
     * <p>A cycle interval is astronomical: its ends fall on calendar days, and a calendar day is
     * a Saturday about a seventh of the time. An exact `indexOf` drops every such interval
     * SILENTLY -- measured here, 13 of 21 on a Long Cycle run -- so a snap is required. WHICH
     * snap was the bug.
     *
     * <pre>
     *                       a SATURDAY start resolves to
     *   pillar   findDateIndex(…, 'forward')        MONDAY
     *   band     alignToBar  (NEAREST, ties later)  FRIDAY   <- one bar early
     * </pre>
     *
     * <p>Saturday is one day after Friday and two before Monday, so "nearest" picks Friday while
     * "at or after" picks Monday. The horizontal band therefore began one bar to the LEFT of the
     * pillar it belongs to, on roughly every interval whose start fell on a Saturday — and
     * cleanly on the others, which is what makes it read as an intermittent rendering fault
     * rather than as a rule.
     *
     * <p>The comment that used to sit here said this WAS the snap the vertical shading uses. It
     * was not, and nothing checked it; `cycleBandAlignment.test.js` does now.
     *
     * <p>`indexAtOrAfter` rather than `findDateIndex` itself: identical semantics on an ascending
     * axis (a test asserts that over a weekend-holed one), binary search rather than a linear
     * scan plus a full sort per miss, and already the resolver the replay's own band overlay
     * uses. The `alignToBar` fallback is for a date PAST the last real bar, where "at or after"
     * has no answer and dropping the interval would lose a band that is drawn today.
     */
    const barIndex = buildBarIndex(dates);
    const resolve = (d) => {
      const forward = indexAtOrAfter(dates, String(d || '').split('T')[0]);
      return forward === -1 ? alignToBar(barIndex, d) : forward;
    };
    const intervals = intervalIndices(run.pairs, resolve);
    const { visible, pending } = gateIntervals(intervals, revealIndex);
    if (visible.length === 0) return { bands: [], visible, pending };

    /*
     * THE TRIAL CUTOFF AS A BAR INDEX, and it is deliberately NOT fed to `gateIntervals`.
     *
     * That function's withheld set is reported by `gateSummary` as "waiting for the interval to
     * close", which is true of a replay boundary and false of an entitlement — a trial reader
     * would be told their account's limit was a timing matter that would resolve itself. Two
     * reasons, two mechanisms, two sentences.
     *
     * `indexAtOrBefore`, not `indexAtOrAfter`: the cutoff is the LAST column that may be drawn
     * on, so when it falls on a weekend the answer is the Friday before it rather than the Monday
     * after. Snapping forward would draw two days past a boundary described to the day.
     *
     * -1 — a cutoff earlier than the whole axis — would pass `Number.isFinite` and clip every
     * band to column zero, which renders as nothing at all with no reason given. It becomes 0
     * instead, which `buildCycleBands` already refuses as a zero-width rectangle, and the caller
     * says so in words.
     */
    const cycleCutoff = cutoffForCycle(trialCutoffs, run.key);
    const cutoffIndex = cycleCutoff ? indexAtOrBefore(dates, cycleCutoff) : -1;
    const trialClip = cycleCutoff ? Math.max(0, cutoffIndex) : null;

    // MEASURED over the interval, DRAWN to the next one — see buildCycleBands. The PADDED dates
    // go in here and only here: `dates` above is the real series, which is what the intervals
    // must resolve against, while the projection needs a future slot to land in.
    const projectionDates = extendedBarData?.dates?.length ? extendedBarData.dates : dates;
    const bands = annotateBandCongestion(
      buildCycleBands(visible, intervals, projectionDates, source.ohlc || [],
        {
          projectLastTo: dates.length - 1 + FUTURE_PROJECTION_BARS,
          // NO BAND PAST THE PLAYHEAD. `gateIntervals` already withholds an interval that is not
          // fully revealed, which is not the same thing: a VISIBLE interval's band still runs to
          // the next cycle's start, and the last one projects a year forward -- both straight
          // into masked canvas. A band is a horizontal mark and may not be drawn over a column
          // with no price. Null outside a replay, where the projection is the point.
          //
          // ⚠ ...AND NO BAND PAST A TRIAL CUTOFF, through the same bound. Two unrelated reasons a
          // column may not be drawn on, so the NARROWER wins. This is what catches the FORWARD
          // PROJECTION: the pairs are clamped where they arrive, so no interval reaches past the
          // cutoff — but the final band still projects `FUTURE_PROJECTION_BARS` past the last
          // real bar on its own, which is a year of the one level a reader trades against drawn
          // straight through the withheld window.
          clipRightTo: narrowestBound(revealIndex, trialClip),
        }));
    return { bands, visible, pending };
  }, [barData, extendedBarData, trialCutoffs]);

  const renderCycleBands = useCallback((run, revealIndex) => {
    if (!run) return { visible: [], pending: [] };
    const prefix = cycleBandIdPrefix(run.key);
    (overlaysRef.current || []).forEach((o) => {
      if (o && typeof o.id === 'string' && o.id.startsWith(prefix)) removeOverlay(o.id);
    });

    const { bands, visible, pending } = cycleBandsFor(run, revealIndex);
    if (visible.length === 0) return { visible, pending };

    // THE CYCLE'S OWN COLOUR, not the planet's. `bandColorsForPlanet(run.colorKey)` gave PTB
    // Mercury's amber -- the hue that means PTA in the replay, the CTS pillars and everywhere
    // else -- so a PTB run painted the chart in PTA's colour and read as both having been
    // drawn. See utils/cycleColors: which body a cycle follows is a property of the study,
    // and which colour it is drawn in is a property of being PTA or PTB.
    const { fill, stroke } = cycleColors(run.key);
    // PTA for the long cycle and PTB for the short one, the same mapping the odds effect uses.
    const code = run.key === 'long' ? 'PTA' : 'PTB';

    bands.forEach((band) => {
      addOverlay({
        type: 'drawRect',
        id: `${prefix}${band.date1}`,
        // THE SAME GROUP AS THIS CYCLE'S PILLARS, derived from the key rather than taken from
        // the context's per-tick batch. This function runs in two different ticks — once from the
        // click and again from the reveal effect — so an arrival-time group would put the bands
        // in a batch of their own and undo would separate them from the pillars they belong to.
        groupId: cycleGroupId(run.key),
        date1: band.date1,
        price1: band.price1,
        date2: band.date2,
        price2: band.price2,
        colorRole: run.key === 'long' ? 'ptaHorizontal' : 'ptbHorizontal',
        borderColor: band.isCongested ? lightenStroke(stroke) : stroke,
        /*
         * NO BORDER ON THE RECTANGLE, which is what removes the vertical caps.
         *
         * A markArea takes ONE border for all four sides, so a bordered channel came with a
         * heavy vertical at the pillar it starts on and the one it ends on. Neither is a
         * reading: the left edge is already marked by the vertical pillar standing there, and
         * the right edge is the next pillar. The measured TOP and BOTTOM come back as lines
         * just below, at the same weight the border had.
         */
        borderWidth: PROJECTION_BORDER_WIDTH,
        borderType: band.borderType,
        // A touch richer than the projections' fill, which is the second half of the same
        // distinction. `boostAlpha` returns the colour untouched if it is not an rgba() string,
        // so a reader's configured hex keeps the hierarchy through the stroke alone rather than
        // through a guess at a format it was not given.
        color: boostAlpha(fill),
        label: '',
      });
    });

    /*
     * THE PROJECTED RANGE, one band-height above and one below each cycle band.
     *
     * <p>The reading is that a cycle establishes a range, and the ranges immediately above and
     * below it are where price is expected to travel while that cycle is in force — so they are
     * drawn as companion channels over the same stretch, from this pillar to the next.
     *
     * <p>SAME FILL, NO STROKE. The fill is the band's own, not a lightened one: the three tiers
     * are meant to read as one cohesive structure rather than as a measurement with two hints
     * beside it. The stroke is dropped because an outline is what says "this edge was measured",
     * and these two were derived — the cycle band keeps its own, which is what still marks the
     * measured range.
     *
     * <p>Their ids extend the band's own, so the prefix sweep at the top of this function clears
     * them with it, and they carry the SAME groupId so an undo takes the whole figure rather
     * than leaving two orphaned rectangles where a cycle used to be.
     */
    /*
     * THE HORIZONTAL LINES: the channel's own two edges, its midline, and the excursion bounds.
     *
     * <p>ONE overlay for the whole run rather than one per line. 140 bands at seven lines each
     * is 980 lines, and an overlay apiece would be 980 entries in the overlay list and 980
     * ECharts series -- the renderer puts them all on a single series' markLine.
     *
     * <p>Added BEFORE the projections so the overlay list reads channel, lines, projections --
     * and drawn at z 2, under the price, because they are a background to audit against rather
     * than a mark competing with the candles.
     */
    const guides = cycleGuideLines(bands, CHANNEL_BORDER_WIDTH, GUIDE_LINE_WIDTH);
    if (guides.length > 0) {
      addOverlay({
        type: 'cycleLines',
        id: `${prefix}guides`,
        groupId: cycleGroupId(run.key),
        colorRole: run.key === 'long' ? 'ptaHorizontal' : 'ptbHorizontal',
        color: stroke,
        lines: guides,
        label: '',
      });
    }

    /*
     * ── THE CLICKABLE HANDLE, one per channel ────────────────────────────────────────────
     *
     * A diamond at each band's equator, carrying `simSignal`, which is what opens the
     * Interactive Trade Simulator on that element.
     *
     * A MARKER rather than the rectangle itself. The channel is a `markArea`, whose click
     * event carries a coord pair rather than a datum — so the existing hook, which reads
     * `params.data.simSignal`, cannot see one. Reusing the proven path costs a mark and buys
     * the whole chain: the claiming flag, the refusal on a second selection, the provider
     * lookup. It also gives the reader something to AIM at, which a 900-pixel-wide rectangle
     * with no handle does not.
     *
     * At the EQUATOR, because that is the one level inside the channel that is neither edge,
     * so the handle cannot be mistaken for the Top or Bottom line it would otherwise sit on.
     *
     * ── CONTRAST ──────────────────────────────────────────────────────────────────────────
     *
     * The handle used to take `stroke`, the band's own full-strength colour, which put it in
     * the same hue as the equator it sits on, the fill it sits inside and the projections
     * either side. `cycleHandleColors` splits that: a high-contrast CORE for visibility and a
     * RIM in the band's hue for identity. The measurements are in that module, because the
     * choice follows from the canvas the mark is drawn on rather than from taste.
     *
     * THE MODE IS PASSED, and it decides the core: the same 0.28 band fill composites to a PALE
     * tint on the light canvas and a DARK one on charcoal, so the colour that stands out against
     * it is the opposite in each. Omitting the argument resolves to light, which is what this
     * call did before the theme existed — so the failure mode of forgetting it is the old
     * behaviour rather than an exception.
     */
    const sections = sectionsFrom(bands, (barData?.dates?.length || 1) - 1, code);
    const handleInk = cycleHandleColors(run.key, themeModeRef.current);
    const handles = sections.map((section) => ({
      date: section.date1,
      price: section.equator,
      position: 'above',
      offsetPx: 0,
      symbol: 'diamond',
      color: handleInk.fill,
      borderColor: handleInk.border,
      // The rim follows the reader's configured band colour when there is one, so a handle never
      // wears a hue its own channel is not drawn in.
      borderColorRole: run.key === 'long' ? 'ptaHorizontal' : 'ptbHorizontal',
      borderWidth: CYCLE_HANDLE_BORDER_WIDTH,
      // THE WHOLE SECTION travels, because the card's rows are built by the provider adapter
      // from it. Extracting a few fields here would put the adapter's contract in App, which
      // is the seam this architecture exists to keep out of this file.
      simSignal: {
        provider: code,
        element: {
          ...section,
          provider: code,
          label: `${code} ${section.date1}`,
        },
      },
    }));
    if (handles.length > 0) {
      addOverlay({
        type: 'pivotmarkers',
        id: `${prefix}handles`,
        groupId: cycleGroupId(run.key),
        // NO `colorRole` on the overlay: every point carries its own fill and its own rim, and an
        // overlay-level role would be the fallback for neither. Leaving it would read as though
        // the handle took the band's configured colour, which is exactly what it must not do.
        color: handleInk.fill,
        symbolSize: CYCLE_HANDLE_SIZE,
        points: handles,
        label: '',
      });
    }

    cycleRangeProjections(bands).forEach((projection) => {
      addOverlay({
        type: 'drawRect',
        id: `${prefix}${projection.sourceDate}-${projection.side}`,
        groupId: cycleGroupId(run.key),
        date1: projection.date1,
        price1: projection.price1,
        date2: projection.date2,
        price2: projection.price2,
        color: fill,
        colorRole: run.key === 'long' ? 'ptaHorizontal' : 'ptbHorizontal',
        // Borderless, which the renderer honours only because it stopped hardcoding 1.5, and
        // named rather than written as a bare 0 so "the outside bands keep their weight" is a
        // statement one edit can keep true of both renderers.
        borderWidth: PROJECTION_BORDER_WIDTH,
        borderColor: 'transparent',
        label: '',
      });
    });
    return { visible, pending };
  // `cycleBandsFor` carries the barData/extendedBarData dependency, which is why neither is
  // named here any more: this callback no longer reads either directly.
  // `barData` joins the list for the handles' own axis bound. `cycleBandsFor` already carries
  // it transitively; naming it keeps the dependency honest rather than relying on that.
  }, [cycleBandsFor, addOverlay, removeOverlay, barData]);

  /**
   * Re-renders every drawn cycle study's bands whenever the reveal boundary moves.
   *
   * <p>This is the whole of the reactive half: the click handler and the arrow keys already move
   * the boundary, and this watches it. Bands appear as intervals close and vanish again on a step
   * back, with no further wiring in either of those paths.
   */
  useEffect(() => {
    const runs = Object.values(cycleRuns);
    if (runs.length === 0) return;
    const boundary = simulateMode ? simulateReveal : null;
    const said = runs.map((run) => {
      const { visible, pending } = renderCycleBands(run, boundary);
      return gateSummary(run.label, visible, pending);
    });
    // Appended to whatever the step just said, rather than replacing it. A band appearing is the
    // point of stepping forward, and a reader who cannot see one needs to know whether it is
    // suppressed or simply absent - those look identical on a chart. Functional update because the
    // step's own message is set in the same tick.
    if (simulateMode && said.length > 0) setStatusText(prev => `${prev} ${said.join(' ')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulateReveal, simulateMode, cycleRuns]);

  /*
   * A THEME CHANGE REDRAWS THE DRAWN CYCLES, because one of their colours is theme-dependent.
   *
   * <p>The handle's core is chosen by contrast against the band's fill over the CANVAS, so the
   * same diamond is ink on the light theme and paper on the dark one. Overlays are added once,
   * when a cycle is run — so without this, flipping the theme would leave every existing handle
   * in the colour chosen for the other canvas, which is precisely the invisible mark the core
   * exists to prevent. A reader would see it as the handles vanishing on one theme.
   *
   * <p>Quiet, and separate from the reveal watcher above for that reason: that one appends a band
   * summary to the status line, which belongs to a step of the replay and not to a theme switch.
   * It also skips the very first run — on mount there is nothing drawn yet, and the watcher above
   * owns the initial draw.
   */
  const lastThemeForBands = useRef(mode);
  useEffect(() => {
    if (lastThemeForBands.current === mode) return;
    lastThemeForBands.current = mode;
    const runs = Object.values(cycleRuns);
    if (runs.length === 0) return;
    const boundary = simulateMode ? simulateReveal : null;
    runs.forEach((run) => renderCycleBands(run, boundary));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleRunCycleBand = useCallback(async (cycle) => {
    if (!cycle) return;
    const dates = barData?.dates || [];
    if (!selectedSymbol || dates.length === 0) {
      setError('Load a symbol first.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // PLANET IN SIGN, heliocentric tropical — not the retrograde/direct study these buttons ran
      // before. Everything the study needs is on the button (see cycleToolbarButtons.js), so this
      // call names no planet, sign or frame of its own: one place for the three, and the frame is
      // the one that fails quietly if a second place disagrees, since `helio-sidereal` is a valid
      // frame returning different dates rather than an error.
      //
      // Worth knowing when reading a chart drawn from these: the intervals are now REGULAR. A
      // planet crosses a 30-degree sign once per heliocentric orbit, so Venus gives about eight
      // windows of ~19 days in five years and Mercury about twenty of ~10 days. The retrograde
      // windows they replaced were irregular in both length and spacing.
      // ── HOW FAR THE BANDS ARE DRAWN ──────────────────────────────────────────────────────
      //
      // To the end of the EXTENDED axis, not the last traded bar, and that is the fix for bands
      // that stopped short. `dates` is barData - the traded series - so the study was only ever
      // ASKED about history: every interval in the padded future was missing because nobody
      // requested it, not because the renderer declined to draw it.
      //
      // Asking further is legitimate rather than a guess. A planet crossing a 30-degree sign is
      // deterministic ephemeris, computable arbitrarily far ahead, so an interval returned for a
      // future date is as real as one returned for 1998.
      //
      // ⚠ THE TRIAL CAP IS NOT HERE, and the TODO that used to sit here said it would be.
      //
      // It predicted "replace `drawHorizon` with min(subscriptionEndDate, last extended date) and
      // nothing else here has to change", and the second half was right while the first was not:
      // the cap is applied to the PAIRS that come back rather than to the range they are asked
      // for, a few lines below. Two reasons, both found by trying it the predicted way — this
      // request would then carry an end before its start on any chart shorter than the cutoff,
      // and the straddling-interval case would rest on an unstated assumption about what
      // `/planet-in-sign` does with its own range end. See `clampPairsToCutoff` for both.
      //
      // Still open, and still one value in one place when it lands: the cutoff is currently
      // derived from the last traded bar (CTS 1 month, PTB 5, PTA 9). A per-company
      // "Subscription End Date" would be a second input to `trialCutoffDates` and nothing in
      // this function would move.
      const horizonSource = extendedBarData?.dates?.length ? extendedBarData.dates : dates;
      const drawHorizon = horizonSource[horizonSource.length - 1];

      const res = await api.post('/api/area-highlight-study/planet-in-sign', {
        planet: cycle.subject,
        sign: cycle.sector,
        zodiac: cycle.frame,
        startDateTime: dates[0],
        endDateTime: drawHorizon,
        symbol: selectedSymbol,
      });
      const returned = Array.isArray(res.data?.datePairs) ? res.data.datePairs : [];

      /*
       * ⚠ THE TRIAL CUTOFF, applied to the INTERVALS the moment they arrive — which is what the
       * TODO above predicted would be the one place it has to go.
       *
       * Everything this function and the two effects below it draw is derived from `pairs`: the
       * vertical pillars here, and — through `setCycleRuns` — the price bands, the guide lines,
       * the channel handles, the projections, the reveal watcher's redraw and Chart Analytica's
       * own `cycleBandsFor` call. Clamping once, here, bounds all of them. A filter at the
       * renderer would bound the drawing and leave `cycleRuns` holding intervals the audit would
       * then measure.
       *
       * ⚠ AND NOT BY CAPPING `endDateTime` ON THE REQUEST, which was the obvious alternative and
       * is worse in two ways. It would depend on how `/planet-in-sign` treats an interval
       * straddling its range end, which is a backend behaviour this file would be silently
       * assuming; and on a chart whose history is shorter than the cutoff the cutoff precedes
       * `dates[0]`, so the request would carry an end BEFORE its start — a 400, or worse a
       * quietly inverted range. The browser-side clamp handles that case by dropping every pair,
       * which is the honest answer, and says so.
       */
      const cutoff = cutoffForCycle(trialCutoffs, cycle.key);
      const clampedPairs = clampPairsToCutoff(returned, cutoff);
      const pairs = clampedPairs.pairs;

      // Cleared by id prefix rather than by remembering what was added last: a previous run's count
      // is not knowable here, and a leftover band from it would read as part of this one.
      const prefix = cyclePillarIdPrefix(cycle.key);
      (overlays || []).forEach((o) => {
        if (o && typeof o.id === 'string' && o.id.startsWith(prefix)) removeOverlay(o.id);
      });

      // The CYCLE's hue, for the reason renderCycleBands states: these pillars and those bands
      // are one figure, and taking the pillar's colour from the planet while the band takes it
      // from the category would make the two halves of one cycle different colours.
      const color = cycleColors(cycle.key).pillar;
      let drawn = 0;
      // WHAT IS NOT DONE HERE, deliberately: the last interval is NOT stretched to the horizon.
      //
      // If the study says the cycle closes eleven days before the axis ends, the band ends there.
      // Stretching it would shade eleven days of "Venus in Sagittarius" on dates Venus is not in
      // Sagittarius - a fabrication the chart would present in the same pen as the ephemeris, and
      // indistinguishable from it. The bands now reach the end of the axis because the INTERVALS
      // do, which is the only version of "all the way to the end" that is true.
      pairs.forEach((pair, i) => {
        // pairDates, not a field read: this endpoint answers with two-element ARRAYS where the one
        // these buttons used to call answered with objects, and reading the fields directly
        // discarded every interval without erroring.
        const [start, end] = pairDates(pair);
        if (!start || !end) return;
        addOverlay({
          type: 'areaHighlight',
          id: `${prefix}${i}`,
          // See cycleGroupId: the pillars and this cycle's price bands are ONE undoable unit, and
          // they are added in different ticks, so the group cannot come from arrival time.
          groupId: cycleGroupId(cycle.key),
          date1: start,
          date2: end,
          color,
          // WHAT THIS BAND IS, so the renderer can colour it from the customer's configuration.
          // The role travels on the overlay rather than the colour being resolved here: resolved
          // here it would be frozen at the moment the study ran, and a later Save would leave every
          // already-drawn band behind.
          colorRole: cycle.key === 'long' ? 'ptaVertical' : 'ptbVertical',
          // Empty, not absent: the renderer writes `overlay.label || ''` onto the band, so an empty
          // string is what leaves the shading clean. Any text here prints across the chart.
          label: '',
        });
        drawn++;
      });

      // ── the horizontal price bands ──────────────────────────────────────────────────────
      //
      // The vertical shading says WHEN; these say what price did while it lasted. Deferred to
      // renderCycleBands, which is also what the reveal watcher calls — so a band drawn now and one
      // drawn three steps later go through exactly one path, and cannot disagree about the rule.
      //
      // The run is remembered first, because the effect that watches the boundary redraws from it.
      const run = { key: cycle.key, label: cycle.label, colorKey: cycle.colorKey, pairs };
      setCycleRuns(prev => ({ ...prev, [cycle.key]: run }));
      const { visible, pending } = renderCycleBands(run, simulateMode ? simulateRevealRef.current : null);

      // The status line only. No journal entry and no message panel: the button's whole output is
      // the drawing, and a panel opening over it would be the opposite of what it is for.
      // The horizon is named in the status line, because "why does the shading stop there" is the
      // question this feature generates and the answer is a date the reader cannot otherwise see.
      /*
       * ⚠ THE HORIZON NAMED IN THE STATUS LINE IS THE ONE THAT APPLIED.
       *
       * `drawHorizon` is what was ASKED for; a trial account's shading stops at the cutoff
       * instead. "Shaded to 2027-10-29" over a chart that stops nine months earlier is the
       * question this feature generates answered with the wrong date — and it is the kind of
       * wrong that reads as a rendering fault rather than as a rule.
       */
      const appliedHorizon = cutoff && cutoff < drawHorizon ? cutoff : drawHorizon;
      setStatusText(`${cycle.label}: ${drawn} interval${drawn === 1 ? '' : 's'} shaded to `
        + `${appliedHorizon}. ${gateSummary(cycle.label, visible, pending)}`
        // Two sentences again, for the reason the CTS path states: an empty study and a narrowed
        // one are different findings, and on a chart shorter than the nine-month PTA window
        // every interval is dropped and nothing is drawn at all.
        + (drawn === 0 && clampedPairs.dropped > 0
          ? cutoffEmptyNote(cycle.label, cutoff, dates[0])
          : cutoffNote(`${cycle.label} interval`, clampedPairs.dropped, cutoff)));
    } catch (err) {
      console.error(`[App] ${cycle.label} failed:`, err);
      setError(err.response?.data?.error || err.message || `${cycle.label} failed.`);
    } finally {
      setLoading(false);
    }
  }, [api, selectedSymbol, barData, extendedBarData, overlays, addOverlay, removeOverlay,
    // `trialCutoffs` for the reason the CTS scan names it: a stale closure would clamp against
    // whichever account was in force when this callback was last built, and a restriction that
    // fails open leaves nothing on screen to say so.
    setStatusText, renderCycleBands, simulateMode, trialCutoffs]);

  /**
   * What the header shows: the override, else the brand for the customer's own market.
   *
   * <p>Falling back to the MARKET brand rather than to a constant is what makes Reset correct for
   * an India customer as well as a US one - a hardcoded default would quietly rebrand them.
   */
  /**
   * THE RESOLVED MANIFEST. One object naming what this session's product is called, what its
   * watermark says, and which tools it carries.
   *
   * <p>⚠ It supersedes `nonAdminBrandFor(productTarget)` for the header, and that expression is
   * worth reading before it goes: it was added because the previous code read a bare
   * `NON_ADMIN_BRAND` constant, so every customer on every build — integralDelta included — saw
   * the astro title. The manifest answers the same question from the same product id, and
   * additionally answers it for an ADMIN, which that helper never did.
   */
  const manifest = useMemo(
    () => resolveInitialManifest(
      typeof window !== 'undefined' ? window.location.hostname : '',
      isAdmin,
      readStoredPreview(isAdmin),
    ),
    [isAdmin],
  );

  /**
   * What the header shows.
   *
   * <p>⚠ THE ADMIN OVERRIDE STILL WINS, and it has to. The rename box writes a personal nickname
   * for the title and an administrator who set one must keep it — a manifest that outranked it
   * would make that dialog a control with no effect, which is the defect this file records three
   * times under its own name.
   */
  const headerBrand = isAdmin
    ? (brandOverride || manifest.brandName)
    : manifest.brandName;

  /**
   * WHAT A CUSTOMER'S HEADER SAYS, and the three things that follow from it.
   *
   * <p>`AstroStockCharts.Com` exactly — no market brand, no personal override, and no ` - SPY`
   * suffix, because "strictly" was the word in the requirement and the symbol is already on the
   * chart, in the symbol picker and in the tab's own title a moment later.
   *
   * <p>So the rename control goes too: it is the title that is being renamed, and a dialog that
   * writes an override nothing displays would be a setting with no effect. The click handler, the
   * keyboard shortcut and the dialog are each gated rather than just the first — one of them left
   * live is a control a reader can still reach and then watch do nothing.
   *
   * <p>The chart WATERMARK is deliberately untouched. It is a provenance mark on an exported
   * image and follows the customer's own market, which is a different claim from what the header
   * calls the product to someone who already has it open.
   */
  const brandIsEditable = isAdmin;

  /**
   * The active symbol beside the brand, used by BOTH header branches below.
   *
   * <p>One element so a customer and an admin see the same thing, and its own module so it can be
   * RENDER-tested: every other test of this header reads App.js as text, and a source grep cannot
   * see whether an element actually appears. {@link HeaderActiveSymbol} carries the styling
   * reasoning, including why `text.secondary` is invisible on an `AppBar color="primary"`.
   */
  const activeSymbolLabel = <HeaderActiveSymbol symbol={selectedSymbol} />;

  /**
   * The watermark.
   *
   * <p>Follows the PRESET, not the rename box. A watermark is a provenance mark on an image people
   * export, so a personal nickname there would misattribute the chart — but the short preset is a
   * different product configuration rather than a nickname, and it carries its own mark.
   *
   * <p>That mark is the DOMAIN rather than the header's name: the header names the product to
   * someone who already has it open, and the watermark has to name it to someone looking at an
   * exported picture of it.
   */
  /*
   * ⚠ THE PRESET STILL OUTRANKS THE MANIFEST, deliberately.
   *
   * The short-bar preset is a different product CONFIGURATION rather than a nickname, and it
   * carries its own mark. On the astro product the two now agree — both are `Dhanabhyas` — so the
   * branch is a no-op there and is kept for integralDelta, where they differ.
   */
  const displayWatermark = effectiveBarMode === SHORT ? SHORT_BAR_WATERMARK : manifest.watermark;

  /*
   * The browser tab, kept in step with the header. Same brand, same symbol, one source.
   *
   * ⚠ IT NO LONGER BRANCHES ON `brandIsEditable`. It used to, because the customer header carried
   * no symbol and a tab reading "integralDelta.ai - SPY" over a header reading
   * "AstroStockCharts.Com" would have put the other brand in the one place a reader keeps in view
   * with the window minimised. Both halves of that have changed: the brand already comes from
   * `headerBrand`, which is per-product, and the header now shows the symbol for a customer too.
   * Leaving the condition here would recreate the divergence it was written to prevent, in the
   * other direction — a header reading "…| SPY" over a tab that does not.
   *
   * The separator stays a hyphen rather than the header's `|`: this is a window title, where a
   * pipe reads as path syntax and is what a browser truncates around.
   */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = selectedSymbol
        ? `${headerBrand} - ${String(selectedSymbol).toUpperCase()}` : headerBrand;
    }
  }, [headerBrand, selectedSymbol]);

  /**
   * Ctrl+Shift+B opens the rename dialog.
   *
   * <p>NOT Ctrl+Shift+R, which is the browser's hard reload. Taking it would mean either failing to
   * open the dialog when the browser wins, or breaking hard reload when we do - and hard reload is
   * how a stale bundle gets recovered, which is worse to lose than a shortcut is to move.
   *
   * <p>Ignored while focus is in a field, so typing a capital B into the symbol box does not open a
   * dialog over what is being typed.
   */
  useEffect(() => {
    // Not bound at all for a customer, rather than bound and ignored: an unbound key does
    // nothing and a bound one that returns early is a shortcut somebody will report as broken.
    if (!brandIsEditable) return undefined;
    const onKey = (e) => {
      if (!e.ctrlKey || !e.shiftKey || (e.key || '').toLowerCase() !== 'b') return;
      const el = document.activeElement;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return;
      e.preventDefault();
      setBrandDialogOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [brandIsEditable]);

  /**
   * PRESENTATION MODE: the toolbar goes away and the canvas is the whole screen.
   *
   * <h2>Hidden, not unmounted</h2>
   *
   * The toolbar's `Paper` takes `display: none` rather than being conditionally rendered. TabPanel
   * owns the Admin Panel, the dialogs and a good deal of state; unmounting it to tidy a screenshot
   * would throw all of that away and rebuild it on the way back.
   *
   * <p>The dialogs stay reachable regardless, because MUI portals them to `document.body` — they
   * are not DOM descendants of the hidden Paper.
   *
   * <h2>ESC is not a convenience, it is the way out</h2>
   *
   * With the toolbar hidden there is no button to press, and a deliberately invisible chrome with
   * one undiscoverable exit is a trap. So BOTH `P` and `Escape` leave, and the Admin Panel's entry
   * names the shortcut. It is also not persisted: a reload always comes back with the toolbar, which
   * is the last resort that cannot be forgotten.
   *
   * <p>Admin only, and bare `P` only — any modifier is left alone so Ctrl+P still prints.
   */
  const [presentationMode, setPresentationMode] = useState(false);

  /** Deep Pivot Research. Admin only, and gated again inside the dialog and on the server. */
  const [deepPivotOpen, setDeepPivotOpen] = useState(false);
  const [planetaryAnalogOpen, setPlanetaryAnalogOpen] = useState(false);
  const [pivotCommonalitiesOpen, setPivotCommonalitiesOpen] = useState(false);

  /**
   * Frame the chart on an analog date.
   *
   * <p>A WINDOW either side rather than a single column: an analog is a claim about what the
   * market did around that day, and a chart zoomed to one bar shows none of it.
   *
   * <p>Dispatched the way the Elliott focus already does it — explicit `startValue`/`endValue`,
   * never percentages, because `EChartComponent`'s own datazoom handler records only those two
   * and a percentage dispatch is discarded on the next option rebuild.
   */
  const handleFocusAnalogDate = useCallback((isoDate) => {
    const axis = barData?.dates || [];
    if (!axis.length || !isoDate) {
      setStatusText('No series is loaded, so there is nothing to focus.');
      return;
    }
    const centre = indexAtOrAfter(axis, isoDate);
    if (centre < 0) {
      // ⚠ NOT CLAMPED. An analog outside the loaded series is a real case -- the sky grid goes
      // back further than the price history for many symbols -- and snapping to the last bar
      // would frame a date that has nothing to do with the analog.
      setStatusText(`${isoDate} is outside the loaded price history for ${selectedSymbol}.`);
      return;
    }
    const pad = 120;
    const chart = eChartRef.current?.getEchartsInstance?.();
    if (!chart) return;
    try {
      chart.dispatchAction({
        type: 'dataZoom',
        startValue: Math.max(0, centre - pad),
        endValue: Math.min(axis.length - 1, centre + pad),
      });
      setStatusText(`Framed ${axis[centre]} (bar ${centre + 1} of ${axis.length}) `
        + `with ${pad} bars either side.`);
    } catch (e) {
      console.warn('[App] Could not focus the chart on the analog date:', e);
    }
  }, [barData, selectedSymbol]);

  /** Take the analog marks off the chart. One place, so the dialog's two callers agree. */
  const handleClearPlanetaryAnalogMarks = useCallback(() => {
    removeOverlay(PLANETARY_ANALOG_OVERLAY_ID);
  }, [removeOverlay]);

  /**
   * THE SCAN PAINTS THE CHART, which is what makes this a qualitative instrument at all.
   *
   * <p>The scanner's own measured verdict is that its analogs carry no directional edge. What a
   * table cannot show, and a chart can, is whether an analog tends to land near a turn, inside a
   * consolidation, on a gap — the shape of the thing rather than its mean. So the marks are the
   * deliverable and the table is the index to them.
   *
   * <p>Built against `barData.dates` rather than the padded `extendedBarData`: a projection
   * column is a bar the market has not traded, and an analog snapped onto one would be a mark on
   * a day that does not exist. An analog past the last real bar is dropped and counted.
   *
   * <p>`removeOverlay` FIRST and unconditionally — including when the new scan draws nothing. The
   * panel is MODELESS and the reader is looking at the canvas, so a stale paint under a fresh
   * table is the one failure this arrangement makes easy. The dialog clears on submit as well;
   * both, because the clear here also covers a scan that succeeded and landed nothing on screen.
   */
  const handlePaintPlanetaryAnalog = useCallback((report) => {
    removeOverlay(PLANETARY_ANALOG_OVERLAY_ID);
    const dates = barData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) {
      return 'No series is loaded, so the analogs could not be drawn.';
    }
    const built = buildPlanetaryAnalogOverlay(report, dates);
    if (built.overlay) addOverlay(built.overlay);
    const note = describePlanetaryAnalogPaint({
      matchCount: (report?.matches || []).length,
      bars: built.bars,
      snapped: built.snapped,
      beyondChart: built.beyondChart,
      beforeChart: built.beforeChart,
      truncated: built.truncated,
      targetDrawn: built.targetDrawn,
      collided: built.collided,
    });
    setStatusText(note);
    return note;
  }, [barData, addOverlay, removeOverlay]);

  /** Take the pivot-pair marks off the chart. One place, so every caller agrees. */
  const handleClearPivotCommonalityMarks = useCallback(() => {
    removeOverlay(PIVOT_COMMONALITY_OVERLAY_ID);
  }, [removeOverlay]);

  /**
   * MARK THE TWO PIVOTS a selected row names.
   *
   * <p>The whole point of the study is a claim about TWO BARS, and a table can only name their
   * dates. Drawn, a reader can see at once whether the pair is the swing high and the swing low
   * they would have picked by eye — which is the qualitative check the numbers cannot make.
   *
   * <p>Unlike the analog scanner this does NOT paint automatically on a completed scan: a run
   * produces up to four hundred pairs and there is no single one of them the study is about.
   * Painting the strongest would make the chart answer a question nobody asked.
   *
   * <p>Built against `barData.dates` rather than the padded `extendedBarData`: a pivot is by
   * construction a bar the market traded, so none can fall in a projection column — which makes
   * a date that is NOT on the axis evidence the report belongs to another symbol, reported
   * rather than snapped.
   */
  const handlePaintPivotCommonality = useCallback((pair, match) => {
    removeOverlay(PIVOT_COMMONALITY_OVERLAY_ID);
    if (!pair) {
      const note = describePivotCommonalityPaint({ pair: null });
      setStatusText(note);
      return note;
    }
    const dates = barData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) {
      return 'No series is loaded, so the pivots could not be drawn.';
    }
    // The MATCH travels with the pair, so the chip on the chart names the commonality the
    // reader selected rather than leaving the canvas and the table answering different
    // questions about the same two bars.
    const built = buildPivotCommonalityOverlay(pair, dates, match);
    if (built.overlay) addOverlay(built.overlay);
    const note = describePivotCommonalityPaint({
      pair, match, bars: built.bars, missing: built.missing,
    });
    setStatusText(note);
    return note;
  }, [barData, addOverlay, removeOverlay]);

  /**
   * THE SCAN PAINTS THE CHART. One arrow per tagged pivot, replacing the dialog's data table.
   *
   * <p>Built against `barData.dates` rather than the padded `extendedBarData`: every pivot is a
   * bar the market actually traded, so none of them can fall in the projection columns, and
   * filtering against the padded series would admit a date the price grid has no candle for.
   *
   * <p>`removeOverlay` FIRST, unconditionally — including when the new scan draws nothing. A
   * re-scan with the Moon switched on must not leave the previous run's arrows underneath the new
   * ones, and a scan that finds nothing must clear the chart rather than leave the last answer up
   * looking like the current one.
   */
  const handlePaintDeepPivot = useCallback((report) => {
    removeOverlay(DEEP_PIVOT_OVERLAY_ID);
    const overlay = deepPivotOverlay(report, barData?.dates);
    if (overlay.drawnCount > 0) addOverlay(overlay);
    setStatusText(deepPivotStatus(selectedSymbol, overlay));
  }, [barData, selectedSymbol, addOverlay, removeOverlay]);


  useEffect(() => {
    if (!isAdmin) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const key = (e.key || '').toLowerCase();
      if (key !== 'p' && key !== 'escape') return;
      const el = document.activeElement;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      // Typing a "p" into the symbol box must not blank the chrome.
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return;
      // ESC only ever LEAVES. It is also how a dialog closes, so making it a toggle would drop
      // the toolbar every time somebody dismissed one.
      if (key === 'escape') {
        setPresentationMode((on) => (on ? false : on));
        return;
      }
      e.preventDefault();
      setPresentationMode((on) => !on);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAdmin]);

  // A non-admin must never be left in it -- if the role changes under a session, the chrome comes
  // back rather than leaving someone with no toolbar and no shortcut that works.
  useEffect(() => { if (!isAdmin) setPresentationMode(false); }, [isAdmin]);

  /** Deep Celestial Research — the trine-confluence study. Admin only. */
  const [celestialOpen, setCelestialOpen] = useState(false);
  const [chartEndDatesOpen, setChartEndDatesOpen] = useState(false);

  /**
   * THE SCAN PAINTS THE CHART, one triangle per matched day.
   *
   * <p>Built against `barData.dates` rather than the padded `extendedBarData`: an aspect is
   * snapped FORWARD to the next trading day, and the padded series would offer projection
   * columns as snap targets — putting a mark on a bar the market has not traded.
   *
   * <p>`removeOverlay` FIRST and unconditionally, including when nothing matches. A re-run with
   * a box unticked must not leave the previous selection's marks underneath, and a selection
   * that matches nothing must clear the chart rather than leave the last answer looking current.
   */
  /**
   * THE MIDPOINT TRINE SCAN, run over the loaded range and painted on the rail.
   *
   * <p>⚠ ADMIN ONLY, and the button is not what enforces it. The Admin Panel renders nothing for
   * a non-admin and the toolbar does not offer the panel — both conveniences. The refusal is the
   * backend's 403, because anyone can post to a URL.
   *
   * <p>The range is the CHART'S OWN, read from `barData.dates`, so "scan the active chart" means
   * what it says. The padded `extendedBarData` is deliberately not used for the range or for the
   * snap: its forward columns are projections, and a configuration snapped onto one would be a
   * mark on a bar the market has not traded.
   *
   * <p>Returns the status string rather than only setting it, so the panel can show the outcome
   * in place — it stays open for this one, unlike the tools that hand over to a dialog.
   */
  const handleRunMidpointTrineScan = useCallback(async () => {
    const dates = barData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) {
      return 'Load a symbol before running the midpoint trine scan.';
    }
    // Cleared FIRST and unconditionally. A re-run that finds nothing must clear the chart rather
    // than leave the previous answer standing there looking current.
    removeOverlay(MIDPOINT_TRINE_OVERLAY_ID);
    try {
      const res = await api.post('/api/midpoint-trine/scan', {
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        orb: 1.0,
        zodiac: 'geo-sidereal',
      });
      const data = res.data || {};
      if (data.ok === false) return data.error || 'The midpoint trine scan failed.';
      const built = buildMidpointTrineOverlay(data.hits, dates);
      if (built.overlay) addOverlay(built.overlay);
      const text = describeMidpointTrineScan({
        total: (data.hits || []).length,
        drawn: built.drawn,
        snapped: built.snapped,
        beyondChart: built.beyondChart,
        truncated: built.truncated,
      });
      setStatusText(text);
      return text;
    } catch (err) {
      console.error('[App] midpoint trine scan failed:', err);
      // The 403 is NAMED rather than folded into a generic failure: "you are not an admin" and
      // "the scan broke" are different things to be told, and only one of them is worth retrying.
      if (err.response?.status === 403) {
        return err.response?.data?.error || 'The midpoint trine scan is an administrator tool.';
      }
      return err.response?.data?.error || err.message || 'The midpoint trine scan request failed.';
    }
  }, [api, barData, addOverlay, removeOverlay]);

  /**
   * THE ECLIPSE STUDY, run over the loaded range and painted on the rail.
   *
   * <p>⚠ ADMIN ONLY, and the button is not what enforces it. The refusal is the backend's 403,
   * because anyone can post to a URL.
   *
   * <p>The range is the CHART'S OWN, read from `barData.dates`. The padded `extendedBarData` is
   * deliberately not used for the range or for the snap: its forward columns are projections, and
   * an event snapped onto one would be a mark on a bar the market has not traded.
   *
   * <p>⚠ TROPICAL, which the brief leaves to "the active calculation model" and the chart does not
   * expose. It is stated here rather than inferred because the choice is nearly free: every rule
   * is an angle between two longitudes and an ayanamsha shifts both by the same amount, so the
   * frame moves no date — it decides only which degree is printed in a tooltip. The two rules that
   * compare a present longitude against a RECORDED one shift by minutes, which is measured in
   * `EclipseStudyServiceTest` and visible in neither the dates nor the marks.
   */
  /**
   * THE LONGITUDINAL ASPECT STUDY, run with no dialog mounted.
   *
   * <p>Same endpoint, same overlay shape and same marks as `LongitudeStudyDlg` — the builders
   * live in `utils/aspectStudy.js` and both paths use them, so a command-driven run and a
   * hand-driven one cannot disagree about what the study drew.
   *
   * <p>⚠ The resolved parameters are reported back on `detail`, including every default applied
   * and every field that could NOT be honoured. An aspect run that silently dropped an orb would
   * be the `buildSimulationQuery` defect: a correct-looking answer to a question nobody asked.
   */
  const handleRunAspectStudy = useCallback(async (req = {}) => {
    const resolved = resolveAspectParams(req, {});
    if (!resolved.ok) {
      return { ok: false, refusal: { code: REFUSAL.INVALID, message: resolved.problem } };
    }
    const query = aspectQuery(resolved.params, barData);
    if (!query) {
      return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
        message: 'Load a symbol before running the aspect study — it is bounded by the '
          + 'charted date range.' } };
    }
    try {
      const res = await api.get(`/api/followstudy/get-degree-separation?${query.toString()}`);
      const dates = Array.isArray(res.data) ? res.data : [];
      const overlays = aspectOverlays(dates, resolved.params,
        req.color ? { color: req.color } : {});
      overlays.forEach(addOverlay);
      const detail = explainAspectRun(resolved.params, resolved, dates.length);
      setStatusText(detail);
      return { ok: true, detail };
    } catch (err) {
      return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
        message: `The aspect study could not be computed: ${err.message}` } };
    }
  }, [api, barData, addOverlay, setStatusText]);

  /**
   * THE AREA HIGHLIGHT STUDY, HEADLESS — `study.run:areaHighlight`.
   *
   * <p>Seven studies behind one tool. The resolver picks the endpoint and the payload shape;
   * everything here is the chart half: ask, paint, and name what was painted.
   *
   * <p>⚠ ONE `groupId` FOR THE WHOLE SET, and that is what makes the header pill possible. The
   * study answers with dozens of intervals and they are ONE figure — "Sun in Virgo" — so the
   * pill's ✕ must remove all of them or none. Routing through `addOverlay` directly rather than
   * `addDrawing` is deliberate for the same reason: `addDrawing` stamps a FRESH group id on any
   * overlay that arrives without one, which would make every interval its own undoable unit and
   * leave the pill able to clear exactly one band.
   *
   * <p>⚠ AND A PREVIOUS RUN OF THE SAME QUESTION IS CLEARED FIRST, by group. Asking twice is
   * something a reader does while changing their mind about the zodiac, and without this the
   * second answer is drawn on top of the first — two sets of bands, one pill, and no way to tell
   * which shading belongs to the label.
   */
  const handleRunAreaHighlightStudy = useCallback(async (req = {}) => {
    const resolved = resolveAreaHighlightParams(req);
    if (!resolved.ok) {
      return { ok: false, refusal: { code: REFUSAL.INVALID, message: resolved.problem } };
    }
    const window = areaHighlightWindow(barData, extendedBarData);
    if (!window) {
      return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
        message: 'Load a symbol before running an area highlight — it is bounded by the '
          + 'charted date range.' } };
    }

    const label = areaHighlightLabel(resolved.type, resolved.payload);
    // Keyed by the LABEL, so asking the same question twice replaces rather than stacks, while
    // two different questions keep their own bands and their own pill.
    const groupId = areaHighlightGroupId(label);
    const color = req.color || AREA_HIGHLIGHT_DEFAULT_COLOR;

    try {
      const res = await api.post(resolved.path, {
        ...resolved.payload, ...window, symbol: barData?.symbol || selectedSymbol || undefined,
      });
      const pairs = Array.isArray(res.data?.datePairs) ? res.data.datePairs : [];

      (overlaysRef.current || []).forEach((o) => {
        if (o && o.groupId === groupId) removeOverlay(o.id);
      });
      areaHighlightOverlays(pairs, {
        groupId, label, color,
        // What produced these bands, so the header pill's gear can reopen the question. See
        // `areaHighlightOverlays` — carried rather than parsed back out of the label.
        source: { type: resolved.type, payload: resolved.payload },
      }).forEach(addOverlay);

      const detail = explainAreaHighlightRun(
        resolved.type, resolved.payload, resolved.applied, pairs.length);
      setStatusText(detail);
      return { ok: true, detail };
    } catch (err) {
      return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
        message: `The area highlight study could not be computed: ${err.message}` } };
    }
  }, [api, barData, extendedBarData, selectedSymbol, addOverlay, removeOverlay, setStatusText]);

  const handleRunEclipseStudy = useCallback(async () => {
    const dates = barData?.dates;
    if (!Array.isArray(dates) || dates.length === 0) {
      return 'Load a symbol before running the Eclipse Study.';
    }
    // Cleared FIRST and unconditionally. A re-run that finds nothing must clear the chart rather
    // than leave the previous answer standing there looking current.
    removeOverlay(ECLIPSE_STUDY_OVERLAY_ID);
    try {
      const res = await api.post('/api/eclipse-study/scan', {
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        orb: 1.0,
        zodiac: 'tropical',
      });
      const data = res.data || {};
      if (data.ok === false && !data.warning) {
        return data.error || 'The Eclipse Study failed.';
      }
      const built = buildEclipseStudyOverlay(data, dates);
      if (built.overlay) addOverlay(built.overlay);
      const breakdown = markerBreakdown(data.events);
      const text = describeEclipseStudy({
        eclipseCount: data.eclipses,
        eventCount: data.eventCount,
        bars: built.bars,
        snapped: built.snapped,
        beyondChart: built.beyondChart,
        truncated: built.truncated,
        warning: data.warning,
      });
      // ⚠ THE BREAKDOWN GOES IN THE RETURNED STRING, which is what the panel shows.
      //
      // It was on `setStatusText` alone, on the reasoning that the panel note is one line in a
      // narrow dialog — and that channel renders NOWHERE on this layout, so the one thing that
      // says which of the nine rules fired was invisible. Found by reading the DOM after a live
      // run: the only element carrying the text was the panel's own note.
      //
      // It matters because all nine markers are drawn in ONE colour, so the chart cannot say
      // which fired — and under rule 2 a rule being silent is itself a finding. The Alert wraps,
      // so the cost is two or three lines in a dialog that is already several paragraphs long.
      const full = breakdown.length
        ? `${text}\n${breakdown.map((b) => `${b.marker} ${b.count}`).join(' · ')}`
        : text;
      setStatusText(full);
      return full;
    } catch (err) {
      console.error('[App] eclipse study failed:', err);
      // The 403 is NAMED rather than folded into a generic failure: "you are not an admin" and
      // "the study broke" are different things to be told, and only one is worth retrying.
      if (err.response?.status === 403) {
        return err.response?.data?.error || 'The Eclipse Study is an administrator tool.';
      }
      return err.response?.data?.error || err.message || 'The Eclipse Study request failed.';
    }
  }, [api, barData, addOverlay, removeOverlay]);

  const handlePaintCelestial = useCallback((report, placement) => {
    removeOverlay(CELESTIAL_OVERLAY_ID);
    // THE BASELINE GOES TOO. Restoring the real study must leave nothing of the random one
    // behind, and clearing only its own id would leave grey triangles scattered over genuine
    // data — the single worst outcome this feature could produce, because a screenshot taken
    // then shows scaffolding presented as computed aspects.
    removeOverlay(RANDOM_OVERLAY_ID);
    const overlay = celestialAspectOverlay(report, barData, placement);
    if (overlay.drawnCount > 0) addOverlay(overlay);
    setStatusText(celestialStatus(report, overlay));
  }, [barData, addOverlay, removeOverlay]);

  /**
   * THE DAILY EOD MARKET SCANNER — run it, and open the candidates.
   *
   * <p>⚠ ADMIN ONLY, and the button is not what enforces it. The refusal is the backend's 403,
   * because anyone can post to a URL.
   *
   * <p>Returns the status string so the Admin Panel can show it in place, and ALSO opens the
   * review dialog, because the candidate list is the output — a scan that reported "41 with
   * signals" and left the reader to find them would be a count rather than a result.
   */
  const handleRunMarketScanner = useCallback(async (market, scanDate) => {
    /*
     * ⚠ THE MARKET DEFAULTS, because the TOOLBAR icon passes nothing.
     *
     * The Admin Panel supplies both arguments from its own two inputs. The toolbar button has
     * neither, so it calls this with no arguments at all — and `market: undefined` on the wire is
     * not "whatever is sensible", it is a field the backend has to guess about. Defaulting here
     * rather than in the dispatcher keeps it in the ONE place that already knows the deployment,
     * and leaves the panel's explicit choice untouched.
     *
     * `appConfig.market` is the deployment's own market, which is the right default for a
     * parameterless button and is NOT a claim about the signed-in customer: the results dialog
     * names the market it scanned, so a reader is told rather than left to assume. (The symbol
     * LIST is a different matter — the backend overrides that from the user's profile, precisely
     * because this constant is per-deployment.)
     */
    const scanMarket = market || appConfig?.market || 'US';
    /*
     * ⚠ THE DATE DEFAULTS TO THE CHART'S LAST BAR, not to the backend's default.
     *
     * Omitting it lets `MarketScannerController.parseDate` fall back to `Market.currentDate()` --
     * TODAY in the market's timezone. Its javadoc calls that "the market's own latest session",
     * and it is not: it does not roll back to the last session that has DATA, deliberately, so
     * that Friday's signals are never reported under Sunday's date.
     *
     * The consequence, measured on this machine: today was 2026-10-29, the EOD store ended
     * 2026-10-22, and the scan reported `447 scanned (0 judged, 446 no bar), 0 with signals`.
     * Correct, and useless -- a one-click button that can only answer "nothing" whenever the data
     * is a day or more stale, which for a local store is most of the time.
     *
     * `barData.dates` is the series ON SCREEN, so its last entry is a session this deployment
     * demonstrably has bars for. Falling back to `undefined` with no symbol loaded keeps the
     * backend's behaviour rather than inventing a date.
     *
     * ⚠ THIS ALSO CHANGES THE ADMIN PANEL, whose empty date previously meant today. It now means
     * the same "last session on the chart", which is what its own caption already claimed, and
     * the dialog prints the date it used either way.
     */
    const lastChartDate = barData?.dates?.[barData.dates.length - 1];
    const effectiveDate = scanDate || lastChartDate;
    setScannerState(SCAN_STATE.LOADING);
    setScannerStatus(SCAN_STATE_LABEL[SCAN_STATE.LOADING]);
    try {
      // One synchronous request. The intermediate states are a vocabulary rather than a
      // progress feed — see the note in utils/marketScanner.js — so the UI steps to SCANNING
      // immediately and does not pretend to observe the backend's phases.
      setScannerState(SCAN_STATE.SCANNING);
      setScannerStatus(SCAN_STATE_LABEL[SCAN_STATE.SCANNING]);
      const res = await api.post('/api/market-scanner/scan', {
        market: scanMarket,
        ...(effectiveDate ? { scanDate: effectiveDate } : {}),
      });
      const data = res.data || {};
      if (data.ok === false) {
        setScannerState(SCAN_STATE.FAILED);
        const msg = data.error || 'The market scanner failed.';
        setScannerStatus(msg);
        return msg;
      }
      setScannerCandidates(Array.isArray(data.candidates) ? data.candidates : []);
      setScannerState(SCAN_STATE.COMPLETE);
      const text = describeScan(data);
      setScannerStatus(text);
      setScannerOpen(true);
      setStatusText(text);
      return text;
    } catch (err) {
      console.error('[App] market scanner failed:', err);
      setScannerState(SCAN_STATE.FAILED);
      // The 403 is NAMED rather than folded into a generic failure: "you are not an admin" and
      // "the scan broke" are different things to be told, and only one is worth retrying.
      const msg = err.response?.status === 403
        ? (err.response?.data?.error || 'The market scanner is an administrator tool.')
        : (err.response?.data?.error || err.message || 'The market scanner request failed.');
      setScannerStatus(msg);
      return msg;
    }
  }, [api, appConfig, barData]);

  /**
   * Selecting a candidate: load the symbol, then draw BOTH engines on it.
   *
   * <p>⚠ THE OVERLAYS CANNOT BE DRAWN HERE. `handleRunCycleBand` needs the symbol's bars, and
   * those arrive through the fetch effect that `setSelectedSymbol` kicks off — so drawing
   * immediately would run both cycles against the PREVIOUS symbol's series and paint its levels
   * under the new symbol's name. The request is recorded and an effect below fires it once the
   * bars for that symbol have actually landed, which is a condition rather than a delay.
   */
  const handleScannerSelectSymbol = useCallback((symbol) => {
    if (!symbol) return;
    setScannerPendingDraw(symbol);
    setSelectedSymbol(symbol);
  }, []);

  useEffect(() => {
    if (!scannerPendingDraw) return;
    // Keyed on the BARS, not on selectedSymbol: the symbol changes the instant it is clicked
    // and the series follows a request later, so waiting for the state alone is waiting for
    // nothing.
    if (barData?.symbol !== scannerPendingDraw || !(barData?.dates || []).length) return;
    setScannerPendingDraw(null);
    // Both engines, because a candidate's tier is a statement about the two together — showing
    // one would leave a HIGH row unverifiable on the chart it was opened to verify.
    CYCLE_BUTTONS.forEach((cycle) => { handleRunCycleBand(cycle); });
  }, [scannerPendingDraw, barData, handleRunCycleBand]);

  /**
   * Paints a RANDOM VISUAL BASELINE in place of the study.
   *
   * <p>Built here rather than in the dialog because this is where `barData` lives, which mirrors
   * how the real study is painted — the dialog decides WHAT, this decides where it lands on the
   * chart. The draw itself is the dialog's, so re-filtering by the modifier checkboxes reuses
   * the same scatter instead of rolling a new one.
   */
  const handlePaintCelestialRandom = useCallback((report, modifiers, placement) => {
    // Both ids, in both directions: a draw replaces the study exactly as the study replaces a
    // draw, so neither can ever be left half on the chart.
    removeOverlay(CELESTIAL_OVERLAY_ID);
    removeOverlay(RANDOM_OVERLAY_ID);

    // SIZED FROM THE STUDY'S OWN OVERLAY, which is why the real one is rebuilt here rather than
    // the counts being passed in. It is the same pure function the real paint calls, over the
    // same report and the same bars, so the two cannot disagree about how many marks there are
    // — and it is cheap: no fetch, no ephemeris, just a date lookup per match.
    //
    // Note this is the DRAWN count, not report.matchCount: a match whose day falls past the
    // last bar is counted by the report and appears nowhere, so sizing from the report would
    // overstate the baseline by exactly the marks a reader cannot see.
    const real = celestialAspectOverlay(report, barData, placement);
    const { total, sizeA } = baselineSizesFor(real);

    const barCount = (barData && barData.dates && barData.dates.length) || 0;
    const draw = splitIndices(drawRandomIndices(barCount, total), sizeA);
    const indices = subsetForModifiers(draw.subsetA, draw.subsetB, modifiers);

    const common = !!(modifiers && modifiers.common);
    const different = !!(modifiers && modifiers.different);
    const subsetLabel = common === different
      ? `${indices.length} = ${draw.subsetA.length} common + ${draw.subsetB.length} different`
      : `${indices.length}, matching the study's ${common ? 'common' : 'different'} half`;

    const overlay = randomBaselineOverlay(indices, barData, placement, { subsetLabel });
    if (overlay.drawnCount > 0) addOverlay(overlay);
    setStatusText(randomBaselineStatus(overlay, real.drawnCount));
  }, [barData, addOverlay, removeOverlay]);

  const handleBrandSave = useCallback((name) => {
    setBrandOverride(writeBrandOverride(name));
    setBrandDialogOpen(false);
    setStatusText(`Application name set to "${name}".`);
  }, [setStatusText]);

  /**
   * Flips the toolbar preset, and with it the product's name.
   *
   * <p>The name is set through the SAME override the rename box writes, so there is one source of
   * truth for what the header says and the existing subscription updates the header and the tab
   * title with no further wiring. Long clears the override rather than writing "integralDelta.ai",
   * which is what makes the revert correct for a market whose brand is not that.
   */
  const handleToggleBarMode = useCallback(() => {
    const next = otherMode(barMode);
    setBarMode(writeBarMode(next));
    if (next === SHORT) setBrandOverride(writeBrandOverride(SHORT_BAR_BRAND));
    else { clearBrandOverride(); setBrandOverride(null); }
    setStatusText(next === SHORT
      ? `Short bar. The application is now "${SHORT_BAR_BRAND}".`
      : 'Long bar. The full toolbar and the default name are back.');
  }, [barMode, setStatusText]);

  const handleBrandReset = useCallback(() => {
    clearBrandOverride();
    setBrandOverride(null);
    setBrandDialogOpen(false);
    setStatusText('Application name reset to the default.');
  }, [setStatusText]);

  /**
   * Moves the boundary: everything up to `index` is real, everything after it is one flat bar.
   *
   * <p>Masks the ORIGINAL every time rather than the currently masked series. Masking a masked
   * series can only ever hide more - stepping backwards would work and stepping forwards would
   * reveal flat bars, which looks like the data ending rather than the replay advancing.
   */
  const revealTo = useCallback((index) => {
    const original = simulateOriginalRef.current;
    if (!original) return;
    const at = clampRevealIndex(index, original.ohlc?.length || 0);
    if (at === null) return;
    setSimulateReveal(at);
    simulateRevealRef.current = at;
    setBarData(maskBarsAfter(original, at));
    const close = revealedClose(original, at);
    const total = original.ohlc.length;
    setStatusText(`Alpha Replay: ${original.dates?.[at] || `bar #${at + 1}`}`
      + ` (bar ${at + 1} of ${total})`
      + `${close === null ? '' : `, held at ${close.toFixed(2)}`}`
      + '. Click a bar, or use the arrow keys.');
  }, [setStatusText]);

  const exitSimulate = useCallback(({ quiet = false } = {}) => {
    const original = simulateOriginalRef.current;
    simulateOriginalRef.current = null;
    setSimulateMode(false);
    setSimulateReveal(null);
    simulateRevealRef.current = null;
    // A gate without a replay is a constraint on nothing, and leaving one standing would gate
    // the NEXT session for a trade taken in the previous one.
    setReplayGate(null);
    // Restored from the cached original, so every study and indicator recomputes over the full
    // history on the next render - the same path that put the masked data there.
    if (original) setBarData(original);
    if (!quiet) setStatusText('Alpha Replay off. The full chart is back.');
  }, [setStatusText]);

  const handleToggleSimulate = useCallback(() => {
    if (simulateMode) { exitSimulate(); return; }
    const bars = barData?.ohlc?.length || 0;
    if (bars < MIN_BARS_TO_SIMULATE) {
      setStatusText(`Alpha Replay needs at least ${MIN_BARS_TO_SIMULATE} bars; this chart has ${bars}.`);
      return;
    }
    // Cached BEFORE anything is masked, and this is the only copy of the real prices while the
    // mode is on.
    simulateOriginalRef.current = barData;
    setSimulateMode(true);
    setSimulateReveal(null);
    simulateRevealRef.current = null;
    setStatusText('Alpha Replay on. Click a bar to hide everything after it.');
  }, [simulateMode, barData, exitSimulate, setStatusText]);

  // Arrow keys step the boundary. Ignored while focus is in a field, so typing in the symbol box
  // does not replay the chart underneath it.
  useEffect(() => {
    if (!simulateMode) return undefined;
    const onKey = (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const el = document.activeElement;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return;
      const total = simulateOriginalRef.current?.ohlc?.length || 0;
      const next = stepReveal(simulateReveal, e.key === 'ArrowRight' ? 1 : -1, total);
      if (next === null) return;
      e.preventDefault();
      // An arrow key is one bar by construction, so there is nothing left to clamp it with.
      revealTo(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [simulateMode, simulateReveal, revealTo]);

  // A new symbol replaces barData wholesale, so a mode still holding the previous symbol's cached
  // series would restore THAT chart on exit. Left quiet: the status line is already saying what
  // loaded, and "Alpha Replay off" over it would read as a second, unrelated event.
  useEffect(() => {
    if (simulateMode) exitSimulate({ quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  /**
   * A NEW SYMBOL FLUSHES EVERY CYCLE OVERLAY, and the runs behind them.
   *
   * <h2>Why they could not simply be left</h2>
   *
   * A cycle's intervals are ephemeris dates, so they are the same for every instrument — but its
   * horizontal bands are PRICE, measured from the bars between two pillars. Carried onto another
   * symbol those bands are a range the new asset never traded, drawn in the same pen as a
   * measured one and indistinguishable from it. The pillars would survive the change honestly
   * and the bands would not, which is the worse half of leaving them: a figure half of which is
   * about the chart underneath it.
   *
   * <p>And `cycleRuns` has to go with them, or the reveal watcher redraws the previous symbol's
   * bands from the remembered intervals the next time the boundary moves — the overlays would
   * come back by themselves, which is harder to diagnose than their never having gone.
   *
   * <h2>BOTH prefixes, explicitly</h2>
   *
   * `cyclePillarIdPrefix('short')` is `cycle-band-short-` and `cycleBandIdPrefix('short')` is
   * `cycle-band-short-band-`, so the first is a PREFIX of the second and sweeping the pillar one
   * alone would happen to take the bands too. Naming both is what stops that being load-bearing:
   * it is true today by accident of two strings, and a renamed prefix would silently leave half
   * a cycle on a new symbol's chart.
   *
   * <p>No status line. The symbol load is already announcing itself, and "cycle overlays
   * cleared" over it would read as a second, unrelated event — the same judgement the simulate
   * exit above makes.
   */
  useEffect(() => {
    const prefixes = CYCLE_BUTTONS.flatMap(
      ({ key }) => [cyclePillarIdPrefix(key), cycleBandIdPrefix(key)]);
    (overlaysRef.current || []).forEach((o) => {
      if (o && typeof o.id === 'string' && prefixes.some(p => o.id.startsWith(p))) {
        removeOverlay(o.id);
      }
    });
    setCycleRuns({});
    /*
     * AND THE PLANETARY ANALOG MARKS, for a subtler reason than the bands.
     *
     * An analog is a DATE, so unlike a cycle band it lands on the correct column of any
     * instrument and would survive the change looking entirely sound. What does NOT survive is
     * the hover card: it quotes the previous symbol's forward return and its baseline, so on the
     * new chart every mark would carry a measurement of a series that is no longer on screen —
     * wrong, plausible, and with nothing to give it away. Honestly broken beats quietly wrong.
     */
    removeOverlay(PLANETARY_ANALOG_OVERLAY_ID);
    /*
     * AND THE PIVOT-PAIR MARKS, for a reason of its own: a pivot is derived from the chart's
     * OWN bars, so on another symbol the two dates are most likely not pivots at all. Drawn,
     * they would be two amber triangles claiming to be this instrument's swing extremes.
     */
    removeOverlay(PIVOT_COMMONALITY_OVERLAY_ID);
    // The SYMBOL alone. Naming `overlays` would make this fire on every drawing anyone adds,
    // which would delete a cycle the moment a trendline was drawn beside it; the ref is what
    // lets it read the current list without subscribing to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  /* ──────────────────────────── CHART ANALYTICA ──────────────────────────────────────────
   *
   * Declared HERE, below everything it reads, because a `useCallback` dependency array is
   * evaluated during RENDER — so naming `cycleBandsFor` or `rsiData` from a block placed in its
   * natural reading position further up throws `Cannot access … before initialization` on
   * mount, with lint clean and the build compiling. This file has paid for that twice.
   */

  /** Every run, oldest first. The history IS the comparison — nothing is discarded. */
  const [analyticaRuns, setAnalyticaRuns] = useState([]);
  const [analyticaActiveId, setAnalyticaActiveId] = useState(null);
  const [analyticaOpen, setAnalyticaOpen] = useState(false);
  const [analyticaAnnotations, setAnalyticaAnnotations] = useState(true);
  // Monotonic, so two runs on the SAME bar are still two rows. A date would collide, and the
  // second run would silently replace the first in the history it exists to accumulate.
  const analyticaSeq = useRef(0);

  /**
   * The CTS points the chart is currently showing, as the engine wants them.
   *
   * <p>Read off the overlay rather than fetched again, for the reason `replayCtsPoints` beside
   * it states: the chart folds clustered points with `reversalBars`, so a second read of the
   * dataset would score days nothing is drawn on. `position` is the direction — the renderer
   * puts an up arrow BELOW its bar — so it is translated here rather than carried twice.
   */
  const analyticaCtsPoints = useMemo(() => {
    const scan = (overlays || []).find((o) => o && o.id === GANN_CONFLUENCE_OVERLAY_ID);
    return (Array.isArray(scan?.points) ? scan.points : [])
      .map(p => ({ date: p.date, direction: p.position === 'below' ? 'up' : 'down' }));
  }, [overlays]);

  /**
   * Runs the scan from bar 0 to `barIndex` and records it.
   *
   * <p>Through `runWithBusyIndicator`, which is this codebase's own answer to "hold the frame
   * while something blocks": two animation frames so React can commit the label and the browser
   * can paint it before the thread is taken. The engine itself is five linear passes and reports
   * its own `elapsedMs`, which the dashboard prints — so if it ever stops being fast, the number
   * says so rather than a worker being built against a cost nobody measured.
   */
  const runAnalytica = useCallback((barIndex) => {
    const dates = barData?.dates || [];
    if (!selectedSymbol || dates.length === 0) {
      setError('Load a symbol before running Chart Analytica.');
      return;
    }
    const to = Math.max(0, Math.min(Math.floor(barIndex), dates.length - 1));

    runWithBusyIndicator(`Chart Analytica — scanning ${(to + 1).toLocaleString()} bars`, () => {
      // The bands the CHART DREW, from the same function that drew them. A run that is not on
      // the chart contributes nothing: the dashboard then names the gesture that would add it,
      // rather than inventing geometry a reader cannot see.
      const bands = Object.values(cycleRuns)
        .map(run => ({
          category: run.key === 'long' ? 'PTA' : 'PTB',
          bands: cycleBandsFor(run, null).bands,
        }))
        .filter(g => g.bands.length > 0);

      const report = runChartAnalytica({
        symbol: selectedSymbol,
        dates,
        ohlc: barData.ohlc || [],
        volumes: barData.volumes || [],
        rsi: rsiData || [],
        upToIndex: to,
        bands,
        ctsPoints: analyticaCtsPoints,
      });

      analyticaSeq.current += 1;
      const entry = {
        id: `analytica-${analyticaSeq.current}`,
        // The ORDINAL as well as the scope. `runLabel` carries everything the report knows
        // about itself, and two runs of the same bar with the same inputs are genuinely
        // identical by that measure — the ordinal is the only thing that can separate them,
        // and it belongs to the history rather than to the report.
        label: `#${analyticaSeq.current} ${runLabel(report) || 'run'}`,
        report,
      };
      setAnalyticaRuns(prev => [...prev, entry]);
      setAnalyticaActiveId(entry.id);
      setAnalyticaOpen(true);

      const drawn = analyticaOverlays(report, dates, analyticaAnnotations);
      setStatusText(analyticaStatus(report, drawn));
    });
  }, [selectedSymbol, barData, rsiData, cycleRuns, cycleBandsFor, analyticaCtsPoints,
    analyticaAnnotations, runWithBusyIndicator, setStatusText]);

  /** The toolbar's way in: the same scan, to the last bar, because no bar was named. */
  const handleChartAnalyticaToolbar = useCallback(() => {
    runAnalytica((barData?.dates?.length || 1) - 1);
  }, [runAnalytica, barData]);

  /* ──────────────────── CTS LEFT-ONLY REVERSAL ODDS, on the hover card ────────────────────
   *
   * A stable signature for the CTS scan that is on the chart.
   *
   * <p>The effect below WRITES to the same overlay it reads, so it cannot depend on
   * `analyticaCtsPoints` — that memo is derived from `overlays`, so the write would give it a
   * new identity, re-run the effect and write again, for ever. The signature is the scan's
   * CONTENT rather than its identity, and attaching a tooltip does not change the content.
   */
  const ctsScanKey = useMemo(() => {
    const pts = analyticaCtsPoints;
    if (pts.length === 0) return '';
    return `${pts.length}|${pts[0].date}|${pts[pts.length - 1].date}`;
  }, [analyticaCtsPoints]);

  /**
   * Attaches the odds card to every CTS mark, and re-attaches when its inputs change.
   *
   * <p>An EFFECT rather than a line inside the scan handler, because the odds depend on three
   * things that arrive in any order — the scan, a drawn cycle, and the bars themselves. A
   * reader who scans first and draws PTB second must get zone-aware odds without re-scanning,
   * and computing once at scan time would leave the card describing a chart that has changed
   * under it.
   *
   * <p>`sectionByBar` is built from `cycleBandsFor`, the same function that DREW the channels —
   * so a zone in the tooltip is the zone a reader can see, which is the rule the whole Chart
   * Analytica engine rests on.
   */
  useEffect(() => {
    const dates = barData?.dates || [];
    if (!ctsScanKey || dates.length === 0) return;
    const scan = (overlaysRef.current || [])
      .find(o => o && o.id === GANN_CONFLUENCE_OVERLAY_ID);
    if (!scan || !Array.isArray(scan.points) || scan.points.length === 0) return;

    const sections = Object.values(cycleRuns)
      .flatMap(run => sectionsFrom(
        cycleBandsFor(run, null).bands,
        dates.length - 1,
        run.key === 'long' ? 'PTA' : 'PTB',
      ));
    const byBar = sectionByBar(sections, dates.length - 1);

    const records = ctsOddsSeries({
      dates,
      ohlc: barData.ohlc || [],
      rsi: rsiData || [],
      sectionByBar: byBar,
      // THE REPLAY BOUNDARY. A point past it is returned un-assessed, and an earlier point
      // whose horizon ends past it has no knowable outcome and is not admitted as a precedent.
      upToIndex: assessmentBoundary,
      points: scan.points.map(p => ({
        date: p.date, direction: p.position === 'below' ? 'up' : 'down',
      })),
    });
    const byDate = new Map(records.map(r => [r.date, r]));

    updateOverlay(GANN_CONFLUENCE_OVERLAY_ID, {
      // The card is STATISTICAL now, so the OHLCV-floater preference must not suppress it.
      // Without this flag the odds would be invisible to everybody, which is the failure the
      // celestial and Deep Pivot cards already shipped once.
      statsTooltip: true,
      points: scan.points.map((p) => {
        /*
         * ⚠ NOT ONTO A CLUSTER GLYPH, which shares its date with the triangle it annotates.
         *
         * The dash/dot column sits on the SAME bar as the mark, so a lookup by date finds the
         * record and the glyphs were being given a card of their own — measured live at 420 of
         * them. A 2px dot that opens an odds panel is noise, and with `enterable: true` it is
         * noise that stays on screen once the pointer touches it.
         *
         * The glyph's own meaning is "the next bars also fired", which the triangle's expanded
         * half already says in words.
         */
        if (p.cluster) return p;
        const record = byDate.get(p.date);
        if (!record) return p;
        return {
          ...p,
          /*
           * ⚠ COMPOSED FROM `studyLines`, NOT APPENDED TO `tooltipLines`.
           *
           * This effect reads the overlay it writes, so it sees its own previous output on every
           * re-run -- a cycle drawn, the replay stepped. Appending here would append again each
           * time and the card would grow without bound after the second re-render.
           *
           * It also used to REPLACE the whole field, which silently discarded the aspect
           * breakdown: the reader asked for the trine and the hard aspect and kept being shown
           * the odds card, because this line ran last.
           */
          tooltipMoreLines: composeMarkCard(p.studyLines, oddsTooltipLines(record)),
          // THE SIMULATOR HOOK. The whole odds record travels, because the card's metadata rows
          // are built by the provider adapter from it -- passing a handful of extracted fields
          // would put the adapter's own contract in this file, which is the seam PTA and PTB
          // are meant to plug into without touching App.
          simSignal: { provider: 'CTS', date: p.date, index: record.index, record },
        };
      }),
    });
    // `overlaysRef` rather than `overlays`: naming the list would fire this on every drawing
    // anyone adds, and the write below would then re-enter it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctsScanKey, cycleRuns, barData, rsiData, cycleBandsFor, updateOverlay,
    // NAMED, so stepping the replay re-assesses: a point the playhead has just reached must
    // stop reading "beyond the replay boundary" without the reader re-running the scan.
    assessmentBoundary]);

  /*
   * ── THE INTERACTIVE TRADE SIMULATOR ───────────────────────────────────────────────────────
   *
   * Clicking a signal marker opens a modeless card and records the theoretical trade. The
   * dialog UPDATES rather than multiplying: there is one session and one selection, so a click
   * on a second point replaces what the card shows and appends to the ledger below it.
   *
   * `tradeSimOpen` is separate from the session on purpose -- closing the dialog must not throw
   * the ledger away, and re-opening it must not start a new one. The close button's own tooltip
   * says so.
   */
  const [tradeSimSession, setTradeSimSession] = useState(EMPTY_SESSION);
  const [tradeSimOpen, setTradeSimOpen] = useState(false);
  /*
   * THE MANUAL EXECUTION MACHINE, for the families that hand the trade to the reader.
   *
   * Separate state from the session: one holds what is BEING built and the other what has been
   * BOOKED, and collapsing them would make Cancel either destroy the ledger or be unable to
   * clear a half-finished trade.
   */
  const [tradeSimManual, setTradeSimManual] = useState(IDLE_STATE);
  // Read by the bar-click handler, which must not be rebuilt on every phase change -- it is
  // `onBarSelect`, and that component states the constraint: a handler identity that moves on
  // every keystroke tears the listener down and back up.
  const tradeSimManualRef = useRef(tradeSimManual);
  useEffect(() => { tradeSimManualRef.current = tradeSimManual; }, [tradeSimManual]);
  /*
   * THE BARS, THROUGH A REF, and this is not optional.
   *
   * `handleBarSelection` DELIBERATELY omits the bar-data arrays from its dependency list -- it
   * is `onBarSelect`, and rebuilding it whenever price data arrives makes echarts-for-react
   * dispose and recreate the whole chart. So the closure's `barData` is whatever it was when
   * the deps last changed, which on a fresh load is undefined.
   *
   * Found in a browser: every Open click came back "That bar has no close to fill at" while
   * `barData.ohlc[4740]` was perfectly good -- the handler was reading an empty array. The
   * overlaysRef beside it exists for exactly this reason.
   */
  const tradeSimBarsRef = useRef(null);
  useEffect(() => { tradeSimBarsRef.current = barData?.ohlc || null; }, [barData]);


  /**
   * ATR for the whole series, memoised.
   *
   * <p>Here rather than inside `simulateTrade` so a session of thirty clicks computes it once.
   * The engine recomputes when it is not handed one, which keeps it usable from a test without
   * a chart.
   */
  const tradeSimAtr = useMemo(() => atrSeries(barData?.ohlc || []), [barData]);

  const handleSignalClick = useCallback((signal) => {
    if (!signal) return;
    setTradeSimOpen(true);

    /*
     * EVERY SIGNAL CLICK RECORDS CONTEXT; only some of them price a trade.
     *
     * The context is what the card shows and what a manual trade taken against it is
     * attributed to, so every family supplies one. `assessesAutomatically` below is the only
     * distinction left: CTS has rules and prices a theoretical trade on the spot, a channel
     * does not.
     */
    {
      const element = signal.element
        // A CTS point has no channel to be an "element" of, so its own record stands in — it
        // is what attributes a manual trade taken while this signal is on the card.
        || { ...signal.record, provider: signal.provider, label: signal.date };
      setTradeSimManual(prev => withContext(prev, element));
    }
    if (!assessesAutomatically(signal.provider) || !signal.record) return;

    /*
     * A POINT PAST THE REPLAY BOUNDARY IS NOT ASSESSED, and is not armed either.
     *
     * Its own record already says so — `ctsOddsSeries` returns it un-assessed — and pricing a
     * trade from it would walk bars that are off the end of the drawn chart. Recorded rather
     * than ignored, so the card can say which kind of silence this is.
     */
    if (signal.record.beyondBoundary) {
      setTradeSimManual(cancelManual);
      setTradeSimSession(prev => recordClick(prev, {
        provider: signal.provider,
        date: signal.date,
        signal: signal.record,
        index: signal.index,
        direction: signal.record.direction,
        horizon: 0,
        priced: false,
        exitReason: 'Beyond the replay boundary — step the replay forward to assess this point',
      }));
      return;
    }
    const adapter = providerFor(signal.provider);
    const bars = barData?.ohlc || [];
    // An UNKNOWN provider is still recorded, with no rules and no result. The card then says it
    // cannot evaluate this family -- which is visible, where silently running CTS's volatility
    // rule under another family's name and reporting it as that family's result is not.
    const result = adapter
      ? simulateTrade({
        ohlc: bars,
        index: signal.index,
        direction: signal.record.direction,
        rules: adapter.rulesFor(signal.record),
        atr: tradeSimAtr,
        // BOUNDED, so a replay cannot price a result from bars it has not drawn.
        maxIndex: assessmentBoundary,
      })
      : null;
    setTradeSimSession(prev => recordClick(prev, {
      provider: signal.provider,
      date: signal.date,
      signal: signal.record,
      ...(result || {
        index: signal.index,
        direction: signal.record.direction,
        horizon: 0,
        priced: false,
        exitReason: `no simulation rules are defined for ${signal.provider} signals yet`,
      }),
    }));

    /*
     * AND IN A REPLAY, TAKING IT GATES THE CHART.
     *
     * `gateFor` arms only on a RULED trade that is still OPEN at the playhead — so a marker
     * well behind the edge, whose outcome is already drawn, arms nothing and browsing the
     * ledger stays free. Set unconditionally rather than only when non-null: clicking a
     * resolved point while another trade is being walked out must not leave the old gate
     * standing over a card that is no longer showing it.
     */
    /*
     * WITH ITS PROVIDER AND DATE, which `simulateTrade` does not carry.
     *
     * Found in a browser: without them the banner read "The trade opened is still open" with
     * a hole where the date goes — and far worse, the re-price below keys on them, so
     * `repriceTrade` matched no stored trade and silently did nothing. The gate would have
     * dropped on resolution while the ledger kept the OPEN record for ever.
     */
    setReplayGate(gateFor(
      { ...result, provider: signal.provider, date: signal.date }, assessmentBoundary,
    ));
  }, [barData, tradeSimAtr, assessmentBoundary]);

  /**
   * RE-PRICES THE GATED TRADE AS THE PLAYHEAD MOVES, and drops the gate when it resolves.
   *
   * <p>Through `simulateTrade` at the new boundary, not through a second verdict of its own —
   * `gateResolution` asks WHETHER it is over and this asks the engine WHAT happened, so the
   * ledger and the gate cannot end up disagreeing about a trade they are both watching.
   *
   * <p>`repriceTrade` rather than `recordClick`: a boundary step is not a click, and counting
   * it as one would make the Clicked tile a measure of how far the reader had stepped.
   */
  useEffect(() => {
    const gate = replayGate;
    if (!gate || !Number.isFinite(assessmentBoundary)) return;
    const bars = barData?.ohlc || [];
    const priced = simulateTrade({
      ohlc: bars,
      index: gate.entryIndex,
      direction: gate.sign < 0 ? 'down' : 'up',
      // THE GATE'S OWN RULES, which are the ones the trade was priced with when it was taken.
      rules: gate.rules,
      atr: tradeSimAtr,
      maxIndex: assessmentBoundary,
    });
    if (priced) {
      setTradeSimSession(prev => repriceTrade(prev, {
        ...priced, provider: gate.provider, date: gate.date,
      }));
    }
    const { resolved, reason } = gateResolution(gate, bars, assessmentBoundary);
    if (!resolved) return;
    setReplayGate(null);
    setStatusText(`The trade resolved — ${reason}. Normal replay freedom is restored.`);
  }, [assessmentBoundary, replayGate, barData, tradeSimAtr, setStatusText]);

  /*
   * ── THE SESSION'S TRADES, ON THE CHART ────────────────────────────────────────────────────
   *
   * One segment from Open to Close per completed trade, for every provider alike — CTS, PTA, PTB
   * and a bare manual trade. The ledger reached the card and nothing else, so a reader could read
   * their trades in a table and not see where any of them sat on the price action.
   *
   * <h3>Replaced wholesale, never appended to</h3>
   *
   * `addOverlay` with a stable id REPLACES, so a session that grows by one trade redraws all of
   * them from the ledger. The alternative — one overlay per trade, or appending to a list held
   * here — would be a second copy of the ledger that drifts the first time a trade is re-priced
   * by the progression gate, which happens on every bar of a gated replay.
   *
   * <h3>Removed when there is nothing to draw</h3>
   *
   * `buildTradeSimVectorOverlay` answers null for an empty or all-open session, and that removes
   * the series rather than leaving an empty one. An empty series still costs a merge and a layout
   * pass on every option rebuild, and on screen it makes "no trades have completed" and "the
   * overlay failed" the same picture.
   *
   * <p>Keyed on the TRADES and the DATES. The dates matter as much as the trades: a trade stores
   * bar INDICES, so the same ledger drawn against a different series would put every segment on
   * the wrong bars — which is why `sessionTradeVectors` resolves indices through the axis it is
   * handed and yields nothing for an index the axis does not carry.
   */
  useEffect(() => {
    const overlay = buildTradeSimVectorOverlay(tradeSimSession?.trades, barData?.dates);
    if (overlay) addOverlay(overlay);
    else removeOverlay(TRADE_SIM_VECTOR_OVERLAY_ID);
  }, [tradeSimSession, barData, addOverlay, removeOverlay]);

  /*
   * ── A NEW SYMBOL FLUSHES THE SESSION ──────────────────────────────────────────────────────
   *
   * Forced by the overlay above, and it is the same defect the replay already records: a trade
   * stores BAR INDICES, so a ledger carried onto another instrument would draw every segment at
   * the right index of the WRONG series — at prices the new asset never traded, in the same pen
   * as a real one and indistinguishable from it.
   *
   * <p>Clearing it is also the honest reading of what a session IS. Its win rate, its profit
   * factor, its alpha and its MAE are all statements about one instrument over one stretch of its
   * history; a book spanning two symbols is not a worse measurement of anything, it is a
   * measurement of nothing. The cycle overlays above go for the same reason, one bullet earlier.
   *
   * <p>The MANUAL machine goes with it. A half-built trade holds an open leg at a bar index on the
   * previous series, so leaving it armed would let the next click close a position against a bar
   * from another chart — the worst available outcome, because it books rather than merely draws.
   *
   * <p>No status line, matching the sibling effect: the symbol load is already announcing itself.
   */
  useEffect(() => {
    setTradeSimSession(clearSession());
    setTradeSimManual(IDLE_STATE);
    setReplayGate(null);
    // The SYMBOL alone, and the three setters are stable. Naming the session here would make this
    // clear the session every time a trade was recorded, which is an infinite loop that empties
    // the ledger on the first click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  /**
   * The overlays follow the ACTIVE run and the annotation switch.
   *
   * <p>An effect rather than a line inside `runAnalytica`, because three things change what is
   * drawn — a new run, switching the history selector, and the toggle — and drawing from each
   * of them separately is three chances for the chart to disagree with the panel.
   *
   * <p>`overlaysRef` rather than `overlays`: naming the list would fire this on every drawing
   * anyone adds, which would redraw the audit the moment a trendline was placed beside it.
   */
  useEffect(() => {
    const active = analyticaRuns.find(r => r.id === analyticaActiveId);
    (overlaysRef.current || []).forEach((o) => {
      if (o && ANALYTICA_OVERLAY_IDS.includes(o.id)) removeOverlay(o.id);
    });
    if (!active || !analyticaOpen) return;
    analyticaOverlays(active.report, barData?.dates || [], analyticaAnnotations)
      .overlays.forEach(addOverlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticaActiveId, analyticaRuns, analyticaAnnotations, analyticaOpen]);

  /**
   * A NEW SYMBOL DISCARDS EVERY RUN, not just the overlays.
   *
   * <p>The same reasoning the cycle sweep above states, and the stronger case: a report is bar
   * INDICES and PRICES from one instrument's series. Kept across a symbol change its overlays
   * would mark whichever bars happen to share those indices, and its dashboard would describe a
   * chart nobody is looking at — with a symbol in the header saying so, which is worse than
   * silence because it reads as current.
   */
  useEffect(() => {
    (overlaysRef.current || []).forEach((o) => {
      if (o && ANALYTICA_OVERLAY_IDS.includes(o.id)) removeOverlay(o.id);
    });
    setAnalyticaRuns([]);
    setAnalyticaActiveId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  const handleBarSelection = useCallback((date, dataIndex, price, hit = {}) => {
    // Recorded FIRST, before any branch below can consume the click and return. Every branch here
    // is a thing a click can additionally do; none of them stops it having been a click on a bar.
    setLastClickedBar({ index: dataIndex, date });

    /*
     * ── TWO ZONES, AND ONLY WHILE A REPLAY IS RUNNING ───────────────────────────────────────
     *
     * On a candle the reader is pointing at a bar and means to trade it; anywhere else in the
     * grid they mean to move the playhead. That split is what breaks the deadlock a replay
     * otherwise creates: opening a trade puts the manual machine into "awaiting close", which
     * owns the next click — and the bar it needs has not been revealed, because revealing it
     * needs the very click the trade is holding.
     *
     * SCOPED TO SIMULATE MODE deliberately. Outside one there is nothing to step forward to, so
     * the split would buy nothing and cost precision: a reader closing a trade on an ordinary
     * chart would have their click swallowed for landing two candles' height above the wick.
     * Every non-replay gesture therefore behaves exactly as it did.
     */
    const zone = simulateMode
      ? hitZoneFor({
        bar: tradeSimBarsRef.current?.[dataIndex],
        price,
        tolerance: hit.priceTolerance,
        index: dataIndex,
        // THE SAME BOUNDARY everything else in the simulator is locked to, so the masked
        // future is canvas by definition rather than by a flatness test that a limit-locked
        // real bar would also satisfy.
        boundary: assessmentBoundaryRef.current,
      })
      : HIT_ZONES.BAR;

    /*
     * ── THE MANUAL TRADE OWNS THE CLICK, and it is checked before simulate mode ──
     *
     * The reader armed this deliberately and the dialog is telling them, in words, to click a
     * bar to open or close the trade. A click that instead moved a replay playhead or dropped a
     * Fibonacci point would be the prompt on screen lying about what the next click does --
     * which is the same argument simulate mode makes for coming before the drawing tools, and
     * this gesture is the more explicit of the two. Cancel is always one press away.
     */
    if (isAwaitingClick(tradeSimManualRef.current) && zone === HIT_ZONES.BAR) {
      const { state, trade } = registerManualClick(
        tradeSimManualRef.current,
        { index: dataIndex, date, price },
        tradeSimBarsRef.current || [],
        assessmentBoundaryRef.current,
      );
      setTradeSimManual(state);
      if (trade) setTradeSimSession(prev => recordClick(prev, trade));
      return;
    }

    // ── Alpha Replay owns the click ──
    //
    // Placed before the drawing tools and returning, because the mode changes the cursor to a
    // crosshair and says bar-click targeting is what clicks now do. A click that both moved the
    // replay boundary and dropped a Fibonacci point would be one gesture with two results, and the
    // cursor would be lying about one of them.
    if (simulateMode) {
      const from = simulateRevealRef.current;
      const wanted = revealTargetFor(
        dataIndex, from, simulateOriginalRef.current?.ohlc?.length || 0,
        simulateCtsIndicesRef.current);
      /*
       * ⚠ NO CLAMP. A canvas click FAST-FORWARDS to the next CTS point, always.
       *
       * It used to be held to one bar whenever a trade was open — the progression gate, and a
       * sentinel for an open manual position. Both are gone: the canvas gesture's whole value
       * is high-probability navigation, and a jump that silently became a single step is the
       * gesture not working. The gate still TRACKS an unresolved trade and still re-prices it
       * as the playhead moves; what it no longer does is brake. `--` the banner says a trade
       * is open and unresolved, which is the information without the constraint.
       */
      const target = wanted;
      if (target !== null && target !== from) {
        revealTo(target);
        // SAID OUT LOUD, because a CTS stop can be a long way from the bar that was clicked --
        // on the revealed side it is unbounded by construction. A playhead that leaps thirty
        // bars with nothing to explain it reads as a bug; naming the date makes it a feature a
        // reader can check against the pillar it landed on.
        const landed = simulateCtsIndicesRef.current.includes(target)
          && target !== (Number.isFinite(from) ? from : -1);
        if (landed) {
          const on = simulateOriginalRef.current?.dates?.[target];
          setStatusText(`Advanced to the CTS point${on ? ` on ${on}` : ''} (bar ${target + 1}).`);
        }
      }
      return;
    }

    // ── Drawing tool modes ──
    if (drawingMode) {
      // ---- Technical tools: Fibonacci (2 clicks), extended trendline and pitchfork (3) ----
      //
      // All three compute in (barIndex, price) space and emit ordinary `trendline` overlays, one
      // per line, sharing a groupId. Reusing the proven two-point renderer means no new drawing
      // path to get wrong; the groupId is what lets a whole figure be identified as one shape.
      //
      // They carry their own `source`, NOT MANUAL_DRAW_SOURCE, so construction geometry never
      // picks up a measurement badge or a line in the journal's move summary — a Fibonacci level
      // is not a trade anyone took.
      if (TOOL_CLICKS[drawingMode]) {
        const needed = TOOL_CLICKS[drawingMode];
        drawingPoints.current = [...drawingPoints.current, { index: dataIndex, price, date }];
        const pts = drawingPoints.current;

        if (pts.length < needed) {
          setStatusText(`${drawingMode.replace('draw_', '').replace(/_/g, ' ')}: point ${pts.length} of ${needed} set. Click ${needed - pts.length} more.`);
          return;
        }
        drawingPoints.current = [];
        setDrawingMode(null);

        // Projected against the EXTENDED dates, which is what the chart actually renders and what
        // the click's dataIndex is an index into. Using barData here was the bug behind pitchfork
        // rays stopping dead at the last price bar: clampIndex pinned every projection to the last
        // REAL bar, so the forks could never reach the ~250 padded future bars already on screen.
        // Real bars occupy the same indices in both arrays — padding is appended — so nothing else
        // shifts.
        const dates = extendedBarData?.dates?.length ? extendedBarData.dates : (barData?.dates || []);
        const lastIndex = dates.length - 1;

        // How far forward a projection may run. Bounded rather than run to the end of the padding:
        // a fork extended 250 bars past the data is mostly empty space, and its rays fan so wide
        // that the part near the pivots — the part being read — is squeezed flat.
        const lastRealIndex = Math.max(0, (barData?.dates?.length || dates.length) - 1);
        const projectionEnd = Math.min(lastIndex, lastRealIndex + FUTURE_PROJECTION_BARS);

        const dateAt = i => dates[clampIndex(i, lastIndex)];
        const groupId = `${drawingMode}-${Date.now()}`;
        const line = (from, to, extra = {}) => addDrawing({
          type: 'trendline',
          date1: dateAt(from.index), price1: from.price,
          date2: dateAt(to.index), price2: to.price,
          color: activeDrawingColor,
          source: TECHNICAL_DRAW_SOURCE,
          groupId,
          id: `${groupId}-${extra.part || 'line'}`,
          ...extra,
        });

        if (drawingMode === 'draw_fibonacci') {
          const [p1, p2] = pts;
          line(p1, p2, { part: 'trend' });   // the move being retraced

          // Segments come from one function so the 0%/100% boundaries and the interior levels
          // share an X-span by construction rather than by two call sites agreeing.
          //
          // `maxIndex` is the same forward budget the pitchfork and the extended trendline use, so
          // the levels run into the future workspace but stop where every other projection stops.
          // Without it a retracement drawn near the end of the padding would push its labels out
          // to the far right of the axis and straight through the price scale.
          const segments = fibonacciSegments(p1, p2, { maxIndex: projectionEnd });
          segments.forEach(seg => line(seg.from, seg.to, {
            part: seg.part,
            lineType: seg.lineType,
            lineWidth: seg.lineWidth,
            endLabel: seg.label,
            endLabelPosition: seg.labelPosition,
          }));
          setStatusText(`Fibonacci retracement drawn (${segments.length} levels).`);
          return;
        }

        if (drawingMode === 'draw_fib_time') {
          const [q1, q2] = pts;
          // Projected against `dates`, which is the EXTENDED array here, so targets can land in
          // the padded future space rather than being clamped to the last price bar.
          const overlays = buildFibTimeOverlays(q1, q2, dates, {
            color: activeDrawingColor,
            groupId,
            lastRealIndex,
          });
          if (overlays.length === 0) {
            setStatusText('Fib Time: those two points give no interval to project.');
            return;
          }
          // addOverlay, not addDrawing: addDrawing takes ONE overlay and spreads it, so an array
          // would become {0:..., 1:...}. The context flattens arrays, and every line already
          // carries the shared groupId, so they select, delete and undo as a single figure.
          addOverlay(overlays);
          const future = overlays.filter(o => o.isFuture).length;
          setStatusText(`Fib Time: ${overlays.length - 2} projection(s) from ${dateAt(q2.index)}`
            + `${future ? `, ${future} in future space` : ''}. Interval ${q2.index - q1.index} bars.`);
          return;
        }

        if (drawingMode === 'draw_extended_trendline') {
          const [p1, p2, p3] = pts;
          const end = extendedTrendlineEnd(p1, p2, p3);
          if (!end) { setStatusText('Extended trendline needs two points on different bars.'); return; }
          line(p1, end, { part: 'extended' });
          setStatusText(`Trendline extended to ${dateAt(end.index)} (${end.price.toFixed(2)}).`);
          return;
        }

        if (drawingMode === 'draw_pitchfork') {
          const [p1, p2, p3] = pts;
          const fork = pitchfork(p1, p2, p3, projectionEnd);
          if (!fork) { setStatusText('Pitchfork needs three pivots that define a direction.'); return; }
          line(fork.median.from, fork.median.to, { part: 'median', lineWidth: 2 });
          line(fork.upper.from, fork.upper.to, { part: 'upper', lineType: 'dashed' });
          line(fork.lower.from, fork.lower.to, { part: 'lower', lineType: 'dashed' });
          line(fork.base.from, fork.base.to, { part: 'base' });
          setStatusText('Andrews’ pitchfork drawn.');
          return;
        }
      }

      // Vertical and horizontal SEGMENTS, not full-span lines.
      //
      // Both take two clicks, and the second click contributes only ONE of its coordinates: it
      // acts as a boundary line rather than as an endpoint. For a vertical segment the second
      // click supplies its price via an imaginary horizontal line through it, and its date is
      // discarded; for a horizontal segment the reverse. That is what lets a segment be bounded
      // by a point that is nowhere near the line itself.
      //
      // Emitted as `trendline`, which already renders a 2-point series between (date1, price1)
      // and (date2, price2) — a segment with one coordinate held constant is exactly that, so
      // there is no new rendering path to write or keep working. `tool` records which drawing
      // tool produced it, since the geometry alone no longer says.
      if (drawingMode === 'draw_vertical_line') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Vertical segment: start set at ${date}. Click to set how far it extends.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          tool: 'verticalSegment',
          date1: first.date,
          price1: first.price,
          date2: first.date,          // X held at the first click
          price2: price,              // Y bounded by the second click
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-vseg-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Vertical segment drawn at ${first.date}`);
        return;
      }
      if (drawingMode === 'draw_horizontal_line') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Horizontal segment: start set at ${Math.round(price * 100) / 100}. Click to set how far it extends.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          tool: 'horizontalSegment',
          date1: first.date,
          price1: first.price,
          date2: date,                // X bounded by the second click
          price2: first.price,        // Y held at the first click
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-hseg-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Horizontal segment drawn at ${Math.round(first.price * 100) / 100}`);
        return;
      }
      if (drawingMode === 'draw_fixed_cycle') {
        const cycleDates = extendedBarData?.dates?.length ? extendedBarData.dates : (barData?.dates || []);
        const lastReal = Math.max(0, (barData?.dates?.length || cycleDates.length) - 1);
        const overlays = buildFixedCycleOverlays(dataIndex, fixedCycleLength, cycleDates, {
          color: activeDrawingColor,
          groupId: `fixedcycle-${dataIndex}-${fixedCycleLength}-${Date.now()}`,
          lastRealIndex: lastReal,
          unit: fixedCycleUnit,
        });
        if (overlays.length === 0) {
          setStatusText('Fixed cycle: could not anchor there.');
          return;
        }
        // Replacing rather than stacking: two cycles of the same length on one chart is the same
        // statement twice, and the second would be indistinguishable from the first.
        setAllOverlays([...withoutFixedCycles(overlaysRef.current), ...overlays]);
        setDrawingMode(null);
        drawingFirstClick.current = null;
        const forward = overlays.filter(o => o.cycleK > 0).length;
        const back = overlays.filter(o => o.cycleK < 0).length;
        setStatusText(`Fixed cycle of ${fixedCycleLength} `
          + `${fixedCycleUnit === 'days' ? 'calendar day(s)' : 'bar(s)'} anchored at ${date}: `
          + `${back} back, ${forward} forward.`);
        return;
      }

      if (drawingMode === 'draw_vertical_full') {
        // A full-height marker, not a measured move: one click, spanning the whole pane.
        //
        // Emitted as `vLine`, which renders as a markLine on the x-axis and therefore always
        // reaches top to bottom whatever the price scale does. Deliberately NOT a `trendline`
        // with equal endpoints: the measurement badges are attached to manually drawn trendlines,
        // so that shape would earn a "+0.00 pts (+0.00%)" callout on a line that measures nothing.
        addDrawing({
          type: 'vLine',
          date,
          color: activeDrawingColor,
          thickness: manualDrawStyle?.lineWidth || 2,
          label: '',
          id: `draw-vfull-${Date.now()}`,
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Vertical line drawn at ${date}`);
        return;
      }
      if (drawingMode === 'draw_circle_empty' || drawingMode === 'draw_circle_filled') {
        const filled = drawingMode === 'draw_circle_filled';
        addDrawing({
          type: 'pivotMarkers',
          // Reuses the scatter renderer the pivot studies already use, so there is no new drawing
          // path to get wrong — only the symbol and size differ.
          symbol: filled ? 'circle' : 'emptyCircle',
          symbolSize: activeTextSize.px,
          color: activeDrawingColor,
          label: filled ? 'Filled circle' : 'Circle',
          points: [{ date, price, color: activeDrawingColor }],
          id: `draw-circle-${filled ? 'filled' : 'empty'}-${Date.now()}`,
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`${filled ? 'Filled' : 'Empty'} circle placed at ${date} (${activeTextSize.label}).`);
        return;
      }
      if (drawingMode === 'draw_trendline') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Trendline: first point set at ${date}. Click second point.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          date1: first.date,
          price1: first.price,
          date2: date,
          price2: price,
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-trend-${Date.now()}`
        });
        // STICKY: the mode is NOT cleared here, unlike every other tool in this handler.
        //
        // Trendlines are the one tool that is almost never used once. Drawing a second meant going
        // back to the toolbar, and the trip is longer than the drawing. Only the first-click anchor
        // is reset, which is what arms the next line.
        //
        // Two ways out, and both already existed: clicking the toolbar button again toggles the
        // mode off, and Escape clears it along with every other drawing mode. Neither needed new
        // code - which is the reason this is safe to leave latched. A mode with no exit is a trap,
        // and the exits are what make it a mode rather than one.
        drawingFirstClick.current = null;
        setStatusText(`Trendline drawn from ${first.date} to ${date}. `
          + 'Click to start another, or press Esc to stop.');
        return;
      }
      if (drawingMode === 'draw_rectangle') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Rectangle: first corner set at ${date}. Click opposite corner.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'drawRect',
          date1: first.date,
          price1: first.price,
          date2: date,
          price2: price,
          // Fill and border cannot be the same value: a rectangle filled at full opacity would
          // hide the candles it is drawn to frame.
          color: withAlpha(activeDrawingColor, 0.18),
          borderColor: activeDrawingColor,
          id: `draw-rect-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Rectangle drawn from ${first.date} to ${date}`);
        return;
      }
      if (drawingMode === 'draw_text') {
        addDrawing({
          type: 'textAnnotation',
          date,
          price,
          dataIndex,
          text: 'Note',
          color: activeDrawingColor,
          // Stored per note rather than read from current state at render time, so changing the
          // tool size later does not silently resize every annotation already on the chart.
          fontSize: activeTextSize.px,
          id: `draw-text-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Text annotation placed at ${date}. Double-click to edit.`);
        return;
      }
    }

    // Spiral-on-click, after the drawing tools and before the two-bar selection.
    //
    // The order is the point: a drawing tool in progress owns the click, because the user is midway
    // through placing a figure and stealing that click would abandon it. With no tool active the
    // click is free, and this consumes it rather than falling through — running an inspection AND
    // moving a bar-selection marker from one gesture would be two results for one click.
    if (spiralClickRef.current) {
      spiralClickRef.current(dataIndex, date);
      return;
    }

    if (isManualDrawMode) {
      addOverlay({
        type: 'verticalLine',
        source: 'manualCycle',
        date,
        color: manualDrawStyle.color,
        thickness: manualDrawStyle.lineWidth,
        id: `manual-cycle-${Date.now()}`
      });
      setStatusText(`Added manual cycle line at ${date}`);
      // Do not return here; allow bar selection to proceed
    }

    if ((nextBarToSelect === 'bar1' && date === bar2Date) || (nextBarToSelect === 'bar2' && date === bar1Date)) {
      setStatusText('Cannot select the same bar for both start and end points.');
      return;
    }

    if (nextBarToSelect === 'bar1') {
      setBar1Date(date);
      setNextBarToSelect('bar2');
      setStatusText(`Bar 1 set to ${date} (Bar #${dataIndex + 1}). Now select Bar 2.`);
    } else {
      const firstDate = bar1Date;
      const secondDate = date;

      if (new Date(secondDate) < new Date(firstDate)) {
        setBar1Date(secondDate);
        setBar2Date(firstDate);
        setStatusText(`Dates set: ${secondDate} and ${firstDate}. Swapped for chronological order.`);
      } else {
        setBar2Date(secondDate);
        setStatusText(`Dates set: ${firstDate} and ${secondDate}.`);
      }
      
      setNextBarToSelect('bar1');
    }
    // Deliberately omits activeTextSize and the two bar-data arrays. This callback is handed to
    // EChartComponent as onBarSelect, and that component states the constraint plainly: a handler
    // recreated each render makes echarts-for-react dispose and rebuild the whole chart, which
    // shows as flicker. Rebuilding it whenever price data arrives is the same fault the
    // overlaysRef above exists to avoid — the values are read when the handler fires, which is
    // after any of them could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOverlay, addDrawing, setAllOverlays, activeDrawingColor, bar1Date, bar2Date, drawingMode, fixedCycleLength, fixedCycleUnit, isManualDrawMode, manualDrawStyle, nextBarToSelect,
    simulateMode, revealTo]);


  const handleDisplayCycles = (dialogState) => {
    if (bar1Date && bar2Date) {
      addOverlay({
        type: 'cycleLines',
        ...dialogState,
        bar1Date,
        bar2Date,
        id: `cycle-${Date.now()}` // Unique ID for the new overlay
      });
    }
  };

  const handleEraseCycles = () => {
    // Filter out both calculated and manual cycle lines
    const newOverlays = overlays.filter(o => o.type !== 'cycleLines' && o.source !== 'manualCycle');
    setAllOverlays(newOverlays);
  };

  const handleManualDrawToggle = (isEnabled, style) => {
    setIsManualDrawMode(isEnabled);
    if (isEnabled) {
      setManualDrawStyle(style);
    }
  };

  const handleZoomRangeChange = useCallback((startBar, endBar) => {
    const minBar = Math.min(startBar, endBar) + 1;
    const maxBar = Math.max(startBar, endBar) + 1;
    setStatusText(`Zoomed bars: ${minBar} to ${maxBar}`);
  }, []);

  const handleTriggerAreaZoom = () => {
    if (eChartRef.current) {
      const chart = eChartRef.current.getEchartsInstance();
      if (chart) {
        chart.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true
        });
      }
    }
  };

  /* ══ THE COMMAND REGISTRY ═══════════════════════════════════════════════════════════════════
   *
   * Everything from here to the `return` exposes this component's setters to
   * `context/CommandContext`, so a Command Bus can drive the chart without a DOM click.
   *
   * <h2>Why it sits HERE, at the bottom</h2>
   *
   * The dialog map below names about forty setters, and a `useEffect` dependency array is
   * evaluated during RENDER — so an effect placed above a `const` it names in its deps throws
   * `Cannot access '…' before initialization` and mounts nothing. That has happened four times
   * in this file and `App.tdz.test.js` exists for it. Declared last, every setter it touches is
   * already initialised, and the dependency array names one stable function.
   *
   * <h2>Registered ONCE, at mount, and that is safe BY CONSTRUCTION</h2>
   *
   * A mount-only registration captures first-render closures, which is normally the stale-closure
   * trap. It is safe here because every handler below uses only things React guarantees stable —
   * `useState` setters, and refs for anything live. Nothing closes over `barData`, `isAdmin` or
   * any other render-scoped value. Re-registering on every render would also work and would cost
   * ~40 closures per render of the heaviest component in the app, for no gain.
   */

  /**
   * THE ACTIVE PREFILL PER DIALOG, which the Command Bus writes and the dialogs read.
   *
   * <h2>⚠ HERE RATHER THAN IN CommandContext, deliberately</h2>
   *
   * The brief asks for it in the context. The context's value is FROZEN -- a `useMemo` with an
   * empty dependency list -- precisely so registering a handler re-renders nothing; putting
   * state in it would change its identity on every prefill and re-render every consumer,
   * undoing the decision the whole file is built on. The dialogs are rendered HERE, so the
   * prefill belongs here and reaches them as an ordinary prop.
   *
   * <p>One object keyed by dialog id, so a prefill for one dialog cannot disturb another's.
   * Each value is a stable object identity until the next command replaces it, which is what
   * `useInitialConfig` compares against -- see that module for why identity and not a deep
   * compare.
   */
  const [dialogPrefills, setDialogPrefills] = useState({});

  /**
   * MOA's open state, SECOND SOURCE.
   *
   * <p>`MOAStrategyDialog` is mounted in TabPanel and opened from TabPanel's own local state --
   * App never passed an opener, which is why the toolbar's `open_moa_strategy_dialog` falls
   * through to that local fallback. A command has no way to reach it.
   *
   * <p>So App keeps its own boolean and TabPanel ORs the two. Two sources, one derived: either
   * may open the dialog and closing clears both. The alternative -- lifting the state out of
   * TabPanel -- is a larger change to a component that has no other reason to be touched.
   */
  const [moaDialogOpen, setMoaDialogOpen] = useState(false);

  /** Entitlement, read at call time. The `apiRef` pattern a few hundred lines above. */
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  /*
   * ⚠ THE OTHER FIVE THE BUS READS, all for the same reason as `isAdminRef` above: the command
   * handlers are registered ONCE at mount, so anything they close over is frozen at that
   * moment. For `toolbarProfile` and `isAdmin` a frozen value is a stale SECURITY VERDICT; for
   * the three callbacks it is a handler holding the first render's `barData`, which is the
   * "closure read the wrong bars" defect this file already paid for once in `handleBarSelection`.
   */
  // ⚠ A REF FOR THE SAME REASON, and it is the ALREADY-GATED value. Closing over `productTarget`
  // would freeze the target at mount, so an administrator who switched product would keep the
  // Copilot gating on the old one — the toolbar would re-filter and the command bus would not,
  // which is the two-axes-disagree failure the PRODUCT gate step was added to prevent.
  const productTargetRef = useRef(productTarget);
  productTargetRef.current = productTarget;

  /*
   * ⚠ THE AUTH READOUT, in development only, and it answers ONE question: did the backend send
   * a role, or did the frontend ignore it?
   *
   * Those two look identical on screen — the astro toolbar either way — and the pill in the
   * header shows the resolved state while this shows the RAW profile the moment it lands. Keyed
   * on uid and role so it prints once per sign-in rather than on every render.
   */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    // eslint-disable-next-line no-console
    console.log('[DEBUG AUTH] User Profile:', {
      uid: user?.uid,
      email: user?.email,
      role: user?.role ?? null,
      subscriptionType: user?.subscriptionType ?? null,
      isAdmin,
      buildTarget: ACTIVE_PRODUCT,
      effectiveTarget: productTarget,
      // ⚠ The host axis is SEPARATE and defaults to ASC for any unrecognised domain, localhost
      // included. It drives the Copilot's host boundary, not the toolbar.
      host: describeHostContext(),
    });
  }, [user, isAdmin, productTarget]);
  const handleRunEngineRef = useRef(handleRunEngine);
  handleRunEngineRef.current = handleRunEngine;
  const handleRunCycleBandRef = useRef(handleRunCycleBand);
  handleRunCycleBandRef.current = handleRunCycleBand;
  const handleRunEclipseStudyRef = useRef(handleRunEclipseStudy);
  handleRunEclipseStudyRef.current = handleRunEclipseStudy;
  const runAspectStudyRef = useRef(handleRunAspectStudy);
  runAspectStudyRef.current = handleRunAspectStudy;

  /**
   * THE HEADER PILLS' TWO ACTIONS.
   *
   * <p>⚠ REMOVAL IS BY GROUP, over `overlaysRef` rather than `overlays`. A question's bands are
   * dozens of overlays and one answer, so the ✕ must take all of them — and reading the ref
   * keeps these callbacks off the overlay list, which changes on every draw and would otherwise
   * rebuild them continuously.
   *
   * <p>The gear routes through the same `dialogPrefills` machinery `dialog.open:areaHighlight`
   * uses, rather than a second path into the dialog: one way in means the prefill is applied the
   * same way whether the Copilot asked or a reader clicked, and the applier is only written once.
   */
  /**
   * The pill's ✕ — removes every overlay of the thing the pill names.
   *
   * <p>⚠ IT MATCHES `runId` OR `groupId`, because the key it is handed may be either.
   * `activeAreaHighlights` groups a study by its RUN and the cycle pillars by their GROUP, and it
   * returns whichever it used under the name `groupId`. Matching only `o.groupId` would make the ✕
   * on a study pill a silent no-op: the handler would find nothing, remove nothing, and the pill
   * would stay on screen with no error anywhere.
   *
   * <p>That also makes one press clear the whole run — the bands, both vertical markers per
   * interval, the trendlines and the horizontal bands — because `addAreaHighlightOverlaysAndMessages`
   * stamps the run id on all of them. A ✕ that left the vertical lines behind would be a
   * half-dismissed study, which is worse than one that does nothing.
   */
  const handleRemoveOverlayGroup = useCallback((key) => {
    if (!key) return;
    (overlaysRef.current || []).forEach((o) => {
      if (o && (o.runId === key || o.groupId === key)) removeOverlay(o.id);
    });
  }, [removeOverlay]);

  const handleEditAreaHighlight = useCallback((prefill) => {
    setDialogPrefills(prev => ({ ...prev, areaHighlight: { ...prefill } }));
    setIsAreaHighlightDialogOpen(true);
  }, []);

  const runAreaHighlightStudyRef = useRef(handleRunAreaHighlightStudy);
  runAreaHighlightStudyRef.current = handleRunAreaHighlightStudy;
  const clearOverlaysRef = useRef(clearOverlays);
  clearOverlaysRef.current = clearOverlays;

  /*
   * The bar series, read at call time for the same reason.
   *
   * A headless strategy run needs the symbol and the date range, and the registration is
   * mount-only -- so closing over `barData` would send the first render's value, which is null.
   * Assigned during render, so they are current before any command can arrive.
   */
  const barDataRef = useRef(barData);
  barDataRef.current = barData;
  const extendedBarDataRef = useRef(extendedBarData);
  extendedBarDataRef.current = extendedBarData;

  /**
   * THE DIALOG MAP: one id per addressable dialog, each pointing at its own boolean setter.
   *
   * <p>A frozen module-shaped literal built once, because the setters are stable. Ids are stable
   * names a Copilot can be taught; the internal state names are not — several are historical
   * (`setStudyDialogOpen` opens the Controls dialog) and renaming state to match an id would be
   * a large edit for a cosmetic reason.
   *
   * <p>⚠ NOT every boolean in this component. `simulateMode`, `studyMode`, `presentationMode`,
   * `showRSI` and the rest are MODES rather than dialogs — some have their own command, and the
   * others are deliberately not addressable yet. Listing them here would make `closeDialog`
   * able to silently leave a mode on.
   */
  const commandDialogsRef = useRef(null);
  if (commandDialogsRef.current === null) {
    commandDialogsRef.current = {
      /* the four Strategy Designers — the Command Bus's primary targets */
      strategyDesign: setIsStrategyDesignDialogOpen,
      strategy2Design: setIsStrategy2DesignDialogOpen,
      timeAndEventStrategy: setTimeAndEventStrategyDialogOpen,
      /* studies */
      controls: setStudyDialogOpen,
      // The canonical id for the command bus. `controls` is the toolbar action's own name and
      // stays, so nothing that already used it breaks; `aspectStudy` is what a sentence means.
      aspectStudy: setStudyDialogOpen,
      planetFollowStudy: setPlanetFollowStudyDialogOpen,
      aspectsAreaHighlight: setIsAspectsAreaHighlightDialogOpen,
      declinationStudy: setDeclinationStudyDialogOpen,
      planetaryLines: setPlanetaryLinesDialogOpen,
      averageLongitude: setAverageLongitudeDialogOpen,
      visualUniverse: setVisualUniverseDialogOpen,
      intradayAnimation: setIntradayAnimationDialogOpen,
      chartComparison: setChartComparisonDialogOpen,
      areaHighlight: setIsAreaHighlightDialogOpen,
      aggregateAreaHighlight: setIsAggregateAreaHighlightDialogOpen,
      planetGroup: setPlanetGroupDialogOpen,
      strategyReportStudy: setStrategyReportStudyDialogOpen,
      strategyReportStudyB: setStrategyReportStudyBDialogOpen,
      planetCharacteristics: setIsPlanetCharacteristicsDialogOpen,
      astroGraph: setIsAstroGraphDialogOpen,
      patternConfig: setIsPatternConfigDialogOpen,
      patternPrediction: setPatternPredictionOpen,
      cycles: setCyclesDialogOpen,
      fixedCycle: setFixedCycleDialogOpen,
      gannScan: setGannDialogOpen,
      spiralMode: setSpiralDialogOpen,
      elliottWave: setElliottOpen,
      chartAnalytica: setAnalyticaOpen,
      horizontalLine: setSrDialogOpen,
      /* admin research */
      deepPivot: setDeepPivotOpen,
      planetaryAnalog: setPlanetaryAnalogOpen,
      pivotCommonalities: setPivotCommonalitiesOpen,
      celestialAspects: setCelestialOpen,
      chartEndDates: setChartEndDatesOpen,
      marketScanner: setScannerOpen,
      scanViewer: setScanViewerOpen,
      /* engines and simulation */
      moa: setMoaDialogOpen,
      dynaManSimulator: setDynaManSimulatorOpen,
      dynaManProduction: setDynaManProductionOpen,
      portfolioManager: setPortfolioOpen,
      simulatorSetup: setSimulatorSetupOpen,
      tradeSimulator: setTradeSimOpen,
      /* chrome */
      configuration: setConfigurationDialogOpen,
      messages: setMessagesDialogOpen,
      messagesPanel: setMessagesOpen,
      appBrand: setBrandDialogOpen,
      locationSettings: setLocationSettingsOpen,
    };
  }

  /**
   * The dialogs that genuinely accept an initial value, and the ONLY ones a prefill may be sent
   * to.
   *
   * <p>⚠ ONE, and the audit explains why: no Strategy Designer takes an initial-config prop —
   * every one holds its whole form in local `useState` from literal constants. Opening a dialog
   * and dropping the prefill would leave the caller believing it had been applied, so
   * `openDialog` REFUSES a prefill it cannot honour rather than discarding it. Making the four
   * designers prefillable is a change to those components and is blocker 4 of the audit, still
   * open.
   */
  const commandPrefillRef = useRef(null);
  if (commandPrefillRef.current === null) {
    commandPrefillRef.current = {
      // `handleOpenVisualUniverse(date)` sets the initial date and then opens.
      visualUniverse: (prefill) => {
        if (prefill.date === undefined) return { ok: false, why: 'expects { date }' };
        handleOpenVisualUniverse(prefill.date);
        return { ok: true };
      },
    };
    /*
     * THE FOUR STRATEGY DESIGNERS, prefillable since 2026-10-24 — audit blocker 4.
     *
     * Each takes an `initialConfig` prop and applies it once per distinct object. The applier
     * is identical for all four, so it is generated rather than written out: four copies would
     * be four chances for one to store under the wrong key, and that failure is a dialog that
     * opens with the previous command's configuration.
     *
     * MOA's prefill is threaded through TabPanel, which owns its mount.
     *
     * ⚠ AN UNKNOWN FIELD IS REFUSED, with the valid set listed. Dropping it silently would open
     * the dialog, change nothing and report success — and the caller is a Copilot translating
     * somebody's words, so a field name it guessed wrong is the expected case rather than a
     * rare one. Same precedent as `ctsDirection`: Spring drops an unparseable parameter, so an
     * unknown value is rejected rather than quietly running the default. The field lists are
     * exported by the dialogs themselves, beside the appliers that read them.
     */
    Object.entries({
      aspectStudy: ASPECT_STUDY_PREFILL_FIELDS,
      controls: ASPECT_STUDY_PREFILL_FIELDS,
      strategyDesign: STRATEGY1_PREFILL_FIELDS,
      strategy2Design: STRATEGY2_PREFILL_FIELDS,
      timeAndEventStrategy: TIME_EVENT_PREFILL_FIELDS,
      moa: MOA_PREFILL_FIELDS,
      areaHighlight: AREA_HIGHLIGHT_PREFILL_FIELDS,
    }).forEach(([id, fields]) => {
      commandPrefillRef.current[id] = (prefill) => {
        if (typeof prefill !== 'object' || Array.isArray(prefill)) {
          return { ok: false, why: 'expects an object of field values' };
        }
        const unknown = Object.keys(prefill).filter(k => !fields.includes(k));
        if (unknown.length > 0) {
          return {
            ok: false,
            why: `does not have ${unknown.map(k => `"${k}"`).join(', ')}. `
               + `Its fields are: ${fields.join(', ')}.`,
          };
        }
        // A NEW OBJECT every time, so `useInitialConfig` sees a new identity and re-applies
        // even when the same prefill is sent twice. Spreading also stops a caller mutating
        // their own object afterwards and silently changing what the dialog holds.
        setDialogPrefills(prev => ({ ...prev, [id]: { ...prefill } }));
        const setter = commandDialogsRef.current[id];
        if (setter) setter(true);
        return { ok: true };
      };
    });
  }

  /**
   * COMMANDS WAITING FOR THEIR SYMBOL'S BARS.
   *
   * <p>State rather than a ref, following `pendingScan` a few thousand lines above: the drain is
   * an effect, and an effect has to be woken by a dependency changing. Queueing therefore costs
   * one render, which is what the existing pattern already costs.
   */
  const [pendingCommands, setPendingCommands] = useState([]);

  /**
   * DRAIN: run each queued command once its OWN symbol's bars are on the chart.
   *
   * <p>`barData.symbol` is the authority, not `selectedSymbol`. The symbol state commits
   * immediately and the clear effect fires off it, while the bars arrive a fetch later — so
   * `barData.symbol === cmd.symbol` is the one condition that means "the new bars are here AND
   * the clear that would have wiped this paint has already happened". It is the same test
   * `scannerPendingDraw` uses.
   *
   * <p>Three outcomes, because a promise that never settles is worse than one that rejects:
   * ready, failed (the fetch errored while this symbol was selected), or superseded (something
   * moved the symbol on after we had seen ours applied).
   */
  useEffect(() => {
    if (pendingCommands.length === 0) return;

    // Recorded BEFORE partitioning: `sawSymbol` is what lets a later move away from this
    // command's symbol count as a supersede rather than as "not applied yet". Mutating our own
    // queue entry, never a rendered value.
    pendingCommands.forEach((cmd) => { if (selectedSymbol === cmd.symbol) cmd.sawSymbol = true; });

    const { settle, waiting, changed } = partitionPendingCommands(pendingCommands, {
      selectedSymbol, barSymbol: barData?.symbol, loading, error,
    });
    if (!changed) return;
    setPendingCommands(waiting);

    settle.forEach(({ cmd, disposition, reason }) => {
      if (cmd.timer) clearTimeout(cmd.timer);
      if (disposition !== DISPOSITION.READY) {
        cmd.reject(new Error(`${disposition}: ${reason}`));
        return;
      }
      try {
        // Handed the EXTENDED series as well, because a paint is almost always built against it:
        // 250 padded weekday columns carry the future marks a study draws, and building against
        // the raw series makes every one of them fail the column lookup and vanish.
        cmd.resolve(typeof cmd.run === 'function'
          ? cmd.run({ symbol: cmd.symbol, barData, extendedBarData })
          : { symbol: cmd.symbol, barData, extendedBarData });
      } catch (e) {
        cmd.reject(e);
      }
    });
  }, [pendingCommands, selectedSymbol, barData, extendedBarData, loading, error]);

  /**
   * A live view of the queue, so the unmount cleanup can settle what is still waiting.
   *
   * <p>Declared BEFORE the effect that reads it: an effect body runs after the commit and would
   * be safe either way, but a reference above its own declaration is the shape `App.tdz.test.js`
   * exists to catch and is not worth writing deliberately.
   */
  const pendingCommandsRef = useRef([]);
  pendingCommandsRef.current = pendingCommands;

  /** Nothing may be left holding an unsettled promise when the session is torn down. */
  useEffect(() => () => {
    pendingCommandsRef.current.forEach((cmd) => {
      if (cmd.timer) clearTimeout(cmd.timer);
      cmd.reject(new Error('The chart was unmounted before the symbol finished loading.'));
    });
  }, []);

  const { registerCommandHandlers } = useCommandContext();

  /**
   * Queue one symbol-dependent command. ONE implementation, two commands.
   *
   * <p>`setSymbol` is `executeWithSymbol` with no callback — the waiting, the timeout and the
   * three outcomes are identical, and two copies of that would be two places for the drain's
   * contract to drift from.
   */
  const queueSymbolCommandRef = useRef(null);
  queueSymbolCommandRef.current = (label, symbol, run) => new Promise((resolve, reject) => {
    const clean = String(symbol || '').trim();
    if (!clean) { reject(new Error(`${label} needs a symbol.`)); return; }
    const cmd = { symbol: clean, run, resolve, reject, sawSymbol: false, timer: null };
    cmd.timer = setTimeout(() => {
      setPendingCommands(prev => prev.filter(c => c !== cmd));
      reject(new Error(
        `Timed out after ${COMMAND_SYMBOL_TIMEOUT_MS}ms waiting for ${clean} bars.`));
    }, COMMAND_SYMBOL_TIMEOUT_MS);
    setPendingCommands(prev => [...prev, cmd]);
    setSelectedSymbol(clean);
  });

  useEffect(() => registerCommandHandlers({
    /* ── the symbol, and everything that has to wait for it ───────────────────────────────── */
    executeWithSymbol: (symbol, afterBarsLoaded) =>
      queueSymbolCommandRef.current('executeWithSymbol', symbol, afterBarsLoaded),

    setSymbol: (symbol) => queueSymbolCommandRef.current('setSymbol', symbol, null),

    /* ── the view ─────────────────────────────────────────────────────────────────────────── */
    /*
     * ONE state, two command names. `chartType` IS the view mode -- there is no second
     * `viewMode` -- and its values are line / candlestick / hidden, not the brief's
     * line / candlestick / bar. An unknown view is REFUSED rather than set: the renderer would
     * draw nothing for it, which looks like a broken chart rather than a rejected command.
     *
     * `hidden` is admin-only. normalizeChartView is what a non-admin's request is measured
     * against, so asking for it is refused here rather than silently normalised -- a command
     * that reports success and leaves the chart as it was is the worse of the two.
     */
    setChartType: (view) => {
      const requested = String(view || '').toLowerCase();
      const allowed = cycleFor(isAdminRef.current);
      if (!allowed.includes(requested)) {
        return { ok: false, refusal: { code: REFUSAL.INVALID,
          message: `Unknown chart view "${view}". This entitlement allows: ${allowed.join(', ')}.` } };
      }
      setChartType(requested);
      return { ok: true };
    },

    /* ── indicators ───────────────────────────────────────────────────────────────────────── */
    /*
     * The FUNCTIONAL form, so an absent argument flips and a boolean sets. Reading the current
     * value to decide would need the caller to be inside React, which a Command Bus is not.
     */
    toggleRSI: (show) => {
      setShowRSI(prev => (show === undefined ? !prev : !!show));
      return { ok: true };
    },
    toggleMACD: (show) => {
      setShowMACD(prev => (show === undefined ? !prev : !!show));
      return { ok: true };
    },

    /* ── the viewport ─────────────────────────────────────────────────────────────────────── */
    /*
     * ⚠ THROUGH THE CHART'S IMPERATIVE HANDLE, NEVER THROUGH STATE, and this is the only
     * command here that is. The zoom window lives in EChartComponent's `zoomRef` on purpose --
     * its own datazoom handler records that setting state there "would trigger memo
     * recomputation -> notMerge:true full rebuild on every scroll, which corrupts the chart".
     * Routing a command through a setter would do that to every caller and would drop the
     * overlays they had just drawn.
     *
     * The ref is read at CALL time, so a mount-only registration is correct: `eChartRef.current`
     * is null during the first render and is the chart by the time any command arrives.
     */
    focusDateRange: (fromDate, toDate) => {
      const handle = eChartRef.current;
      if (!handle || typeof handle.focusDateRange !== 'function') {
        return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
          message: 'No chart is mounted.' } };
      }
      const moved = handle.focusDateRange(fromDate, toDate);
      if (!moved) {
        return { ok: false, refusal: { code: REFUSAL.INVALID,
          message: `No bars between ${fromDate || 'the start'} and ${toDate || 'the end'} `
            + 'on the loaded series. Dates must be yyyy-mm-dd.' } };
      }
      return { ok: true, data: moved };
    },

    setMovingAverages: (configs) => {
      if (!Array.isArray(configs)) {
        return { ok: false, refusal: { code: REFUSAL.INVALID,
          message: 'setMovingAverages expects an array of MA configs.' } };
      }
      setMaConfigs(configs);
      return { ok: true };
    },

    /* ── headless strategy execution ──────────────────────────────────────────────────────── */
    /*
     * RUNS A DESIGNER WITHOUT ITS DIALOG — audit blocker 4's other half.
     *
     * <p>It returns the backend's RESULT and paints nothing. The dialogs' follow-through
     * (overlays, the messages panel, the journal) needs `addOverlay` and half of this
     * component, and folding it in would make the one thing a script or a Copilot wants —
     * the numbers — unobtainable without also redrawing the chart. A caller who wants the
     * chart drawn opens the dialog.
     *
     * <p>⚠ Designer 3 is submit-then-poll and can take up to three minutes; the builder module
     * owns that loop. The promise is the honest shape for all four either way.
     */
    runStrategy: (designerId, config) => runStrategyHeadless(
      apiRef.current, designerId, config,
      { barData: barDataRef.current, extendedBarData: extendedBarDataRef.current },
    ),

    /* ── what the Command Bus needs beyond the chart setters ──────────────────────────────── */

    /*
     * THE GATE'S CONTEXT, read through refs.
     *
     * ⚠ REFS, not the values: this effect registers ONCE at mount, so a closure over `isAdmin`
     * or `toolbarProfile` would freeze whatever they were then -- and the frozen answer here is
     * a security verdict. An admin who signed in after mount would be refused, and worse, a
     * profile switched to ASC would still gate as ADMIN.
     */
    /*
     * ⚠ `profile` IS GONE FROM THE CONTEXT with the gate's profile axis — see step 3's note in
     * `commandGate.js`. Its two real rules were product facts, and they are expressed once now,
     * on the axis that applies to everyone rather than only to a developer who flipped a
     * dropdown.
     */
    gateContext: () => ({
      isAdmin: !!isAdminRef.current,
      product: productTargetRef.current,
    }),

    runEngine: (provider) => handleRunEngineRef.current(provider),
    runCycleBand: (cycle) => handleRunCycleBandRef.current(cycle),
    clearOverlays: () => { clearOverlaysRef.current(); },

    /*
     * The two Admin Panel studies, which have no toolbar action -- `commandGate` carries them
     * in ADMIN_ONLY_COMMANDS for exactly that reason. An unknown study is REFUSED with the
     * names, rather than resolving to nothing and reporting success.
     */
    runStudy: (study, config) => {
      if (study === 'eclipse') return handleRunEclipseStudyRef.current();
      if (study === 'pivotCommonalities') { setPivotCommonalitiesOpen(true); return { ok: true }; }
      if (study === 'aspect') return runAspectStudyRef.current(config || {});
      if (study === 'areaHighlight') return runAreaHighlightStudyRef.current(config || {});
      return Promise.reject(new Error(
        `No study "${study}". Available: aspect, eclipse, pivotCommonalities.`));
    },

    /* ── dialogs ──────────────────────────────────────────────────────────────────────────── */
    openDialog: (dialogId, prefill) => {
      const setter = commandDialogsRef.current[dialogId];
      if (!setter) {
        return { ok: false, refusal: { code: REFUSAL.INVALID,
          message: `Unknown dialog "${dialogId}". Known: ${Object.keys(commandDialogsRef.current).sort().join(', ')}.` } };
      }
      if (prefill !== undefined && prefill !== null) {
        const apply = commandPrefillRef.current[dialogId];
        if (!apply) {
          return { ok: false, refusal: { code: REFUSAL.UNAVAILABLE,
            message: `"${dialogId}" accepts no prefill — it holds its form in local state. `
              + 'Opening it would discard what you sent, so it was not opened.' } };
        }
        const applied = apply(prefill);
        if (!applied.ok) {
          return { ok: false, refusal: { code: REFUSAL.INVALID,
            message: `Prefill rejected by "${dialogId}": ${applied.why}` } };
        }
        return { ok: true };
      }
      setter(true);
      return { ok: true };
    },

    closeDialog: (dialogId) => {
      const setter = commandDialogsRef.current[dialogId];
      if (!setter) {
        return { ok: false, refusal: { code: REFUSAL.INVALID,
          message: `Unknown dialog "${dialogId}".` } };
      }
      setter(false);
      return { ok: true };
    },
  // registerCommandHandlers is frozen for the life of the provider, so this runs exactly once.
  // Every setter named above is a stable useState setter or a ref read; naming any of them here
  // would reach a `const` declared below this point, which is the temporal dead zone
  // App.tdz.test.js exists to catch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [registerCommandHandlers]);

  return (
    <ManifestProvider
      hostname={typeof window !== 'undefined' ? window.location.hostname : ''}
      isAdmin={isAdmin}
    >
    <ThemeProvider theme={theme}>
      <HelpViewer />
      {/*
        THE ROOT CARRIES THE THEME'S COLOUR AND BACKGROUND, and that is not decoration.

        There is no `CssBaseline` in this application, so nothing was setting a colour on the
        document — and MUI's `Typography` defaults to `color: inherit`. Any text not inside a
        component that sets one therefore fell through to the BROWSER's default black, which on
        a `#0d1117` workspace is 1:1. Found that way: the symbol sidebar's whole ticker list was
        black on charcoal, invisible, while its heading beside it read 16:1 because `Typography`
        there names a colour.

        `CssBaseline` is the usual answer and is not used here deliberately: it also sets
        `box-sizing: border-box` on every element, and this layout is full of hand-measured
        panels. Setting the two properties the theme actually needs, on the one element
        everything is inside, fixes the inheritance without touching the box model.
      */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
        bgcolor: 'background.default', color: 'text.primary' }}>
    <AppBar position="static" color="primary" elevation={2} sx={{ zIndex: 1201 }}>
      <Toolbar>
        {/*
          THE TITLE IS THE CONTROL — for an admin. A separate edit icon would be a second thing to
          find on a bar that already carries a menu; the title is the thing being renamed, so
          clicking it is where a reader looks first. The pencil appears on hover so the bar stays
          clean until someone is actually pointing at it.

          For a CUSTOMER it is a plain heading and INERT. Two branches rather than conditional
          props on one, because "un-clickable" is six separate properties — the handler, the
          keyboard handler, `role`, `tabIndex`, the cursor and the hover affordance — and leaving
          any one of them behind produces a title that still looks or behaves like a button. The
          pencil goes with them: a hover affordance for an action that no longer exists is worse
          than a dead click, because it advertises the action before the click disproves it.
        */}
        {brandIsEditable ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setBrandDialogOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBrandDialogOpen(true); } }}
            title="Rename this application (Ctrl+Shift+B)"
            sx={{
              flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.75,
              cursor: 'pointer', borderRadius: 1, px: 0.75, py: 0.25, mx: -0.75,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
              '&:hover .brand-edit': { opacity: 0.9 },
              '&:focus-visible': { outline: '2px solid rgba(255,255,255,0.7)' },
            }}
          >
            {/* ⚠ WAS `${headerBrand} - ${selectedSymbol}` IN ONE STRING. The symbol was already
                here; what changed is that it is now its own element with a divider, so it can be
                styled as secondary and addressed by a test id. A concatenated string can be
                neither. */}
            <Typography variant="h6" component="div" sx={{ whiteSpace: 'nowrap' }}>
              {headerBrand}
              {activeSymbolLabel}
            </Typography>
            <svg className="brand-edit" width="15" height="15" viewBox="0 0 24 24"
              aria-hidden="true" style={{ opacity: 0, transition: 'opacity .15s' }}>
              <path fill="currentColor"
                d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          </Box>
        ) : (
          <Typography
            variant="h6"
            component="div"
            data-testid="app-brand-static"
            // `default` rather than inheriting the bar's cursor, and pointer-events off: the
            // requirement names both, and a text cursor over a heading nobody can select or
            // click is still the honest answer for a label.
            sx={{
              flexGrow: 1, cursor: 'default', pointerEvents: 'none', userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {headerBrand}
            {/* ⚠ A REVERSAL, AND A NARROW ONE. This branch deliberately carried NO symbol — the
                requirement behind it said "strictly as AstroStockCharts.Com", on the reasoning
                that the symbol is already on the chart, in the picker and in the tab. Showing the
                active symbol beside the brand was asked for unconditionally and names a brand a
                CUSTOMER sees (`nonAdminBrandFor(INTEGRALDELTA)` is `integralDelta.ai`), so it
                applies here too.

                What is NOT reversed: the title is still inert. The symbol inherits this
                Typography's `pointerEvents: none` and `userSelect: none`, so it is a label and
                not a control, and the rename affordances are still absent from this branch. */}
            {activeSymbolLabel}
          </Typography>
        )}
        {/* AFTER the brand and OUTSIDE the ternary, so it appears for a customer and an admin
            alike: an area highlight is not an admin feature, and duplicating the element into
            both branches would be two places for one to be forgotten. The brand carries
            `flexGrow: 1`, so the pills sit where the brand's slack ends rather than pushing the
            controls on the right off the bar. */}
        <ActiveOverlayPills
          overlays={overlays}
          onEdit={handleEditAreaHighlight}
          onRemoveGroup={handleRemoveOverlayGroup}
          // The GATED target, so an admin inspecting integralDelta sees that product's header.
          product={productTarget}
        />
        {/*
          * The study-output drawer's toggle. It drives `messagesOpen` directly rather than
          * dispatching `open_messages_panel`, because that action is deliberately show-only — see
          * the component's own note. `productTarget` is the gated target, as above, so an admin
          * inspecting integralDelta sees that product's header rather than their own.
          */}
        <MessagesPanelButton
          open={messagesOpen}
          onToggle={() => setMessagesOpen((v) => !v)}
          product={productTarget}
        />
        <ThemeModeToggle />
        <UserMenu />
    </Toolbar>
    </AppBar>
        {isTrial && !isExpired && (
          <Box sx={{ bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(210, 153, 34, 0.18)' : '#fff3e0'),
            color: (t) => (t.palette.mode === 'dark' ? '#e3b341' : '#e65100'), textAlign: 'center', py: 0.5, px: 2, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <span>Trial account — you are viewing sample data. Expires on {user?.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString() : 'N/A'}.</span>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          {/* PRESENTATION MODE hides the whole bar -- the Paper too, so its border and elevation
              go with it and the canvas starts at the top of the window. `display: none` rather
              than unmounting: TabPanel owns the dialogs and a lot of state, and its dialogs are
              portalled so they stay reachable while this is hidden. */}
          <Paper elevation={2} sx={{ borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider', zIndex: 10,
            ...(presentationMode ? { display: 'none' } : {}) }}>
            <TabPanel
               isAdmin={isAdmin}
               /* MOA is mounted inside TabPanel, so a command reaches it by prop. See the note
                  on moaDialogOpen -- TabPanel ORs this with its own local open state. */
               moaOpen={moaDialogOpen}
               onMoaClose={() => setMoaDialogOpen(false)}
               moaInitialConfig={dialogPrefills.moa || null}
               onEnterPresentationMode={() => setPresentationMode(true)}
               onOpenDeepPivot={() => setDeepPivotOpen(true)}
               onOpenPlanetaryAnalog={() => setPlanetaryAnalogOpen(true)}
               onOpenPivotCommonalities={() => setPivotCommonalitiesOpen(true)}
               onOpenCelestialAspects={() => setCelestialOpen(true)}
               onRunMidpointTrineScan={handleRunMidpointTrineScan}
               onRunEclipseStudy={handleRunEclipseStudy}
               onRunMarketScanner={handleRunMarketScanner}
               onOpenChartEndDates={() => setChartEndDatesOpen(true)}
               // THE ADMIN PANEL'S three chart-level actions. The panel itself lives in TabPanel,
               // beside the dispatcher it reuses; only these need the chart ref, which lives here.
               priceHidden={effectiveChartType === CHART_VIEW.HIDDEN}
               onTogglePriceHidden={handleTogglePriceHidden}
               onZoomFirstYear={handleZoomFirstYear}
               onSetAllTradeCards={handleSetAllTradeCards}
               tradeCardCount={tradeCardCount}
               openTradeCardCount={openTradeCardCount}
               yearCount={eChartRef.current?.countYearlySummaries?.() ?? 0}
               summariesShown={summariesShown}
               onSetYearlySummaries={handleSetYearlySummaries}
               onRunEngine={handleRunEngine}
               // THE GATED value, never `productOverride`. See its declaration.
               product={productTarget}
               onProductChange={handleProductTargetChange}
               barData={barData}
               extendedBarData={extendedBarData}
               onOpenControlsDialog={() => setStudyDialogOpen(true)}
               showRSI={showRSI}
               onToggleRSI={() => setShowRSI((prev) => !prev)}
               showMACD={showMACD}
               onToggleMACD={() => setShowMACD((prev) => !prev)}
               onOpenPlanetFollowStudyDialog={() => setPlanetFollowStudyDialogOpen(true)}
               onOpenAspectsAreaHighlightDialog={() => setIsAspectsAreaHighlightDialogOpen(true)}
               onOpenDeclinationStudyDialog={() => setDeclinationStudyDialogOpen(true)}
               onOpenPlanetaryLinesDialog={() => setPlanetaryLinesDialogOpen(true)}
               onOpenAverageLongitudeDialog={() => setAverageLongitudeDialogOpen(true)}
               onOpenVisualUniverseDialog={() => setVisualUniverseDialogOpen(true)}
               onOpenIntradayAnimationDialog={() => setIntradayAnimationDialogOpen(true)}
               onOpenChartComparisonDialog={() => setChartComparisonDialogOpen(true)}
               onOpenAreaHighlightDialog={handleOpenAreaHighlightDialog}
               onOpenAggregateAreaHighlightDialog={() => setIsAggregateAreaHighlightDialogOpen(true)}
               onOpenPlanetGroupDialog={() => setPlanetGroupDialogOpen(true)}
               onOpenStrategyReportStudyDialog={() => setStrategyReportStudyDialogOpen(true)}
               onOpenStrategyReportStudyBDialog={() => setStrategyReportStudyBDialogOpen(true)}
               onOpenStrategyDesignDialog={() => setIsStrategyDesignDialogOpen(true)}
               onOpenStrategy2DesignDialog={() => setIsStrategy2DesignDialogOpen(true)}
               onOpenTimeAndEventStrategyDialog={() => setTimeAndEventStrategyDialogOpen(true)}
               onOpenPlanetCharacteristicsDialog={() => setIsPlanetCharacteristicsDialogOpen(true)}
               onOpenAstroGraphDialog={() => setIsAstroGraphDialogOpen(true)}
               onRunTrendPulse={handleRunTrendPulse}
               onRunCycleBand={handleRunCycleBand}
               onToggleSimulate={handleToggleSimulate}
               activeActions={simulateMode ? ['toggle_simulate'] : EMPTY_ACTIONS}
               barMode={effectiveBarMode}
               onSpiralReversalScan={handleSpiralReversalScan}
               onOpenConfigurationDialog={() => setConfigurationDialogOpen(true)}
               onOpenMessagesDialog={() => setMessagesDialogOpen(true)}
               // ONE ICON, TWO DIALOGS, decided by profile HERE rather than inside either of
               // them. A dialog that chose its own shape from a role would make "which did they
               // see" unanswerable from the button that opened it, and would couple a scaffold
               // about to be redesigned to the one surface that must not change under it.
               /*
                * ⚠ THE SPLIT READS THE MANIFEST NOW, not the dev profile.
                *
                * `isResearchDialogAvailable(toolbarProfile)` was the test, and the profile
                * dropdown that drove it is gone — so the production scaffold would have become
                * unreachable, which is a worse outcome than either keeping or removing the
                * mechanism. The Studio gets the research dialog and a customer product gets the
                * scaffold: the same decision, on the axis that now governs every other surface.
                * The routing stays HERE rather than inside either dialog, for the reason stated
                * directly above it.
                */
               onOpenDynaManSimulator={() => (manifest.id === ADMIN_PRODUCT
                 ? setDynaManSimulatorOpen(true)
                 : setDynaManProductionOpen(true))}
               onOpenPortfolioManager={() => setPortfolioOpen(true)}
               // THE FLIGHT SIMULATOR'S entry point. It opens the SETUP MODAL rather than
               // starting a run: the window, the overlays and the mandate all change what is
               // fetched, so they are chosen before anything is requested.
               onLaunchSimulator={() => { setSessionError(null); setSimulatorSetupOpen(true); }}
               onChartAnalytica={handleChartAnalyticaToolbar}
               // The orb comes from the button that was pressed. ctsScanParams derives the
               // combined-orb ceiling from it, so the three buttons are three tolerances of
               // one rule rather than three differently-tuned rules.
               onRunCts={(orb) => handleGannConfluenceScan(ctsScanParams(orb))}
               onRunAstroGraphPatterns={handleRunAstroGraphPatterns}
               onOpenPatternPredictionDialog={() => setPatternPredictionOpen(true)}
               onSweepScreenshots={handleSweepScreenshots}
               sweepRunning={sweepRunning}
               operatorFeatures={operatorFeatures}
               onEvaluateValidationLines={handleEvaluateValidationLines}
               onCopyChart={handleCopyChart}
               onSaveScreenshot={handleSaveScreenshot}
               analysisSymbol={barData?.symbol || selectedSymbol}
               onAnalysisComplete={handleAnalysisComplete}
               onAnalysisError={handleAnalysisError}
               onOpenDailyScanViewer={() => setScanViewerOpen(true)}
               scanMarketId={appConfig?.market === 'IN' ? 2 : 1}
               onOpenPatternConfigDialog={() => setIsPatternConfigDialogOpen(true)}
               chartType={effectiveChartType}
               onToggleChartType={handleToggleChartType}
               messagesTabContents={messagesTabContents}
               setMessagesTabContents={setMessagesTabContents}
               messagesOpen={messagesOpen}
               setMessagesOpen={setMessagesOpen}
               messagesTabIndex={messagesTabIndex}
               onMessagesTabIndexChange={setMessagesTabIndex}
               onRunElliottAnalysis={handleRunElliottAnalysis}
               elliottLoading={elliottLoading}
               studyTitle={studyTitle}
               setStudyTitle={setStudyTitle}
               journalEditing={journalEditing}
               onJournalEditingChange={setJournalEditing}
               journalMarkdown={journalMarkdown}
               onJournalChange={setJournalMarkdown}
               onJournalReset={handleJournalReset}
               onJournalAppend={handleJournalAppend}
               onOpenHorizontalLineDialog={() => setSrDialogOpen(true)}
               onVerticalLinesAction={() => eChartRef.current?.toggleCycleMarkers?.()}
               onOpenCyclesDialog={() => setCyclesDialogOpen(true)}
               onTriggerAreaZoom={handleTriggerAreaZoom}
               studyMode={studyMode}
               onExitStudyMode={exitStudyMode}
               drawingMode={drawingMode}
               nextTextSize={textSizeAt(textSizeIndex)}
               onDeleteLastDrawing={handleDeleteLastDrawing}
               onClearCycleRuns={() => setCycleRuns({})}
               ctsCooldownBars={ctsCooldownBars}
               onCtsCooldownBarsChange={setCtsCooldownBars}
               onRunPivotAdditionSeries={handleRunPivotAdditionSeries}
               onRunFibPivotPairs={handleRunFibPivotPairs}
               onToggleRecentFibProjections={handleToggleRecentFibProjections}
               onToggleSk360={handleToggleSk360}
               onToggleTimeProjection={handleToggleTimeProjection}
               onToggleSkGrid={handleToggleSkGrid}
               skGridAnchorIndex={Number.isInteger(lastClickedBar?.index) ? lastClickedBar.index : null}
               onOpenFixedCycleDialog={() => setFixedCycleDialogOpen(true)}
               activeDrawingColor={activeDrawingColor}
               onDrawingColorChange={setActiveDrawingColor}
               barFloaterMode={barFloaterMode}
               onBarFloaterModeChange={setBarFloaterMode}
               onSetDrawingMode={(mode) => {
                 drawingFirstClick.current = null;
                 drawingPoints.current = [];
                 setDrawingMode(mode);
                 // Only an activation advances the cycle. Toggling the tool off passes null, and
                 // advancing on that would skip a size every time someone changed their mind.
                 if (isSizedTool(mode)) {
                   const { activeSize, nextIndex } = activateTextTool(textSizeIndex);
                   setActiveTextSize(activeSize);
                   setTextSizeIndex(nextIndex);
                   // Shared by the text and circle tools, so the message names whichever is active
                   // rather than always claiming to be the text tool.
                   const toolName = mode === 'draw_text' ? 'Text tool'
                     : mode === 'draw_circle_filled' ? 'Filled circle'
                     : 'Circle';
                   const extra = mode === 'draw_text' ? ' Click to place, double-click to edit.' : ' Click to place.';
                   setStatusText(`${toolName}: ${activeSize.label} (${activeSize.px}px).${extra} Click the tool again to change size.`);
                   return;
                 }
                 if (mode) setStatusText(`Drawing mode: ${mode.replace('draw_', '').replace(/_/g, ' ')}. Click on the chart. Esc to cancel.`);
               }}
               onToggleWatermark={handleToggleWatermark}
               watermarkOverride={watermarkOverride}
               watermarkText={displayWatermark}
             />
            {/* OPERATOR FEATURES — the toast for the two export quick-actions. Auto-hides: it
                confirms an action that has already happened, so it must not need dismissing. */}
            <Snackbar
              open={!!toast}
              message={toast}
              autoHideDuration={3000}
              onClose={() => setToast('')}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
            {/* The PM Scan Viewer. Mounted always, opened by the toolbar - MUI builds the element
                tree whether or not the dialog is open, so anything it reads must survive being
                evaluated while closed. It fetches on `open`, not on mount, for the same reason. */}
            <DailyScanViewer
              open={scanViewerOpen}
              onClose={() => setScanViewerOpen(false)}
              onLoadSymbol={handleScanLoadSymbol}
            />
            <PatternPredictionDialog
              open={patternPredictionOpen}
              onClose={() => setPatternPredictionOpen(false)}
              barData={barData}
              trendPulseSeries={trendPulseSeriesForPatterns}
              onApply={handleApplyPatternPrediction}
            />
            <HorizontalLineDialog
              open={srDialogOpen}
              onClose={() => setSrDialogOpen(false)}
              onRun={({ color, price, description }) => {
                if (typeof price === 'number' && !isNaN(price)) {
                                    addOverlay({
                    type: 'horizontalLine',
                    color,
                    price,
                    description: description || '',
                  });
                }
              }}
            />
          </Paper>
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
              <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {loading && <Typography>Loading chart data...</Typography>}
                {computingLabel && (
                  // Fixed, centred and above the chart, with pointer events captured so a second
                  // click cannot queue another multi-second scan behind the first.
                  <Box
                    sx={{
                      position: 'fixed', inset: 0, zIndex: 4000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.08)', cursor: 'progress',
                    }}
                  >
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2, px: 3, py: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                    }}>
                      <CircularProgress size={22} />
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{computingLabel}...</Typography>
                        <Typography variant="body2" color="text.secondary">
                          This can take a few seconds on a long chart.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                {/* Floats over the chart, positioned against the same relatively-positioned
                    container the loading overlay above uses. Renders nothing when no zone was
                    found, so a scan that came back empty leaves no permanent chip behind. */}
                <SpiralClickControl
                  mode={spiralClickMode}
                  onSelectMode={setSpiralClickMode}
                  canClear={spiralDrawn}
                  onClear={clearSpiralInspection}
                  shiftDown={Boolean(spiralBadgeDismissed ? null : spiralZone)}
                />
                <SpiralScanBadge
                  zone={spiralBadgeDismissed ? null : spiralZone}
                  zones={spiralZones}
                  anchored={spiralAnchored}
                  undrawn={spiralUndrawn}
                  params={spiralParams}
                  truncated={spiralTruncated}
                  onClick={spiralZone ? () => runSpiralInspection('BACKWARD', spiralZone.targetBarIndex) : undefined}
                  onDismiss={() => setSpiralBadgeDismissed(true)}
                />
                {/* Right-click / shift-click a candle. Anchored to the pointer, so it is rendered
                    here rather than inside the chart component, which owns no DOM per bar. */}
                <SpiralInspectMenu
                  anchorPosition={spiralMenu?.position || null}
                  barDate={spiralMenu?.date}
                  onForward={() => runSpiralInspection('FORWARD', spiralMenu?.barIndex)}
                  onBackward={() => runSpiralInspection('BACKWARD', spiralMenu?.barIndex)}
                  onBoth={() => runSpiralInspection('BOTH', spiralMenu?.barIndex)}
                  onAnalytica={() => {
                    // The menu closes FIRST. `runAnalytica` holds the thread for the scan, and
                    // a menu still open over the chart while that happens reads as a hang.
                    const at = spiralMenu?.barIndex;
                    closeSpiralMenu();
                    if (Number.isFinite(at)) runAnalytica(at);
                  }}
                  onClose={closeSpiralMenu}
                />
                <ChartAnalyticaDialog
                  open={analyticaOpen}
                  runs={analyticaRuns}
                  activeId={analyticaActiveId}
                  onSelectRun={setAnalyticaActiveId}
                  onClose={() => setAnalyticaOpen(false)}
                  annotations={analyticaAnnotations}
                  onToggleAnnotations={setAnalyticaAnnotations}
                />
                {/* The two orbs, asked for before the scan runs: the condition is not selective on
                    its own, so which numbers make it a signal is the thing to experiment with. */}
                {/* The customer's own chart colours. Its defaults come from the studies that draw
                    with them, so a swatch here cannot disagree with the chart behind it. */}
                <ConfigurationDialog
                  open={configurationDialogOpen}
                  defaults={workspaceColorDefaults}
                  onClose={() => setConfigurationDialogOpen(false)}
                />
                {/* Messages from the integralDelta team. Read-only unless the caller is an
                    admin - and the POST re-checks that, so the hidden button is a courtesy
                    rather than the permission. */}
                <MessagesDialog
                  open={messagesDialogOpen}
                  onClose={() => setMessagesDialogOpen(false)}
                />
                {/* The DynaMan research engine. Admin-gated in the toolbar and again inside the
                    dialog; the endpoints enforce it themselves, so both gates are courtesies.
                    Opens on whatever symbol the chart is showing, because the question a reader
                    has is almost always about the chart in front of them. */}
                <DynaManSimulatorDialog
                  customSeries={customSeries}
                  open={dynaManSimulatorOpen}
                  initialSymbol={selectedSymbol}
                  onClose={() => {
                    setDynaManSimulatorOpen(false);
                    // Cleared on close so a later toolbar click opens a dialog that WAITS. The
                    // boot run has already happened; re-running it under a reader who opened the
                    // dialog deliberately would be a surprise rather than a convenience.
                  }}
                  onShowOnChart={handleShowDynaManTrades}
                  onStartReplay={handleStartReplay}
                />
                {/* The production view, for ASC and AI. A scaffold, and it says so on screen. */}
                <DynaManProductionDialog
                  customSeries={customSeries}
                  open={dynaManProductionOpen}
                  onClose={() => setDynaManProductionOpen(false)}
                  symbol={selectedSymbol}
                  /* The product it is standing in for, where this was the dev profile. */
                  profile={manifest.id}
                />
                {/* The portfolio layer. Takes no symbol: it is about the BOOK across several
                    symbols, so the chart's current selection is not its subject. */}
                <DynaManPortfolioDialog
                  open={portfolioOpen}
                  onClose={() => setPortfolioOpen(false)}
                />
                <GannScanDialog
                  open={gannDialogOpen}
                  symbol={selectedSymbol}
                  onRun={(opts) => { setGannDialogOpen(false); handleGannConfluenceScan(opts); }}
                  onClose={() => setGannDialogOpen(false)}
                />
                {/* The toolbar button lands here: pick the question before anything is drawn. */}
                <SpiralModeDialog
                  open={spiralDialogOpen}
                  scan={spiralScan}
                  symbol={selectedSymbol}
                  lastClickedBar={lastClickedBar}
                  onRun={handleSpiralModeRun}
                  onClose={() => setSpiralDialogOpen(false)}
                />
                <SpiralInspectDialog
                  open={Boolean(spiralInspection)}
                  inspection={spiralInspection}
                  symbol={selectedSymbol}
                  onClose={() => setSpiralInspection(null)}
                  onInspectBar={(barIndex) => runSpiralInspection('BACKWARD', barIndex)}
                />
                {/* A profile that never loaded is NOT a downgraded account, and must not look
                    like one. Everything entitlement-shaped is derived from the profile, so a
                    fallback session silently loses the whole admin toolbar — which reads as
                    features having been removed rather than as a backend that did not answer. */}
                {profileIncomplete && (
                  <Typography sx={{ color: '#b26a00', fontSize: 13 }}>
                    Your profile did not load, so account-specific features are hidden. This is a
                    connection problem, not a change to your account — reload to try again.
                  </Typography>
                )}
                {error && <Typography color="error">Error: {error}</Typography>}
                {/* THE UPLOAD, shown whenever the synthetic symbol is selected -- not only while
                    empty. A reader needs to be able to see which file is loaded and replace it
                    without leaving the symbol, and hiding the control after a successful parse is
                    how a one-shot upload becomes a reload to try a second file. */}
                {customSelected && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <CustomDataUpload
                      onSeriesReady={setCustomSeries}
                      onClear={() => setCustomSeries(null)}
                    />
                  </Box>
                )}
                {extendedBarData && Array.isArray(extendedBarData.ohlc) && extendedBarData.ohlc.length > 0 && Array.isArray(extendedBarData.dates) && extendedBarData.dates.length > 0 ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      // RELATIVE so the Copilot bar can pin itself to the chart area rather than
                      // to the viewport -- it is a chart control, and a fixed one would float
                      // over the dialogs and the toolbar too.
                      position: 'relative',
                      // Crosshair while replaying, so the pointer says the click is aimed at a BAR
                      // rather than at the drawing tools it normally reaches. Applied to the chart
                      // area alone: the toolbar above still has to look clickable, and the button
                      // that turns the mode off is up there.
                      ...(simulateMode ? { cursor: 'crosshair' } : {}),
                    }}
                    onClickCapture={studyMode ? (e) => {
                      const instance = eChartRef.current?.getEchartsInstance?.();
                      if (!instance) return;
                      const rect = instance.getDom().getBoundingClientRect();
                      const ox = e.clientX - rect.left;
                      const oy = e.clientY - rect.top;
                      // Only intercept clicks inside the chart grid; let toolbox/slider clicks through
                      if (!instance.containPixel({ gridIndex: 0 }, [ox, oy])) return;
                      e.stopPropagation();
                      e.preventDefault();

                      // --- Animate mode: toggle pause/continue ---
                      if (animateIntervalRef.current) {
                        animatePausedRef.current = !animatePausedRef.current;
                        setStatusText(animatePausedRef.current ? 'Animation PAUSED — click to continue' : 'Animation resumed...');
                        return;
                      }

                      // --- Execute & Study mode: jump to clicked bar ---
                      try {
                        const dataIdx = instance.convertFromPixel({ gridIndex: 0 }, [ox, oy])[0];
                        if (typeof dataIdx === 'number' && dataIdx >= 0 && dataIdx + 1 > studyVisibleCount) {
                          setStudyVisibleCount(dataIdx + 1);
                        } else {
                          handleStudyAdvance();
                        }
                      } catch (_) {
                        handleStudyAdvance();
                      }
                    } : undefined}
                  >
                    {/*
                      * THE COPILOT BAR, inside the chart's own box.
                      *
                      * ⚠ GATED ON `isAdmin`, matching the backend. The endpoint is admin-only --
                      * it spends money per call and there is no entitlement field to read -- so
                      * showing the bar to a customer would offer a control whose every use is a
                      * 403. Hiding it also unbinds Cmd+K, which would otherwise be a shortcut
                      * that opens a box nothing can answer.
                      */}
                    <ChartCopilotBar
                      api={api}
                      activeSymbol={barData?.symbol || selectedSymbol}
                      enabled={isAdmin}
                    />
                    <EChartComponent
                      ref={eChartRef}
                      onTradeCardsChanged={setOpenTradeCardCount}
                      barData={extendedBarData}
                      // The replay boundary, for the final future-marker gate.
                      //
                      // NOT a date. In simulation mode barData keeps its full length and dates and
                      // only its PRICES are masked, so the last date of the series is the end of
                      // all history rather than the edge of the replay - which is precisely how a
                      // date-based gate came to classify nothing as future on the one screen whose
                      // whole purpose is hiding it. The index is the honest statement of where the
                      // visible chart stops.
                      simulationBoundary={simulateMode ? simulateReveal : null}
                      /*
                       * THE LAST BAR WITH A PRICE ON IT, as a date — which is a different
                       * question from the index above and is why both are here.
                       *
                       * <p>That one is "where does the replay stop", for the future-marker gate.
                       * This is "which columns have a price", which a rail-anchored mark needs
                       * per bar, and the two differ outside a replay: `extendedBarData` carries
                       * 250 synthetic weekdays whose OHLC is the last real close repeated, so a
                       * mark there reads a finite price off a bar that never traded.
                       *
                       * <p>A DATE rather than an index because the renderer compares it against
                       * each point's own date, and an index into `extendedBarData` is not the
                       * index into anything the points were built from.
                       */
                      revealedUpToDate={revealedUpToDate}
                      lastTradedDate={lastTradedDate}
                      overlays={overlays}
                      showRSI={showRSI}
                      rsiData={rsiData}
                      showMACD={showMACD}
                      macdData={macdData}
                      theme={theme}
                      loading={loading}
                      error={error}
                      chartType={effectiveChartType}
                      onZoomRangeChange={handleZoomRangeChange}
                      maConfigs={maConfigs}
                      onBarSelect={handleBarSelection}
                      onSignalClick={handleSignalClick}
                      onBarContextMenu={handleBarContextMenu}
                      studyMode={studyMode}
                      barFloaterMode={barFloaterMode}
                      shortBarMode={effectiveBarMode === SHORT}
                      // An administrator is an exception to the rules made for other readers --
                      // see `isAdmin` in EChartComponent. Hover cards only.
                      isAdmin={isAdmin}
                      workspaceColors={workspaceColors}
                      symbol={selectedSymbol}
                      watermarkOverride={watermarkOverride}
                      watermarkText={displayWatermark}
                      onUpdateOverlay={updateOverlay}
                    />
                    {/* The replay HUDs, INSIDE the chart's positioned container so they sit over
                        the price action rather than over the page. The layer is pointer-through
                        except for the transport, so a candle tooltip still works mid-replay. */}
                    <DynaManReplayOverlay
                      timeline={replayTimeline}
                      dates={barData?.dates}
                      // THE FULL AXIS, padding included, for the calendar structure alone -- the
                      // bands, their rays and the CTS pillars. `dates` stays the REAL bars, which
                      // is what bounds everything price-bearing.
                      axisDates={extendedBarData?.dates}
                      // COST PARITY. The same account the replay request was built from, so the
                      // discretionary book pays the identical commission and slippage. Omitting it
                      // does not error -- it silently gives the human column free execution and
                      // lets it win by the friction it was never charged, which on the published
                      // SPY run is a fifth of the result.
                      account={DEBUG_ACCOUNT}
                      ctsPoints={replayCtsPoints}
                      // THE SESSION, or null for a run started from the research dialog -- which
                      // keeps every earlier behaviour: the whole run is walked, no window bounds
                      // the comparison, and no mandate selector appears.
                      session={replaySession}
                      onMandateChange={replaySession ? handleMandateChange : null}
                      mandateLoading={sessionLaunching}
                      // THE REAL BARS and the view they are drawn in, for the bar-by-bar
                      // unveiling. `barData` rather than `extendedBarData`: the reveal buffer is
                      // sized to the padded AXIS and fills from the real series, so handing it
                      // the padded one would reveal 250 flat synthetic bars as though the market
                      // had traded them. The chart type has to travel because the price patch
                      // RESTATES the series type, and restating the wrong one silently converts
                      // a line chart into candles mid-run.
                      barData={barData}
                      chartType={effectiveChartType}
                      getChartInstance={getChartInstance}
                      onClose={handleCloseReplay}
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', textAlign: 'center', color: '#888', py: 6 }}>
                    {/* "Loading chart data..." is a lie for the synthetic symbol: nothing is
                        loading and nothing will, until a file is chosen. Saying so is the
                        difference between a feature that looks broken and one that looks ready. */}
                    {customSelected
                      ? 'Choose a CSV above to chart it and run the engines over it.'
                      : 'Loading chart data...'}
                  </Box>
                )}
                {/* THE SIMULATOR SETUP. Mounted beside the chart rather than inside the
                    research dialog: it configures a run that happens ON the chart, and a modal
                    that has to be opened from inside another dialog is one the reader has to find
                    twice. */}
                <SimulatorSetupDialog
                  open={simulatorSetupOpen}
                  onClose={() => { setSimulatorSetupOpen(false); setSessionError(null); }}
                  onLaunch={handleLaunchSession}
                  symbol={selectedSymbol}
                  symbols={simulatorSymbols}
                  // IMMEDIATE, not on launch. The window picker needs the series' own dates and
                  // the band tables are fetched per symbol, so a symbol chosen at launch time
                  // would bound the session to a window measured against the PREVIOUS symbol's
                  // calendar. Changing the chart under the modal is visible and understandable.
                  onSymbolChange={setSelectedSymbol}
                  // THE SAME two callbacks the chart's own upload panel uses, so an uploaded
                  // series is one series however it was provided -- a second parse path would be
                  // a second chance to persist something this feature has promised not to keep.
                  onCustomSeries={setCustomSeries}
                  onClearCustomSeries={() => setCustomSeries(null)}
                  dates={barData?.dates}
                  launching={sessionLaunching}
                  error={sessionError}
                />
                <AreaHighlightDialog
                  open={isAreaHighlightDialogOpen}
                  onClose={handleCloseAreaHighlightDialog}
                  barData={extendedBarData}
                  initialConfig={dialogPrefills.areaHighlight}
                  onJournalAppend={handleJournalAppend}
                  onRun={(data) => {
                    const { title, summaryLines, futureDates } = data;
                    console.log('[MESSAGE_PANEL_DATA]', { title, summaryLines, futureDates });
                    setStudyTitle(title || '');
                    const tabs = formatMessagesPanelTabs(summaryLines, futureDates);
                    setMessagesTabContents(tabs);
                    setMessagesOpen(true);
                  }}
                />
              </Box>
            </Box>
            <SymbolList user={user} token={token} selectedSymbol={selectedSymbol} onSelectSymbol={setSelectedSymbol} />
          </Box>
        </Box>
        <StatusBar statusText={statusText} />

        {/* Dialogs */}
        <FixedCycleDialog
          open={fixedCycleDialogOpen}
          onClose={() => setFixedCycleDialogOpen(false)}
          totalBars={barData?.dates?.length || 0}
          initialValue={fixedCycleLength}
          initialUnit={fixedCycleUnit}
          dates={extendedBarData?.dates}
          onApply={(n, unit) => {
            setFixedCycleLength(n);
            setFixedCycleUnit(unit);
            setFixedCycleDialogOpen(false);
            // The dialog only collects the interval; the anchor is the next click on the chart.
            setDrawingMode('draw_fixed_cycle');
            setStatusText(`Fixed cycle of ${n} ${unit === 'days' ? 'calendar day(s)' : 'bar(s)'}: `
              + 'click a bar to anchor it. Esc to cancel.');
          }}
        />
        <LocationSettingsDialog
          open={isLocationSettingsOpen}
          onClose={() => setLocationSettingsOpen(false)}
          onSave={handleSaveLocationSettings}
          currentPreferences={locationPreferences}
        />
        <StrategyDesignDialog
          open={isStrategyDesignDialogOpen}
          onClose={() => setIsStrategyDesignDialogOpen(false)}
          onRun={handleStrategyDesignRun}
          initialConfig={dialogPrefills.strategyDesign}
          barData={barData}
          extendedBarData={extendedBarData}
          onJournalAppend={handleJournalAppend}
        />
        <Strategy2DesignDialog
          open={isStrategy2DesignDialogOpen}
          onClose={() => setIsStrategy2DesignDialogOpen(false)}
          onRun={handleStrategy2DesignRun}
          initialConfig={dialogPrefills.strategy2Design}
        />
        <TimeAndEventStrategyDialog 
          open={isTimeAndEventStrategyDialogOpen} 
          onClose={() => setTimeAndEventStrategyDialogOpen(false)} 
          onRun={handleTimeAndEventStrategyRun}
          initialConfig={dialogPrefills.timeAndEventStrategy}
        />
        {barData && barData.dates && barData.dates.length > 0 && (
          <>
            <ElliottWaveDialog
              open={elliottOpen}
              onClose={() => setElliottOpen(false)}
              loading={elliottLoading}
              error={elliottError}
              result={elliottResult}
              anchor={elliottAnchor}
              structure={elliottStructure}
              projections={elliottProjections}
              score={elliottScore}
              overlayShown={elliottOverlayShown}
              onToggleOverlay={handleToggleElliottOverlay}
            />
            <LongitudeStudyDlg open={isStudyDialogOpen} onClose={() => setStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} onJournalAppend={handleStudyReportJournal} initialConfig={dialogPrefills.aspectStudy ?? dialogPrefills.controls} />
            <PlanetFollowStudyDialog open={isPlanetFollowStudyDialogOpen} onClose={() => setPlanetFollowStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} />
            <AspectsAreaHighlightDialog
              open={isAspectsAreaHighlightDialogOpen}
              onClose={() => setIsAspectsAreaHighlightDialogOpen(false)}
              barData={barData}
              extendedBarData={extendedBarData}
              onRun={handleAspectsAreaHighlightRun}
              onJournalAppend={handleJournalAppend}
            />
            <DeclinationStudyDialog open={isDeclinationStudyDialogOpen} onClose={() => setDeclinationStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} onJournalAppend={handleStudyReportJournal} />
            <PlanetaryLinesDialog open={isPlanetaryLinesDialogOpen} onClose={() => setPlanetaryLinesDialogOpen(false)} onRun={handleRunPlanetaryLines} />
            <AverageLongitudeDialog open={isAverageLongitudeDialogOpen} onClose={() => setAverageLongitudeDialogOpen(false)} onRun={handleRunAverageLongitude} />
          </>
        )}
        <VisualUniverseDialog 
          open={isVisualUniverseDialogOpen} 
          onClose={() => setVisualUniverseDialogOpen(false)} 
          token={token} 
          initialDate={visualUniverseInitialDate}
        />
        <IntradayAnimationDialog
          open={isIntradayAnimationDialogOpen}
          onClose={() => setIntradayAnimationDialogOpen(false)}
        />
        <ChartComparisonDialog
          open={isChartComparisonDialogOpen}
          onClose={() => setChartComparisonDialogOpen(false)}
          barData={barData}
          symbol={selectedSymbol}
        />
        <PlanetGroupDialog open={isPlanetGroupDialogOpen} onClose={() => setPlanetGroupDialogOpen(false)} />
        <StrategyReportStudyDialog open={isStrategyReportStudyDialogOpen} onClose={() => setStrategyReportStudyDialogOpen(false)} />
        <StrategyReportStudyBDialog open={isStrategyReportStudyBDialogOpen} onClose={() => setStrategyReportStudyBDialogOpen(false)} />
        {/*
          barData/extendedBarData were never passed before, because the only control that
          opened this dialog was disabled — so the omission was invisible. The dialog guards
          against missing data and logs, rather than crashing, which means it would have
          opened and silently done nothing.
        */}
        <AggregateAreaHighlightDialog
          open={isAggregateAreaHighlightDialogOpen}
          onClose={() => setIsAggregateAreaHighlightDialogOpen(false)}
          barData={barData}
          extendedBarData={extendedBarData}
        />
        <PlanetCharacteristicsPlotDialog
          open={isPlanetCharacteristicsDialogOpen}
          onClose={() => setIsPlanetCharacteristicsDialogOpen(false)}
          onRunSeasonality={handleRunSeasonality}
          onRunCharacteristics={handleRunCharacteristicsPlot}
          onRunPivotAnalysis={async ({ pivotType, vicinity, minPivot, maxPivot, factorThreshold, futureThreshold, projectionMonths, asOfDate, setReportContent, switchToReport }) => {
            const symbol = barData?.symbol || selectedSymbol;
            if (!symbol) {
              alert('No symbol loaded. Please load a chart first.');
              return;
            }
            const mode = asOfDate ? ` [backtest as-of ${asOfDate}]` : '';
            console.log('[App.js] Pivot analysis requested:', pivotType, 'symbol:', symbol, mode);
            setReportContent('Running pivot analysis for ' + symbol + ' (' + pivotType + ')' + mode + '...\nPlease wait, this may take a minute.');
            switchToReport();
            try {
              const payload = { symbol, pivotType, vicinity, minPivot, maxPivot, factorThreshold, futureThreshold, projectionMonths };
              if (asOfDate) payload.asOfDate = asOfDate;
              const response = await api.post('/api/pivot-analysis', payload,
                { headers: { 'Authorization': `Bearer ${token}` } });
              const { report, error } = response.data;
              if (error) {
                setReportContent('ERROR: ' + error);
              } else {
                setReportContent(report || 'No report generated.');
              }
            } catch (err) {
              console.error('[App.js] Pivot analysis error:', err);
              setReportContent('ERROR: ' + (err.response?.data?.error || err.message));
            }
          }}
          barData={barData}
        />
        {/* The third gate on the rename, after the click and the shortcut. Belt and braces: a
            stray `setBrandDialogOpen(true)` from anywhere else would otherwise open a dialog that
            writes an override the header no longer reads. */}
        {/* MODELESS, like Chart Analytica beside it: no backdrop, no focus trap, so the next
            CTS point stays one click away. Rendered unconditionally and self-gating on `open`,
            so the session survives a close. */}
        <TradeSimulatorDialog
          open={tradeSimOpen}
          session={tradeSimSession}
          manual={tradeSimManual}
          onClose={() => setTradeSimOpen(false)}
          // RESET clears both: a ledger emptied while a half-built trade is still armed would
          // book that trade into a session the reader thought they had cleared.
          onReset={() => { setTradeSimSession(clearSession()); setTradeSimManual(IDLE_STATE); }}
          onOpenTrade={dir => setTradeSimManual(prev => beginOpen(prev, dir))}
          // THE PREVIOUS STATE, so Cancel keeps the structure on the card — it aborts a
          // TRADE, and clearing what the reader is reading at the same time would make
          // them click the handle again for no reason they asked for.
          onCancelManual={() => setTradeSimManual(cancelManual)}
          // So the prompt can name the SECOND half of the gesture — clicking empty space to
          // step the playhead — which exists only while a replay is running.
          replay={simulateMode}
          gate={replayGate}
          gateRemaining={barsRemaining(replayGate, assessmentBoundary)}
          onReleaseGate={() => {
            setReplayGate(null);
            setStatusText('Released — the trade stays open in the ledger and the replay is free.');
          }}
        />
        <AppBrandDialog
          open={brandDialogOpen && brandIsEditable}
          currentName={headerBrand}
          defaultName={watermarkText}
          onSave={handleBrandSave}
          onReset={handleBrandReset}
          onClose={() => setBrandDialogOpen(false)}
          barMode={barMode}
          {...(isAdmin ? { onToggleBarMode: handleToggleBarMode } : {})}
        />

        <ChartEndDateDialog
          open={chartEndDatesOpen}
          isAdmin={isAdmin}
          api={api}
          onClose={() => setChartEndDatesOpen(false)}
        />

        {/* ⚠ THE SCANNER'S REVIEW DIALOG, AND ITS GATE IS NO LONGER `isAdmin`.
            It said "reachable only from the Admin Panel, which renders nothing for anyone else,
            so this is the second gate rather than the first" — true until the scanner got a
            toolbar icon. After that an integralDelta customer could RUN a scan, the backend
            answered it, and this returned null: the result existed and there was nowhere for it
            to appear. Reported as "not finding anything to display", and that is exactly what it
            was.

            Gated on the SAME rule as the icon and the server — admin, or an integralDelta
            customer — through the one predicate, so the three cannot drift into a scan whose
            output is invisible to the person who ran it. */}
        {(isAdmin || isCustomerFacingOnProduct('run_market_scanner', productTarget)) && (
          <ScannerCandidatesDialog
            open={scannerOpen}
            onClose={() => setScannerOpen(false)}
            candidates={scannerCandidates}
            status={scannerStatus}
            state={scannerState}
            selectedSymbol={selectedSymbol}
            onSelectSymbol={handleScannerSelectSymbol}
            scanDate={scannerDate}
            onScanDateChange={setScannerDate}
            onRerun={(d) => handleRunMarketScanner(undefined, d)}
            latestSession={barData?.dates?.[barData.dates.length - 1]}
          />
        )}

        <CelestialAspectDialog
          open={celestialOpen}
          isAdmin={isAdmin}
          token={token}
          // The chart's OWN span, so the ephemeris search covers exactly what can be drawn.
          from={barData?.dates?.[0] || ''}
          to={barData?.dates?.[barData.dates.length - 1] || ''}
          onClose={() => setCelestialOpen(false)}
          onPaint={handlePaintCelestial}
          onPaintRandom={handlePaintCelestialRandom}
          barCount={(barData && barData.dates && barData.dates.length) || 0}
        />

        <PlanetaryAnalogDialog
          open={planetaryAnalogOpen}
          isAdmin={isAdmin}
          token={token}
          symbol={selectedSymbol}
          onClose={() => setPlanetaryAnalogOpen(false)}
          onFocusDate={handleFocusAnalogDate}
          onPaint={handlePaintPlanetaryAnalog}
          onClearMarks={handleClearPlanetaryAnalogMarks}
        />

        <PivotCommonalitiesDialog
          open={pivotCommonalitiesOpen}
          isAdmin={isAdmin}
          token={token}
          symbol={selectedSymbol}
          onClose={() => setPivotCommonalitiesOpen(false)}
          onFocusDate={handleFocusAnalogDate}
          onPaint={handlePaintPivotCommonality}
          onClearMarks={handleClearPivotCommonalityMarks}
        />

        <DeepPivotDialog
          open={deepPivotOpen}
          isAdmin={isAdmin}
          symbol={selectedSymbol}
          token={token}
          onClose={() => setDeepPivotOpen(false)}
          onPaint={handlePaintDeepPivot}
        />

        <AstroGraphDialog
          open={isAstroGraphDialogOpen}
          onClose={() => { setIsAstroGraphDialogOpen(false); setAstroGraphCommentary(''); }}
          onExecute={handleAstroGraphExecute}
          onExecuteAndStudy={handleAstroGraphExecuteAndStudy}
          onAnimate={handleAstroGraphAnimate}
          commentary={astroGraphCommentary}
          barData={barData}
        />
        <CyclesDialog 
          open={isCyclesDialogOpen} 
          onClose={() => setCyclesDialogOpen(false)} 
          bar1Date={bar1Date}
          bar2Date={bar2Date}
          onDisplay={handleDisplayCycles}
          onErase={handleEraseCycles}
          onManualDrawToggle={handleManualDrawToggle}
          onOpenVisualUniverse={handleOpenVisualUniverse}
        />
      <PatternConfigDialog
        open={isPatternConfigDialogOpen}
        onClose={() => setIsPatternConfigDialogOpen(false)}
        maConfigs={maConfigs}
        onApply={(newMaConfigs) => {
          setMaConfigs(newMaConfigs);
          setIsPatternConfigDialogOpen(false);
        }}
      />
      <TrialWelcomeDialog
        open={trialWelcomeOpen}
        onClose={() => setTrialWelcomeOpen(false)}
      />
      </Box>
    </ThemeProvider>
    </ManifestProvider>
  );
}

export default App;